/**
 * Thin fetch wrapper for the Kilogram API.
 *
 * Responsibilities:
 *   - Attaches the bearer access token automatically.
 *   - On a 401, attempts a single refresh-token round trip and retries
 *     the original request.
 *   - Throws ApiError for non-2xx responses so callers can switch on
 *     status / code.
 *
 * Token storage lives in lib/auth.ts; this module imports the getters
 * and setters but doesn't own the React context.
 */
import type { StoryboardImageModel, StoryboardLook } from './storyboard-look';
import { CLASSIC_LOOK_KEYS } from './story-looks';
import { getTokens, setTokens, clearTokens } from './auth-store';
import type {
  CoachOverview,
  CoachSettings,
  ARollGroup,
  BillingStatus,
  TenantFolder, ApiError,
  ClientJob,
} from './types';

export class ApiClientError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, body: ApiError | { error?: { message?: string } } | null) {
    const message =
      body?.error?.message ??
      (typeof body === 'string' ? body : `Request failed (${status})`);
    super(message);
    this.status = status;
    this.code = (body as ApiError | undefined)?.error?.code ?? 'UNKNOWN';
    this.details = (body as ApiError | undefined)?.error?.details;
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /** When true, do not attach the bearer token (for /auth endpoints). */
  unauthenticated?: boolean;
};

/**
 * Module-scoped share token override. Set by PortalSharePage on mount
 * so subsequent calls (e.g. the preproduction API) can append
 * `?shareToken=xxx` instead of a bearer token. Cleared on unmount.
 *
 * The portal share routes (/portal/share/:token/...) build their own
 * URLs from this token explicitly - the override is only consulted by
 * helpers that need to send the token as a query/header to a generic
 * `/api/...` route.
 */
let shareTokenOverride: string | null = null;

export function setShareTokenOverride(token: string | null): void {
  shareTokenOverride = token;
}

export function getShareTokenOverride(): string | null {
  return shareTokenOverride;
}

/**
 * Stable per-tab session id for the A-Roll single-editor lock. Share-link
 * contacts all share one token (no per-person id), so the lock holder is keyed
 * on this. Sent as X-Aroll-Session on every request; only the fork mutations
 * read it.
 */
let arollSession: string | null = null;
export function arollSessionId(): string {
  if (arollSession) return arollSession;
  const mint = () =>
    globalThis.crypto?.randomUUID?.() ??
    `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    let s = sessionStorage.getItem('kg.aroll.session');
    if (!s) {
      s = mint();
      sessionStorage.setItem('kg.aroll.session', s);
    }
    arollSession = s;
  } catch {
    arollSession = mint();
  }
  return arollSession;
}

/**
 * Append `?shareToken=xxx` to a path when the override is active and
 * there is no logged-in user. The backend's `requireAuthOrShareToken`
 * middleware reads this query param and synthesizes a CLIENT-role
 * payload scoped to the token's client.
 */
function withShareToken(path: string): string {
  if (!shareTokenOverride) return path;
  if (getTokens()) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}shareToken=${encodeURIComponent(shareTokenOverride)}`;
}

async function rawRequest(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const { body, unauthenticated, headers, ...rest } = options;
  const tokens = getTokens();
  const finalHeaders: HeadersInit = {
    Accept: 'application/json',
    'X-Aroll-Session': arollSessionId(),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(unauthenticated || !tokens
      ? {}
      : { Authorization: `Bearer ${tokens.accessToken}` }),
    ...(headers ?? {}),
  };

  return fetch(path, {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Issue a request and return parsed JSON. On 401, try a one-shot
 * refresh before giving up. On any non-2xx, throw ApiClientError so
 * callers can catch and render.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await rawRequest(path, options);

  if (res.status === 401 && !options.unauthenticated) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await rawRequest(path, options);
    }
  }

  if (!res.ok) {
    const body = (await readBody(res)) as ApiError | null;
    throw new ApiClientError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return (await readBody(res)) as T;
}

/**
 * Send a raw file body as POST with the bearer token. Handles 401 by
 * doing the same one-shot refresh-and-retry dance as apiFetch, so
 * binary uploads don't silently fail after the 15-min access-token
 * expiry. Throws ApiClientError on any non-2xx.
 */
async function uploadBinary<T>(
  path: string,
  file: File,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const send = async () => {
    const tokens = getTokens();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      // URL-encode so non-ASCII filenames (em-dashes, accented chars,
      // emoji, smart quotes) survive the HTTP header layer, which is
      // ISO-8859-1 only. Backend decodes via decodeURIComponent with
      // a safe fallback to the raw value for legacy uploads.
      'X-Filename': encodeURIComponent(file.name),
      'Content-Type': file.type || 'application/octet-stream',
      ...extraHeaders,
    };
    if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
    return fetch(path, { method: 'POST', headers, body: file });
  };
  let res = await send();
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) res = await send();
  }
  if (!res.ok) {
    let body: ApiError | null = null;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      body = null;
    }
    throw new ApiClientError(res.status, body);
  }
  return (await res.json()) as T;
}

let refreshInFlight: Promise<boolean> | null = null;

/** One-shot refresh. De-duplicates concurrent refresh attempts. */
async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const tokens = getTokens();
  if (!tokens?.refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
      if (!res.ok) {
        clearTokens();
        return false;
      }
      const data = (await res.json()) as {
        accessToken: string;
        refreshToken: string;
      };
      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// ---------------------------------------------------------------------
// Thumbnail fetch gate
// ---------------------------------------------------------------------
// Thumbnail grids fire one request per tile in a single burst (a Final
// Exports page can be 60+ files), which can trip the share-link rate
// limiter or Dropbox's own 429s, and a failed tile renders as a
// permanent "No preview". Funnel every thumbnail fetch through a small
// concurrency gate and retry 429s with backoff so a burst degrades to
// "loads a moment later" instead of a dead rectangle.

const THUMB_MAX_CONCURRENT = 6;
const THUMB_MAX_ATTEMPTS = 4;

let thumbActive = 0;
const thumbQueue: Array<() => void> = [];

async function acquireThumbSlot(): Promise<void> {
  if (thumbActive < THUMB_MAX_CONCURRENT) {
    thumbActive += 1;
    return;
  }
  await new Promise<void>((resolve) => thumbQueue.push(resolve));
  thumbActive += 1;
}

function releaseThumbSlot(): void {
  thumbActive -= 1;
  const next = thumbQueue.shift();
  if (next) next();
}

const thumbSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// The slot is deliberately held through the backoff sleep: when the
// server is rate-limiting us, the whole queue should slow down, not
// just the request that saw the 429.
async function fetchThumbnailGated(send: () => Promise<Response>): Promise<Response> {
  await acquireThumbSlot();
  try {
    let res = await send();
    for (let attempt = 1; res.status === 429 && attempt < THUMB_MAX_ATTEMPTS; attempt += 1) {
      const retryAfter = Number(res.headers.get('retry-after'));
      const waitMs =
        Number.isFinite(retryAfter) && retryAfter > 0
          ? Math.min(retryAfter * 1000, 30_000)
          : 1000 * 2 ** attempt;
      await thumbSleep(waitMs);
      res = await send();
    }
    return res;
  } finally {
    releaseThumbSlot();
  }
}

// ---------------------------------------------------------------------
// Endpoint wrappers (typed)
// ---------------------------------------------------------------------

import type {
  AuthResult,
  MfaChallenge,
  MfaState,
  Tenant,
  Brief,
  BriefInput,
  Client,
  CreateClientInput,
  CreateJobInput,
  DashboardPayload,
  EmailThread,
  EmailThreadSummary,
  FeedbackComment,
  GmailStatus,
  GmailSyncResult,
  Job,
  JobFigmaBoard,
  MusicTrack,
  MusicGeneration,
  MusicRound,
  Notification,
  TimelineEvent,
  CommentStatus,
  UpcomingShootsPayload,
  Task,
  TaskBoard,
  TasksOverview,
  TaskWeek,
  WeekTask,
  TaskBlockType,
  TaskCalendarSync,
  CommitPlanResult,
  DayReview,
  TaskComment,
  TaskPriority,
  TaskStatus,
  InternalJob,
  InternalJobStatus,
  PersonalTask,
  QuietDaysOverview,
  QuietIdea,
  QuietIdeaEffort,
  QuietIdeaImpact,
  QuietPulse,
  StudioDoc,
  StudioDocSummary,
} from './types';

export const auth = {
  /** Password leg. Answers with tokens, or with an MFA challenge when
   *  the account has two-factor on (then call loginMfa). */
  login: (email: string, password: string) =>
    apiFetch<AuthResult | MfaChallenge>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
      unauthenticated: true,
    }),
  /** Second leg: the challenge from login plus a six-digit code or a
   *  backup code. Rate limited like login itself. */
  loginMfa: (challengeToken: string, code: string) =>
    apiFetch<AuthResult>('/api/auth/login/mfa', {
      method: 'POST',
      body: { challengeToken, code },
      unauthenticated: true,
    }),
  logout: (refreshToken: string) =>
    apiFetch<void>('/api/auth/logout', {
      method: 'POST',
      body: { refreshToken },
    }),
  /**
   * Ask the server to send a password-reset email. Always returns
   * success regardless of whether the email exists in the DB —
   * standard enumeration defence. Caller renders "Check your inbox"
   * either way.
   */
  requestPasswordReset: (email: string) =>
    apiFetch<{ devToken?: string }>('/api/auth/password/reset-request', {
      method: 'POST',
      body: { email },
      unauthenticated: true,
    }),
  /** Consume a reset token and set a new password. Server enforces
   *  1-hour expiry + single-use + revokes all existing sessions on
   *  the user. */
  performPasswordReset: (token: string, password: string) =>
    apiFetch<void>('/api/auth/password/reset', {
      method: 'POST',
      body: { token, password },
      unauthenticated: true,
    }),
  /** Signed-in change (Settings > Account). Server verifies the current
   *  password and revokes every session, so the caller signs out after. */
  changePassword: (currentPassword: string, password: string) =>
    apiFetch<void>('/api/auth/password/change', {
      method: 'POST',
      body: { currentPassword, password },
    }),
  /** Start a login-email change: server verifies the password, then
   *  emails a 1-hour confirm link to the NEW address. 202 either way. */
  requestEmailChange: (newEmail: string, currentPassword: string) =>
    apiFetch<{ devToken?: string }>('/api/auth/email/change-request', {
      method: 'POST',
      body: { newEmail, currentPassword },
    }),
  /** Consume an email-change token (clicked from the mail client,
   *  possibly logged out). Returns the new login email. */
  confirmEmailChange: (token: string) =>
    apiFetch<{ email: string }>('/api/auth/email/change', {
      method: 'POST',
      body: { token },
      unauthenticated: true,
    }),

  // --- A-Roll SaaS public signup ------------------------------------

  /** Create a SaaS workspace + owner account. Server responds 202 and
   *  emails a verification link; no tokens until the email is verified. */
  register: (input: {
    email: string;
    password: string;
    name: string;
    workspaceName: string;
    /** Browser-detected interface language; sets email language from
     *  the first (verification) mail on. */
    locale?: string;
    /** The terms box; the server refuses a signup without it. */
    termsAccepted: true;
    /** Cloudflare Turnstile token, when Turnstile is on. */
    turnstileToken?: string;
    /** The sales page's currency (AUD inc GST or USD): the price set
     *  checkout will use. */
    currency?: 'AUD' | 'USD';
    /** Campaign parameters carried from the landing page. */
    attribution?: Record<string, string>;
  }) =>
    apiFetch<{ devToken?: string }>('/api/auth/register', {
      method: 'POST',
      body: input,
      unauthenticated: true,
    }),
  /** Whether signup is open, and the Turnstile site key when it's on. */
  signupConfig: () =>
    apiFetch<{ signupEnabled: boolean; turnstileSiteKey: string | null }>(
      '/api/auth/signup-config',
      { unauthenticated: true },
    ),
  /** Burn a verification token. Returns a full AuthResult so the app
   *  can log the new owner straight into onboarding. */
  verifyEmail: (token: string) =>
    apiFetch<AuthResult>('/api/auth/verify-email', {
      method: 'POST',
      body: { token },
      unauthenticated: true,
    }),
  /** Always 202 (enumeration-safe). */
  resendVerification: (email: string) =>
    apiFetch<{ devToken?: string | null }>('/api/auth/resend-verification', {
      method: 'POST',
      body: { email },
      unauthenticated: true,
    }),
};

export type SendTarget = { kind: 'draft' | 'cut'; id: string };
export type RoomClient = { id: string; name: string; projectCount: number };
export type ProjectRoom = {
  /** Null until the studio says who the project is for. */
  client: { id: string; name: string; projectCount: number } | null;
  /** The client of the folder's other projects, when there is one. */
  suggestedClient: { id: string; name: string } | null;
  /** The client row the link list / options dialog works on. */
  linkClientId: string;
  /** The room link's token, once something has been sent. */
  token: string | null;
  lastOpenedAt: string | null;
};
export type RoomItemStatus = 'IN_REVIEW' | 'NEEDS_CHANGES' | 'APPROVED';
export type RoomItem =
  | {
      kind: 'cut';
      id: string;
      name: string;
      versionId: string;
      versionNumber: number;
      status: RoomItemStatus;
      sentAt: string | null;
      durationMs: number | null;
    }
  | {
      kind: 'draft';
      id: string;
      assetId: string;
      name: string;
      status: RoomItemStatus;
      /** The client has started their own version of it. */
      hasClientCopy: boolean;
      /** ...and sent it to the studio. */
      changesSent?: boolean;
      /** The row IS the client's version (its draft is no longer out with
       *  them); `id` is the version's own id. */
      versionOnly?: { madeOn: string };
      sentAt: string | null;
      durationMs: number | null;
    };
export type ClientRoomPayload = {
  clientName: string;
  projects: Array<{
    id: string;
    code: string;
    title: string;
    arollApprovedAt: string | null;
    items: RoomItem[];
  }>;
};

/** Both directions of the A-Roll to Playback link (GET .../links). */
export type ProjectLinks = {
  source: { jobId: string; title: string; draftId: string | null; cutName: string | null } | null;
  playback: Array<{ jobId: string; title: string; draftId: string | null }>;
};

/** A-Roll SaaS workspace surface. Only meaningful for users with a
 *  tenantId; the API 403s everyone else. */
export const tenant = {
  get: () => apiFetch<Tenant>('/api/tenant'),
  /** `clientAiEnabled` is owner-only (403 for other members). */
  update: (input: { name?: string; clientAiEnabled?: boolean }) =>
    apiFetch<Tenant>('/api/tenant', { method: 'PATCH', body: input }),
  uploadLogo: (file: File) => uploadBinary<{ logoUrl: string }>('/api/tenant/logo', file),
  /** `aroll`: born with the A-Roll activation on ("Start from A-Roll")
   *  or as a plain review room (a new project from a cut). */
  createProject: (input: { name: string; aroll: boolean }) =>
    apiFetch<Job>('/api/tenant/projects', { method: 'POST', body: input }),
  /** Flip a project's A-Roll activation. Off is refused (409) while
   *  the project still holds clips. */
  setProjectAroll: (jobId: string, on: boolean) =>
    apiFetch<{ id: string; arollEnabled: boolean }>(`/api/tenant/projects/${jobId}/aroll`, {
      method: 'PATCH',
      body: { on },
    }),
  renameProject: (jobId: string, patch: { name?: string; folderId?: string | null }) =>
    apiFetch<Job>(`/api/tenant/projects/${jobId}`, { method: 'PATCH', body: patch }),
  /** One project, one module (2026-10-21): the A-Roll to Playback link,
   *  both ways. */
  projectLinks: (jobId: string) =>
    apiFetch<ProjectLinks>(`/api/tenant/projects/${jobId}/links`),
  // ---- Client room: named clients, one link each, Send ---------------
  /** The workspace's named clients, most recently used first. */
  listClients: () => apiFetch<RoomClient[]>('/api/tenant/clients'),
  renameClient: (clientId: string, name: string) =>
    apiFetch<{ id: string; name: string }>(`/api/tenant/clients/${clientId}`, {
      method: 'PATCH',
      body: { name },
    }),
  /** Who this project is for, and their room link once one exists. */
  projectRoom: (jobId: string) => apiFetch<ProjectRoom>(`/api/tenant/projects/${jobId}/room`),
  /** Say who a project is for. 409 LINK_CHOICE_REQUIRED when the
   *  project already has links out and joins an existing client:
   *  answer with existingLinks. */
  setProjectClient: (
    jobId: string,
    input: { clientId?: string; name?: string; existingLinks?: 'keep' | 'revoke' },
  ) =>
    apiFetch<{ client: { id: string; name: string } }>(`/api/tenant/projects/${jobId}/client`, {
      method: 'PUT',
      body: input,
    }),
  /** Put one cut or draft in the client's room. 409 CLIENT_REQUIRED
   *  until the project has a client. Returns the room link's token. */
  sendToClient: (jobId: string, target: SendTarget) =>
    apiFetch<{
      client: { id: string; name: string };
      token: string;
      lastOpenedAt: string | null;
    }>(`/api/tenant/projects/${jobId}/send`, { method: 'POST', body: target }),
  unsendFromClient: (jobId: string, target: SendTarget) =>
    apiFetch<{ ok: true }>(`/api/tenant/projects/${jobId}/unsend`, {
      method: 'POST',
      body: target,
    }),
  /** Soft-delete: hides the project and frees the Free-tier slot. */
  deleteProject: (jobId: string) =>
    apiFetch<void>(`/api/tenant/projects/${jobId}`, { method: 'DELETE' }),
  /** Frame.io-style version stack: fold project jobId into another —
   *  its cuts become the target's next versions, the husk soft-deletes. */
  stackProject: (jobId: string, targetProjectId: string) =>
    apiFetch<{ moved: number }>(`/api/tenant/projects/${jobId}/stack`, {
      method: 'POST',
      body: { targetProjectId },
    }),
  listFolders: () => apiFetch<TenantFolder[]>('/api/tenant/folders'),
  createFolder: (name: string) =>
    apiFetch<TenantFolder>('/api/tenant/folders', { method: 'POST', body: { name } }),
  renameFolder: (id: string, name: string) =>
    apiFetch<TenantFolder>(`/api/tenant/folders/${id}`, { method: 'PATCH', body: { name } }),
  deleteFolder: (id: string) =>
    apiFetch<{ movedToRoot: number }>(`/api/tenant/folders/${id}`, { method: 'DELETE' }),
};

export type SaasTenantRow = {
  id: string;
  name: string;
  createdAt: string;
  state: 'free' | 'trial' | 'active' | 'past_due' | 'expired' | 'canceled';
  planKey: 'free' | 'review' | 'starter' | 'pro' | 'agency' | null;
  planLabel: string;
  priceUsd: number;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  usage: {
    draftsUsed: number;
    draftsAllowance: number;
    minutesUsed: number;
    minutesAllowance: number;
  };
  aiSpendMonthUsd: number;
  users: number;
  projects: number;
  /** Internal (test) workspace: listed, but left out of the totals. */
  isInternal?: boolean;
};

export type SaasOverview = {
  totals: {
    tenants: number;
    active: number;
    trials: number;
    mrrUsd: number;
    aiSpendMonthUsd: number;
    aiPoolCapUsd: number;
  };
  tenants: SaasTenantRow[];
};

export type SaasTenantAssetRow = {
  id: string;
  jobId: string;
  jobTitle: string;
  filename: string;
  sizeBytes: number | null;
  durationMs: number | null;
  status: string;
  errorMessage: string | null;
  ingest: 'mux' | 'dropbox';
  muxUploadId: string | null;
  muxAssetId: string | null;
  muxPlaybackId: string | null;
  muxImageTokens?: { thumbnail: string; gif: string; storyboard: string } | null;
  mediaExpiredAt: string | null;
  createdAt: string;
  transcribedAt: string | null;
  draftCount: number;
  highlightCount: number;
  noteCount: number;
};

export type SaasTenantDetail = {
  tenant: SaasTenantRow & { ownerEmail: string | null };
  users: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    createdAt: string;
    isOwner: boolean;
    /** Null while the signup is stranded on "check your email". */
    emailVerifiedAt: string | null;
  }>;
  projects: Array<{ id: string; title: string; createdAt: string; assetCount: number }>;
  assets: SaasTenantAssetRow[];
  recentDrafts: Array<{
    id: string;
    assetId: string;
    filename: string;
    name: string;
    kind: string;
    durationMs: number | null;
    isClientDraft: boolean;
    createdAt: string;
  }>;
  recentAiCalls: Array<{
    createdAt: string;
    feature: string;
    model: string;
    estCostUsd: number;
  }>;
};

/** A-Roll SaaS back office (owner-only). */
export const saasAdmin = {
  overview: () => apiFetch<SaasOverview>('/api/saas-admin/overview'),
  tenant: (id: string) =>
    apiFetch<SaasTenantDetail>(`/api/saas-admin/tenants/${encodeURIComponent(id)}`),
  /** Wave a stranded signup through email verification by hand — the
   *  account exists and the password is set, this only lifts the gate
   *  the verification email would have lifted. Owner-only. */
  /** Mark a workspace internal (a test login: no limits, no billing,
   *  out of the totals) or a customer again. Owner-only. */
  setInternal: (tenantId: string, isInternal: boolean) =>
    apiFetch<{ id: string; isInternal: boolean }>(
      `/api/saas-admin/tenants/${encodeURIComponent(tenantId)}/internal`,
      { method: 'POST', body: { isInternal } },
    ),
  verifyUserEmail: (userId: string) =>
    apiFetch<{ id: string; email: string; emailVerifiedAt: string }>(
      `/api/saas-admin/users/${encodeURIComponent(userId)}/verify-email`,
      { method: 'POST', body: {} },
    ),
  /** Running-costs panel: measured Mux/AssemblyAI/Claude usage plus the
   *  hand-entered flat lines (Railway, Postmark, Cloudflare, domains). */
  costs: () => apiFetch<SaasCosts>('/api/saas-admin/costs'),
  saveManualCosts: (patch: Partial<SaasManualCosts>) =>
    apiFetch<SaasCosts>('/api/saas-admin/costs/manual', { method: 'PATCH', body: patch }),
};

export type SaasManualCosts = {
  railway: number;
  postmark: number;
  cloudflare: number;
  domains: number;
  other: number;
};

export type SaasCostLine = {
  key: string;
  service: string;
  basis: string;
  kind: 'measured' | 'derived' | 'manual';
  monthlyUsd: number;
  excluded?: boolean;
};

export type SaasCosts = {
  lines: SaasCostLine[];
  totalUsd: number;
  manual: SaasManualCosts;
  month: string;
  degraded: string[];
};

export const billing = {
  /** `currency` previews the status (prices, purchasable rungs) in the
   *  other price set without changing the workspace's stamp. */
  status: (currency?: 'AUD' | 'USD') =>
    apiFetch<BillingStatus>(`/api/billing/status${currency ? `?currency=${currency}` : ''}`),
  /** Card on file for the settings Plan tab; null when no card yet. */
  paymentMethod: () =>
    apiFetch<{ paymentMethod: { brand: string; last4: string } | null }>(
      '/api/billing/payment-method',
    ),
  checkout: (
    plan: 'review' | 'starter' | 'pro' | 'agency',
    cadence: 'monthly' | 'annual' = 'monthly',
    currency?: 'AUD' | 'USD',
  ) =>
    apiFetch<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: { plan, cadence, ...(currency ? { currency } : {}) },
    }),
  portal: () => apiFetch<{ url: string }>('/api/billing/portal', { method: 'POST' }),
};

export const calendar = {
  upcomingShoots: (days = 60) =>
    apiFetch<UpcomingShootsPayload>(`/api/calendar/upcoming-shoots?days=${days}`),
  /** Link a calendar event (from Upcoming Shoots) to a job — creates a
   *  shoot day on it for the event's date. */
  linkShoot: (input: {
    eventId: string;
    jobId: string;
    date: string;
    kind: 'SHOOT' | 'HOLD';
  }) =>
    apiFetch<void>('/api/calendar/link-shoot', { method: 'POST', body: input }),
  /** Unlink a manually-linked calendar event from its job. */
  unlinkShoot: (eventId: string) =>
    apiFetch<void>('/api/calendar/unlink-shoot', {
      method: 'POST',
      body: { eventId },
    }),
  /** Hide a calendar event from the Upcoming Shoots card (the Google
   *  connection is read-only; nothing is deleted at the source). */
  dismissShoot: (eventId: string) =>
    apiFetch<void>('/api/calendar/dismiss-shoot', {
      method: 'POST',
      body: { eventId },
    }),
  /** Undo a dismissal (the toast's Undo action). */
  undismissShoot: (eventId: string) =>
    apiFetch<void>('/api/calendar/undismiss-shoot', {
      method: 'POST',
      body: { eventId },
    }),
};

export type PersonalAccessToken = {
  id: string;
  name: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
};

/** Anthropic (Claude API) spend rollup for the current calendar month.
 *  Mirrors the backend ClaudeUsageRollup. Spend is billed on the
 *  Anthropic developer account, separate from any Claude subscription. */
export type ClaudeUsageRollup = {
  range: { start: string; end: string };
  /** KG pool against the EFFECTIVE cap (what the guard enforces). */
  budget: {
    capUsd: number;
    spentUsd: number;
    remainingUsd: number;
    exceeded: boolean;
  };
  /** PictureUp (SaaS tenants) pool, capped on its own line. */
  saasBudget: {
    capUsd: number;
    spentUsd: number;
    remainingUsd: number;
    exceeded: boolean;
  };
  totals: {
    callCount: number;
    inputTokens: number;
    outputTokens: number;
    searchCalls: number;
    estCostUsd: number;
  };
  failed: {
    callCount: number;
    estCostUsd: number;
    last24hCount: number;
    lastFailedAt: string | null;
    byFeature: Array<{ feature: string; callCount: number; lastFailedAt: string }>;
  };
  byFeature: Array<{
    feature: string;
    callCount: number;
    inputTokens: number;
    outputTokens: number;
    searchCalls: number;
    estCostUsd: number;
  }>;
  byModel: Array<{ model: string; callCount: number; estCostUsd: number }>;
  daily: Array<{ date: string; estCostUsd: number; callCount: number }>;
};

/** Higgsfield platform-API spend for the current calendar month, in
 *  credits (native unit) with a soft USD estimate. Mirrors the backend
 *  HiggsfieldUsageRollup. This is the app's OWN API spend, billed
 *  separately from the Higgsfield web/subscription credit pool. */
export type HiggsfieldUsageRollup = {
  range: { start: string; end: string };
  budget: {
    capCredits: number;
    spentCredits: number;
    remainingCredits: number;
    exceeded: boolean;
  };
  creditPriceUsd: number;
  totals: { callCount: number; credits: number; estCostUsd: number };
  failed: { callCount: number; credits: number; estCostUsd: number };
  byFeature: Array<{
    feature: string;
    callCount: number;
    credits: number;
    estCostUsd: number;
  }>;
  byModel: Array<{
    model: string;
    callCount: number;
    credits: number;
    estCostUsd: number;
  }>;
  daily: Array<{ date: string; credits: number; callCount: number }>;
};

/** Combined monthly running cost: recurring subscriptions (per currency)
 *  + this month's metered API spend (Claude + Higgsfield, USD). Owner
 *  only. Currencies are kept separate (never summed across). */
export type RunningCostsSummary = {
  apiCurrency: string;
  byCurrency: Array<{
    currency: string;
    subscriptionsMonthly: number;
    apiMonthly: number;
    totalMonthly: number;
  }>;
  /** Month to date, NOT a monthly rate. */
  api: {
    claudeUsd: number;
    higgsfieldUsd: number;
    higgsfieldCredits: number;
    monthToDateUsd: number;
    claudeProjectedUsd: number | null;
    dayOfMonth: number;
    daysInMonth: number;
  };
  subscriptions: {
    totalsByCurrency: Array<{ currency: string; monthly: number; annual: number }>;
    unpricedCount: number;
  };
};

export const admin = {
  /** Month-to-date Claude API spend, capped + broken down. ADMIN only. */
  claudeUsage: () => apiFetch<ClaudeUsageRollup>('/api/admin/claude-usage'),
  /** Month-to-date Higgsfield platform-API credit spend. ADMIN only. */
  higgsfieldUsage: () =>
    apiFetch<HiggsfieldUsageRollup>('/api/admin/higgsfield-usage'),
  /** Combined monthly running cost (subscriptions + API). OWNER only. */
  runningCosts: () => apiFetch<RunningCostsSummary>('/api/admin/running-costs'),
  /** Current Anthropic monthly cap (effective + override + env default) and
   *  month-to-date spend. OWNER only. */
  anthropicCap: () =>
    apiFetch<{
      effectiveCapUsd: number;
      spentUsd: number;
      overrideUsd: number | null;
      envCapUsd: number;
    }>('/api/admin/anthropic-cap'),
  /** Set/clear the Anthropic monthly cap override (USD). `0` disables the
   *  ceiling, `null` clears the override (falls back to the env default).
   *  OWNER only. */
  setAnthropicCap: (capUsd: number | null) =>
    apiFetch<{ ok: boolean; effectiveCapUsd: number }>('/api/admin/anthropic-cap', {
      method: 'PUT',
      body: { capUsd },
    }),
};

/** Two-factor login (Settings > Security). Backend: modules/auth/mfa-service. */
export const mfa = {
  state: () => apiFetch<MfaState>('/api/auth/mfa'),
  /** Begin enrolment: the secret to type and the otpauth URI to scan.
   *  Nothing is enforced until confirm() proves possession. */
  setup: () =>
    apiFetch<{ secret: string; otpauthUri: string }>('/api/auth/mfa/setup', { method: 'POST' }),
  /** Prove possession with a current code. Backup codes come back ONCE. */
  confirm: (code: string) =>
    apiFetch<{ recoveryCodes: string[] }>('/api/auth/mfa/confirm', {
      method: 'POST',
      body: { code },
    }),
  /** Fresh backup codes (the old set stops working). Needs a current code. */
  regenerateCodes: (code: string) =>
    apiFetch<{ recoveryCodes: string[] }>('/api/auth/mfa/recovery-codes', {
      method: 'POST',
      body: { code },
    }),
  /** Turn it off: password AND a current code. Refused for Kilogram staff. */
  disable: (currentPassword: string, code: string) =>
    apiFetch<void>('/api/auth/mfa/disable', {
      method: 'POST',
      body: { currentPassword, code },
    }),
};

export const me = {
  /** Current user (incl. isOwner). Refreshes the stored session so
   *  owner-only UI is reliable without forcing a re-login. */
  profile: () => apiFetch<import('./types').UserPublic>('/api/me'),
  /** Self-service update: display name + notification preferences.
   *  Email changes go through auth.requestEmailChange instead. */
  updateProfile: (input: {
    name?: string;
    notifyByEmail?: boolean;
    notifyProjectUpdates?: boolean;
    notifyProductNews?: boolean;
    locale?: string;
  }) => apiFetch<import('./types').UserPublic>('/api/me', { method: 'PATCH', body: input }),
  /** all=1: the board shows the parked Delivered / Cancelled sections and
   *  the Launch lane, so it needs every job; the API's default is the
   *  active list (not finished, or money still owing). */
  dashboard: () => apiFetch<DashboardPayload>('/api/me/dashboard?all=1'),
  /** Deal funnel; fy scopes Won / Lost to jobs opened in that financial year. */
  pipeline: (fy?: number) =>
    apiFetch<import('./types').Pipeline>(`/api/me/pipeline${fy ? `?fy=${fy}` : ''}`),
  notifications: (unreadOnly = false) =>
    apiFetch<Notification[]>(
      `/api/me/notifications${unreadOnly ? '?unread=true' : ''}`,
    ),
  readAll: () => apiFetch<{ updated: number }>('/api/me/notifications/read-all', { method: 'POST' }),
  /** Personal access tokens (for the Resolve connector etc.). */
  listTokens: () => apiFetch<PersonalAccessToken[]>('/api/me/tokens'),
  createToken: (name: string, expiresInDays?: number | null) =>
    apiFetch<PersonalAccessToken & { token: string }>('/api/me/tokens', {
      method: 'POST',
      body: { name, expiresInDays: expiresInDays ?? null },
    }),
  revokeToken: (id: string) =>
    apiFetch<void>(`/api/me/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export type DropboxEntry = {
  name: string;
  path: string;
  isFolder: boolean;
};

/** Outcome of the last Xero -> CRM mirror sync (null = never run). */
export type XeroLastSync = {
  at: string;
  ok: boolean;
  error: string | null;
  checked: number;
  changed: number;
};

export type XeroStatus =
  | { configured: false; connected: false; lastSync: XeroLastSync | null }
  | {
      configured: true;
      connected: true;
      tenantName: string;
      connectedAt: string;
      lastSync: XeroLastSync | null;
      /** Org short code for "Open in Xero" links (null if unreadable). */
      shortCode: string | null;
    }
  | { configured: true; connected: false; error: string; lastSync: XeroLastSync | null };

export type SocialAccountRow = {
  id: string;
  platform: 'INSTAGRAM' | 'FACEBOOK' | 'LINKEDIN';
  username: string;
  pageId: string | null;
  externalId: string;
  status: string;
  lastError: string | null;
  lastCheckedAt: string | null;
  createdAt: string;
};

/** Meta (Instagram + Facebook Page) accounts for the Launch module. */
export const meta = {
  accounts: () => apiFetch<{ configured: boolean; redirectUri: string; accounts: SocialAccountRow[] }>('/api/meta/accounts'),
  connect: () => apiFetch<{ url: string; redirectUri: string }>('/api/meta/connect', { method: 'POST' }),
  check: (id: string) => apiFetch<{ accounts: SocialAccountRow[] }>(`/api/meta/accounts/${encodeURIComponent(id)}/check`, { method: 'POST' }),
  disconnect: (id: string) => apiFetch<void>(`/api/meta/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const xero = {
  status: () => apiFetch<XeroStatus>('/api/xero/status'),
  /** Live "test connection" — returns the Xero org name. */
  test: () => apiFetch<{ name: string }>('/api/xero/test', { method: 'POST' }),
  disconnect: () => apiFetch<void>('/api/xero/disconnect', { method: 'POST' }),
  /** Customer contacts in Xero, by name/email fragment or one by id. */
  contacts: (opts: { q?: string; id?: string }) => {
    const p = new URLSearchParams();
    if (opts.q) p.set('q', opts.q);
    if (opts.id) p.set('id', opts.id);
    return apiFetch<XeroContactSummary[]>(`/api/xero/contacts?${p.toString()}`);
  },
};

export type XeroContactSummary = { id: string; name: string; email: string | null };

export const dropbox = {
  status: () => apiFetch<import('./types').DropboxStatus>('/api/dropbox/status'),
  start: () =>
    apiFetch<{ url: string; state: string }>('/api/dropbox/auth/start', {
      method: 'POST',
    }),
  disconnect: () =>
    apiFetch<void>('/api/dropbox/disconnect', { method: 'POST' }),
  /** List subfolders + files at a given Dropbox path. Empty / "/"
   *  lists the root. Powers the Browse Dropbox folder picker on the
   *  job Connectors tab. */
  listFolder: (path: string) => {
    const params = new URLSearchParams();
    if (path) params.set('path', path);
    return apiFetch<{ path: string; entries: DropboxEntry[] }>(
      `/api/dropbox/list-folder${params.toString() ? `?${params}` : ''}`,
    );
  },
};

export const jobs = {
  list: () => apiFetch<Job[]>('/api/jobs'),
  get: (id: string) => apiFetch<Job>(`/api/jobs/${id}`),
  /** The creative pathway: where the job (or pitch) is and what to press next. */
  pathway: (id: string) => apiFetch<Pathway>(`/api/jobs/${id}/pathway`),
  /** Send the Treatment to the client: the deck PDF and the Figma link go onto their portal and stay there. */
  sendTreatment: (id: string) => apiFetch<Pathway>(`/api/jobs/${id}/treatment/send`, { method: 'POST' }),
  /** Switch an optional stage (Concepts, Storyboard, Deck) on or off on a client job. */
  setPathwayStage: (id: string, stage: PathwayStageId, on: boolean) =>
    apiFetch<Pathway>(`/api/jobs/${id}/pathway/stages`, { method: 'PUT', body: { stage, on } }),
  timeline: (id: string) => apiFetch<TimelineEvent[]>(`/api/jobs/${id}/timeline`),
  portalView: (id: string) =>
    apiFetch<{ active: import('./types').PortalJobView }>(
      `/api/jobs/${encodeURIComponent(id)}/portal-view`,
    ),
  create: (input: CreateJobInput) =>
    apiFetch<Job>('/api/jobs', { method: 'POST', body: input }),
  /** Confirm a tentative job: stamps confirmedAt and creates the
   *  deferred Dropbox job bag (same tree job creation would have
   *  built). Idempotent. Returns the refreshed job. */
  confirm: (id: string) =>
    apiFetch<Job>(`/api/jobs/${encodeURIComponent(id)}/confirm`, {
      method: 'POST',
    }),
  /** One-shot: bring every existing job's Dropbox folder up to the
   *  current template (creates any missing subfolders, e.g. Web
   *  Rollovers; never deletes). ADMIN only. Returns the number of jobs
   *  queued; the top-up runs in the background. */
  syncAllFolders: () =>
    apiFetch<{ total: number }>('/api/jobs/dropbox/sync-all', { method: 'POST' }),
  update: (id: string, patch: Partial<Job> & Record<string, unknown>) =>
    apiFetch<Job>(`/api/jobs/${id}`, { method: 'PATCH', body: patch }),
  /** Reassign the job to a different client (ADMIN only). Moves the
   *  whole Dropbox job bag into the new client's folder server-side
   *  and rewrites every stored path under it. Returns the refreshed
   *  job. */
  changeClient: (id: string, clientId: string) =>
    apiFetch<Job>(`/api/jobs/${encodeURIComponent(id)}/client`, {
      method: 'POST',
      body: { clientId },
    }),
  autoUpdate: (id: string) =>
    apiFetch<{ queueJobId: string }>(`/api/jobs/${id}/auto-update`, {
      method: 'POST',
    }),
  scrapeEmails: (id: string) =>
    apiFetch<{ processed: number; uploaded: number; failed: number; skipped: number }>(
      `/api/jobs/${id}/scrape-emails`,
      { method: 'POST' },
    ),
  /** Draft a warm "your latest cut is ready to view" email to the
   *  client (Claude, Haiku). Returns body + pre-filled to/subject.
   *  Nothing is sent. */
  draftStatusEmail: (id: string, instructions?: string) =>
    apiFetch<{ to: string; subject: string; body: string }>(
      `/api/jobs/${encodeURIComponent(id)}/status-email/draft`,
      { method: 'POST', body: instructions ? { instructions } : {} },
    ),
  /** Preload the job page's Reply composer for a cut: picks the chain +
   *  recipient, mints a fresh no-login share link, writes the covering
   *  note. No AI call. Latest cut when versionId is omitted. */
  prepareReadyEmail: (id: string, versionId?: string) =>
    apiFetch<{
      threadId: string | null;
      to: string;
      subject: string;
      body: string;
      shareUrl: string;
      versionLabel: string;
      instructions: string;
    }>(`/api/jobs/${encodeURIComponent(id)}/status-email/prepare`, {
      method: 'POST',
      body: versionId ? { versionId } : {},
    }),
  /** Save the (edited) status email as a Gmail draft. Never sends. */
  saveStatusEmail: (
    id: string,
    input: { to: string; subject: string; body: string },
  ) =>
    apiFetch<{ draftId: string; gmailThreadId: string }>(
      `/api/jobs/${encodeURIComponent(id)}/status-email/save`,
      { method: 'POST', body: input },
    ),
  deleteAsset: (jobId: string, assetId: string) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/assets/${encodeURIComponent(assetId)}`,
      { method: 'DELETE' },
    ),
  /**
   * Upload a file to the job's Dropbox folder. Sends the raw bytes
   * with the filename in an X-Filename header so the server doesn't
   * need a multipart parser. Returns the new JobAsset row.
   */
  uploadAsset: async (
    id: string,
    file: File,
    kind:
      | 'PROJECT_FILE'
      | 'JOB_BRIEF'
      | 'SCHEDULE_BRIEF'
      | 'KG_INTERNAL'
      | 'CONTENT_ASSET'
      | 'STILLS' = 'PROJECT_FILE',
  ): Promise<import('./types').PortalUpload> => {
    const raw = await uploadBinary<{
      id: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      sharedUrl: string | null;
      source: 'KG_UPLOAD' | 'CLIENT_UPLOAD';
      createdAt: string;
    }>(
      `/api/jobs/${encodeURIComponent(id)}/assets/upload`,
      file,
      { 'X-Asset-Kind': kind },
    );
    return {
      id: raw.id,
      filename: raw.filename,
      mimeType: raw.mimeType,
      sizeBytes: raw.sizeBytes,
      sharedUrl: raw.sharedUrl,
      source: raw.source,
      uploadedByName: null,
      uploadedAt: raw.createdAt,
    };
  },
};

// =====================================================================
// Content planner (per-job social content staging). Internal tool:
// ADMIN / EDITOR only. Staging assets are JobAssets tagged
// CONTENT_ASSET; posts group them with a caption + workflow.
// =====================================================================

export type ContentPlatform =
  | 'INSTAGRAM'
  | 'TIKTOK'
  | 'YOUTUBE'
  | 'LINKEDIN'
  | 'FACEBOOK'
  | 'OTHER';

export type ContentPostStatus = 'DRAFT' | 'READY' | 'POSTED' | 'SKIPPED' | 'SCHEDULED' | 'PUBLISHING' | 'FAILED';

/** Slot in the job's content plan. OTHER = an ad-hoc post. */
export type ContentPostType =
  | 'HERO_REEL'
  | 'BTS_REEL'
  | 'BTS_CAROUSEL'
  | 'CRAFT'
  | 'STORIES'
  | 'LINKEDIN'
  | 'OTHER';

export interface ContentAsset {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  sharedUrl: string | null;
  createdAt: string;
  /** Derivatives on R2, made once by the worker: a poster and a web
   *  proxy the tile uses directly. Null while preparing (or on an
   *  older row until the backfill reaches it). */
  mediaStatus: string | null;
  posterUrl: string | null;
  proxyUrl: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
}

export interface ContentPost {
  id: string;
  jobId: string;
  caption: string;
  platform: ContentPlatform;
  status: ContentPostStatus;
  type: ContentPostType;
  plannedAt: string | null;
  postedAt: string | null;
  liveUrl: string | null;
  skippedAt: string | null;
  skipReason: string | null;
  /** Publishing from the CRM (PR 6). Null until a post is checked. */
  accountId?: string | null;
  containerId?: string | null;
  checkStatus?: string | null;
  checkError?: string | null;
  checkedAt?: string | null;
  publishError?: string | null;
  publishAttempts?: number;
  /** Wants Instagram's own trending audio: goes in the phone bundle. */
  phoneAudio?: boolean;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
  assets: ContentAsset[];
}

export interface CreateContentPostInput {
  caption?: string;
  platform?: ContentPlatform;
  type?: ContentPostType;
  plannedAt?: string | null;
  assetIds?: string[];
}

export interface UpdateContentPostInput {
  caption?: string;
  platform?: ContentPlatform;
  status?: ContentPostStatus;
  type?: ContentPostType;
  plannedAt?: string | null;
  liveUrl?: string | null;
  /** Required when moving to SKIPPED. */
  skipReason?: string | null;
  assetIds?: string[];
}

/** Result of starting a job's content plan (the default post set). */
export interface ContentPlanResult {
  created: number;
  already: boolean;
  posts: ContentPost[];
}

// ---- Content Studio (generation) ----
export type ContentRenderKind = 'BTS_REEL' | 'STILLS_CAROUSEL';
export type ContentRenderStatus = 'QUEUED' | 'PROCESSING' | 'READY' | 'FAILED';

export interface ContentRender {
  id: string;
  kind: ContentRenderKind;
  status: ContentRenderStatus;
  resultAssetId: string | null;
  postId: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GenerationSources {
  videos: ContentAsset[];
  images: ContentAsset[];
}

export const content = {
  // ---- Staging assets ----
  listAssets: (jobId: string) =>
    apiFetch<ContentAsset[]>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/assets`,
    ),
  /** Direct, range-capable URL (Dropbox temp link, ~4h) so <video> can
   *  show a first frame and play a staged clip inline. url is null when
   *  Dropbox won't mint one; the UI keeps its glyph. */
  previewUrl: (jobId: string, assetId: string) =>
    apiFetch<{ url: string | null; expiresInSeconds: number }>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/assets/${encodeURIComponent(assetId)}/preview`,
    ),
  /** Start the job's content plan (one draft per default slot). Idempotent. */
  createPlan: (jobId: string) =>
    apiFetch<ContentPlanResult>(`/api/jobs/${encodeURIComponent(jobId)}/content/plan`, {
      method: 'POST',
    }),
  /** Upload a staging asset (BTS / on-set clip or still). Reuses the
   *  job asset upload path with the CONTENT_ASSET kind. */
  uploadAsset: (jobId: string, file: File) =>
    jobs.uploadAsset(jobId, file, 'CONTENT_ASSET'),
  deleteAsset: (jobId: string, assetId: string) =>
    jobs.deleteAsset(jobId, assetId),
  /** Authed blob of an asset for an <img src> thumbnail. Bearer token
   *  in the header + one refresh retry. Caller wraps in createObjectURL. */
  assetBlob: async (jobId: string, assetId: string): Promise<Blob> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/assets/${encodeURIComponent(assetId)}/stream`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** Poster JPEG for a staged clip (Dropbox thumbnail). Null when Dropbox
   *  has none (204): the tile keeps its glyph but stays tappable. */
  assetThumb: async (jobId: string, assetId: string): Promise<Blob | null> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/content/assets/${encodeURIComponent(assetId)}/thumbnail`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (res.status === 204) return null;
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** Trigger a browser download of a staging asset (authed blob). */
  downloadAsset: async (
    jobId: string,
    assetId: string,
    filename: string,
  ): Promise<void> => {
    const tokens = getTokens();
    const headers: Record<string, string> = {};
    if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
    const res = await fetch(
      `/api/jobs/${encodeURIComponent(jobId)}/assets/${encodeURIComponent(assetId)}/stream?download=1`,
      { headers },
    );
    if (!res.ok) throw new ApiClientError(res.status, null);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
  },

  // ---- Posts ----
  listPosts: (jobId: string) =>
    apiFetch<ContentPost[]>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/posts`,
    ),
  createPost: (jobId: string, input: CreateContentPostInput = {}) =>
    apiFetch<ContentPost>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/posts`,
      { method: 'POST', body: input },
    ),
  updatePost: (jobId: string, postId: string, input: UpdateContentPostInput) =>
    apiFetch<ContentPost>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/posts/${encodeURIComponent(postId)}`,
      { method: 'PATCH', body: input },
    ),
  deletePost: (jobId: string, postId: string) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/posts/${encodeURIComponent(postId)}`,
      { method: 'DELETE' },
    ),

  // ---- Content Studio (generation) ----
  /** Staged content assets a generator can use, split video / image. */
  listSources: (jobId: string) =>
    apiFetch<GenerationSources>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/generation/sources`,
    ),
  /** Kick off a generation. Returns the QUEUED render to poll. */
  createRender: (
    jobId: string,
    input: { kind: ContentRenderKind; assetIds: string[] },
  ) =>
    apiFetch<ContentRender>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/renders`,
      { method: 'POST', body: input },
    ),
  /** Poll generation status; on READY a new DRAFT post exists. */
  listRenders: (jobId: string) =>
    apiFetch<ContentRender[]>(
      `/api/jobs/${encodeURIComponent(jobId)}/content/renders`,
    ),
};

// ---- Content Studio (studio-level: review queue + agent monitoring) ----
export type ContentAgentRunStatus = 'OK' | 'PARTIAL' | 'FAILED';

export interface ContentAgentRun {
  id: string;
  status: ContentAgentRunStatus;
  summary: string;
  stats: unknown | null;
  error: string | null;
  createdAt: string;
}

export interface ContentReviewItem {
  id: string;
  jobId: string;
  jobCode: string;
  jobTitle: string;
  status: ContentPostStatus;
  platform: ContentPlatform;
  type: ContentPostType;
  captionPreview: string;
  assetCount: number;
  firstAssetId: string | null;
  firstAssetMime: string | null;
  byAgent: boolean;
  createdAt: string;
}

export interface ContentSummary {
  reviewCount: number;
  /** Delivered jobs that still owe content. */
  owedCount: number;
  lastRun: ContentAgentRun | null;
}

/** One delivered job in the "content owed" lane. */
export interface ContentOwedRow {
  jobId: string;
  code: string;
  title: string;
  clientName: string | null;
  deliveredAt: string | null;
  contentEmbargoUntil: string | null;
  videoAssets: number;
  imageAssets: number;
  summary: {
    total: number;
    open: number;
    posted: number;
    skipped: number;
    openTypes: ContentPostType[];
  };
  slots: Array<{ id: string; type: ContentPostType; status: ContentPostStatus; hasAssets: boolean }>;
}

export const contentStudio = {
  /** Pending-review count + last agent run (nav badge / dashboard line). */
  summary: () => apiFetch<ContentSummary>('/api/content/summary'),
  /** The "content owed" lane: delivered jobs with open posts, oldest first. */
  owed: () => apiFetch<ContentOwedRow[]>('/api/content/owed'),
  /** Cross-job drafts awaiting review (the dedicated Content page). */
  reviewQueue: () => apiFetch<ContentReviewItem[]>('/api/content/review-queue'),
  /** Recent weekly-agent runs (activity log). */
  agentRuns: () => apiFetch<ContentAgentRun[]>('/api/content/agent-runs'),
};

export type ShowcaseConsent = 'UNKNOWN' | 'ASKED' | 'OK' | 'NO';
/** Derived on the server (modules/launch/status.ts); never set from here. */
export type LaunchStatus = 'HELD' | 'READY' | 'IN_REVIEW' | 'PUBLISHED' | 'DONE' | 'FAILED';
export type LaunchBlocker = { code: string; message: string; severity: 'hold' | 'fix' | 'note' };
export type LaunchManifestFile = {
  name: string;
  group: string | null;
  aspect: '16:9' | '9:16' | '4:5' | '1:1' | null;
  isVideo: boolean;
  size: number;
  dropboxPath: string;
};
export type LaunchManifest = {
  job: { id: string; code: string; title: string; medium: string; deliveredAt: string | null };
  client: { id: string; name: string; industry: string | null; isAgency: boolean; showcaseConsent: ShowcaseConsent };
  finals: { count: number; hasLandscape: boolean; hasPortrait: boolean; files: LaunchManifestFile[] };
  rollovers: { count: number; files: LaunchManifestFile[] };
  stills: { lowRes: number; selected: number };
  bts: { videos: number; images: number };
  people: { crew: Array<{ role: string; name: string; contractorId: string | null }>; additional: Array<{ role: string; name: string }> };
  story: { brief: boolean; treatment: boolean; emailThreads: number };
  content: { posts: number; filled: number };
  folio: { pieceId: string; slug: string; status: string } | null;
  /** Filled once the website stage has drafted or extended a piece. */
  website?: {
    pieceId: string;
    slug: string;
    status: string;
    created: boolean;
    notes: string[];
    previewUrl: string;
  } | null;
  /** What the single write step produced, or why it wrote nothing. */
  copy?: { model: string | null; website: boolean; captions: number; skipped: string | null } | null;
  embargoUntil: string | null;
  gatheredAt: string;
};
export type Launch = {
  id: string;
  jobId: string;
  status: LaunchStatus;
  building: boolean;
  manifest: LaunchManifest | null;
  blockers: LaunchBlocker[] | null;
  stale: boolean;
  staleReason: string | null;
  folioPieceId: string | null;
  cutRequestedAt: string | null;
  cutCompletedAt: string | null;
  cutError: string | null;
  cutTimelines: Array<{ name: string; seconds: number; clips: number }> | null;
  rolloverRequestedAt: string | null;
  rolloverCompletedAt: string | null;
  rolloverError: string | null;
  rolloverPhase: string | null;
  rolloverWindows: RolloverWindow[] | null;
  rolloverApproved: string[] | null;
  rolloverSheetAssetId: string | null;
  rolloverRenderName: string | null;
  handedOverAt: string | null;
  error: string | null;
  builtAt: string | null;
  updatedAt: string;
};
export type LaunchTracks = {
  website: 'none' | 'draft' | 'published';
  social: { total: number; open: number; posted: number; skipped: number; rendered: number };
  rollover: 'none' | 'cutting' | 'cut' | 'wired';
};
export type LaunchCutState = 'none' | 'queued' | 'cutting' | 'stuck' | 'cut' | 'parked' | 'handed-over';
export type LaunchRolloverState = 'none' | 'queued' | 'proposing' | 'review' | 'rendering' | 'stuck' | 'rendered' | 'wired' | 'parked';
export type RolloverWindow = { id: string; master: string; start: number; end: number; sheetIndex: number };
export type LaunchView = {
  jobStatus: string;
  gate: { showcaseConsent: ShowcaseConsent; embargoUntil: string | null };
  launch: Launch | null;
  tracks: LaunchTracks | null;
  /** The Resolve cut lane, in words. */
  cut: { state: LaunchCutState; detail: string | null } | null;
  /** The rollover lane, in words. */
  rollover: { state: LaunchRolloverState; detail: string | null } | null;
};
export type LaunchRow = {
  launchId: string;
  /** Null on a custom launch (not tied to one job). */
  jobId: string | null;
  /** Empty on a custom launch. */
  code: string;
  title: string;
  clientName: string | null;
  deliveredAt: string | null;
  status: LaunchStatus;
  building: boolean;
  stale: boolean;
  builtAt: string | null;
  holds: number;
  fixes: number;
  tracks: LaunchTracks;
  planStatus: LaunchModulePlanStatus;
  /** The module's posts, cancelled ones left out. Scheduled ones still go out after archiving. */
  posts: LaunchBoardCounts;
  /** Set on a custom launch: the codes of the jobs its footage comes from. */
  custom: { sourceCodes: string[] } | null;
  archivedAt: string | null;
};

export type LaunchModulePlanStatus = 'DRAFT' | 'APPROVED' | 'SCHEDULED' | 'LIVE' | 'DONE';

/**
 * The Launch module answers for a job's own launch (key = job id) and for
 * a custom launch not tied to one job (key = `launch:<launch id>`).
 */
export const customLaunchKey = (launchId: string) => `launch:${launchId}`;

/** Where a launch row opens: the job's Launch tab, or the custom launch's own page. */
export const launchHref = (row: { jobId: string | null; launchId: string }) =>
  row.jobId ? `/jobs/${row.jobId}?tab=launch` : `/launches/${row.launchId}`;

// ---------------------------------------------------------------------
// Launch module (LAUNCH_MODULE_SPEC.md): assets, plan, credits, approve.
// ---------------------------------------------------------------------

export type LaunchAssetKind = 'HERO' | 'BTS_REEL' | 'STILL';
export type LaunchPostType = 'REEL' | 'STORY' | 'CAROUSEL';
export type LaunchPostStatus = 'PENDING' | 'UPLOADING' | 'SCHEDULED' | 'PUBLISHED' | 'FAILED' | 'CANCELLED';
export type LaunchCoverSource = 'PRODUCT' | 'FACE' | 'BTS' | 'STILL' | 'FRAME';

export type LaunchModuleAsset = {
  id: string;
  kind: LaunchAssetKind;
  filename: string;
  subfolder: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  role: string | null;
  orderIndex: number;
  selected: boolean;
  /** Unticked by the dedupe as a near-double of this other still. */
  duplicateOfId: string | null;
  storyUrl: string | null;
  contactUrl: string | null;
  contactMeta: { cols: number; rows: number; count: number; intervalSec: number; frameW: number; frameH: number } | null;
  media: { id: string; mediaStatus: string | null; posterUrl: string | null; proxyUrl: string | null; width: number | null; height: number | null; durationMs: number | null; sizeBytes: number };
};

export type LaunchModulePost = {
  id: string;
  assetId: string | null;
  platform: 'INSTAGRAM' | 'FACEBOOK' | 'LINKEDIN';
  type: LaunchPostType;
  role: string | null;
  publishAt: string | null;
  caption: string;
  coverUrl: string | null;
  coverSource: LaunchCoverSource | null;
  coverTs: number | null;
  coverAssetId: string | null;
  collaborators: string[];
  pin: boolean;
  carouselAssetIds: string[];
  status: LaunchPostStatus;
  permalink: string | null;
  error: string | null;
  publishedAt: string | null;
  orderIndex: number;
  /** Stories: the look set here, baked by the worker. Null = plain frame. */
  storyStyle: Partial<StoryStyle> | null;
  storyMediaUrl: string | null;
  storyMediaType: 'IMAGE' | 'VIDEO' | null;
  storyRenderError: string | null;
  /** Reels: last hook check of the caption's first line (hookFor = hash of that line). */
  hookScore?: number | null;
  hookNotes?: { fails?: string[]; rewrites?: string[] } | null;
  hookFor?: string | null;
};

export type LaunchHook = { postId: string; line: string; score: number; fails: string[]; rewrites: string[]; cached: boolean };
export type LaunchBestTimes = {
  mode: 'best' | 'default';
  account: string | null;
  results: number;
  minResults: number;
  shareOf: 'followers' | 'peak';
  heatmap: number[][];
  suggestions: { postIds: string[]; role: string; current: string | null; suggested: string; pct: number | null; reason: string; changed: boolean }[];
};

export const KG_PRESETS = ['NONE', 'WARM', 'COOL', 'FILMIC', 'MATTE', 'PUNCH', 'FADED', 'TEAL_ORANGE', 'BW', 'BW_GRIT', 'BW_NOIR', 'BW_FILM', 'SEPIA'] as const;
export const GRADE_PRESETS = [...KG_PRESETS, ...CLASSIC_LOOK_KEYS] as const;
export const STORY_MOTIONS = ['NONE', 'PUSH_IN', 'PULL_OUT', 'PAN_LEFT', 'PAN_RIGHT', 'PAN_UP', 'PAN_DOWN'] as const;
export const STORY_FONTS = ['SANS', 'SANS_MEDIUM', 'SANS_BOLD', 'SANS_ITALIC', 'SERIF'] as const;
export type StoryText = {
  text: string;
  x: number;
  y: number;
  size: number;
  font: (typeof STORY_FONTS)[number];
  color: string;
  align: 'left' | 'center' | 'right';
  background: 'NONE' | 'BOX' | 'SHADOW';
  boxColor: string;
};
/** Mirrors kilogram-app/src/lib/story-style.ts (the server validates and fills defaults). */
export type StoryStyle = {
  grade: {
    preset: (typeof GRADE_PRESETS)[number];
    lutId: string | null;
    intensity: number;
    exposure: number;
    contrast: number;
    saturation: number;
    warmth: number;
    fade: number;
    vignette: number;
    grain: number;
  };
  frame: { fit: 'FIT' | 'FILL'; focusX: number; focusY: number; zoom: number };
  motion: (typeof STORY_MOTIONS)[number];
  motionAmount: number;
  durationSec: number;
  clip: { inSec: number; outSec: number } | null;
  music: { trackId: string; startSec: number; volume: number; fadeInSec: number; fadeOutSec: number; keepOriginal: boolean; originalVolume: number } | null;
  muteOriginal: boolean;
  texts: StoryText[];
  mentions: { username: string; x: number; y: number }[];
};
export type StoryLut = { id: string; name: string; size: number; createdAt: string };

export type LaunchCredit = { id?: string; role: string; name: string; handle: string | null; orderIndex?: number };
export type LaunchCheck = { code: string; ok: boolean; message: string };

export type LaunchCampaignPerson = { name: string; role: string; handle: string | null; source: 'crew' | 'call sheet' | 'client' | 'email'; action: string };
export type LaunchCampaign = {
  angle: string;
  capture: { when: 'before' | 'shoot' | 'after'; what: string; why: string; done: boolean }[];
  posts: { slot: 'hero' | 'bts' | 'stories' | 'second wave'; idea: string }[];
  people: LaunchCampaignPerson[];
  notes: string;
  model: string | null;
};

export type LaunchModuleView = {
  /** The job's own; on a custom launch, its context job (the first source job). */
  job: { id: string; code: string; title: string };
  /** Set on a custom launch. */
  custom: { title: string; sourceJobs: { id: string; code: string; title: string }[] } | null;
  client: { id: string; name: string; instagramHandle: string | null; isAgency: boolean };
  launch: {
    id: string;
    size: 'LIGHT' | 'FULL';
    launchDate: string | null;
    timezone: string;
    agencyLiveAt: string | null;
    btsApproved: boolean;
    sourceFolder: string | null;
    planStatus: 'DRAFT' | 'APPROVED' | 'SCHEDULED' | 'LIVE' | 'DONE';
    scannedAt: string | null;
    approvedAt: string | null;
    previewUrl: string | null;
    manualSteps: Record<string, boolean>;
    campaign: LaunchCampaign | null;
    campaignAt: string | null;
    skippedAt: string | null;
    archivedAt: string | null;
    socialAccountId: string | null;
    socialAccount: { id: string; username: string; platform: string; pageId: string | null } | null;
  } | null;
  assets: LaunchModuleAsset[];
  posts: LaunchModulePost[];
  credits: LaunchCredit[];
  checks: LaunchCheck[];
};

export type LaunchApproveSummary = {
  confirmed: boolean;
  rows: { id: string; role: string | null; type: LaunchPostType; platform: string; publishAt: string | null; held: boolean; problems: string[]; willSchedule: boolean }[];
  scheduling: number;
  held: number;
  blocked: number;
};

const lm = (key: string, rest = '') =>
  key.startsWith('launch:')
    ? `/api/launches/${encodeURIComponent(key.slice('launch:'.length))}/module${rest}`
    : `/api/jobs/${encodeURIComponent(key)}/launch/module${rest}`;

export const launchModule = {
  get: (jobId: string) => apiFetch<LaunchModuleView>(lm(jobId)),
  scan: (jobId: string) => apiFetch<LaunchModuleView>(lm(jobId, '/scan'), { method: 'POST' }),
  patch: (jobId: string, body: { size?: 'LIGHT' | 'FULL'; launchDate?: string | null; timezone?: string; agencyLiveAt?: string | null; btsApproved?: boolean; socialAccountId?: string | null }) =>
    apiFetch<LaunchModuleView>(lm(jobId), { method: 'PATCH', body }),
  patchAsset: (jobId: string, assetId: string, body: { selected?: boolean; orderIndex?: number; role?: string | null }) =>
    apiFetch<LaunchModuleView>(lm(jobId, `/assets/${encodeURIComponent(assetId)}`), { method: 'PATCH', body }),
  reorderAssets: (jobId: string, ids: string[]) => apiFetch<LaunchModuleView>(lm(jobId, '/assets/reorder'), { method: 'POST', body: { ids } }),
  renderStories: (jobId: string) => apiFetch<{ queued: number }>(lm(jobId, '/stories'), { method: 'POST' }),
  renderContact: (jobId: string, assetId: string) => apiFetch<{ queued: boolean }>(lm(jobId, `/assets/${encodeURIComponent(assetId)}/contact`), { method: 'POST' }),
  renderPreview: (jobId: string) => apiFetch<{ queued: boolean }>(lm(jobId, '/preview'), { method: 'POST' }),
  plan: (jobId: string) => apiFetch<LaunchModuleView>(lm(jobId, '/plan'), { method: 'POST' }),
  patchPost: (
    jobId: string,
    postId: string,
    body: { caption?: string; publishAt?: string | null; collaborators?: string[]; pin?: boolean; cover?: { source: LaunchCoverSource; ts?: number | null; assetId?: string | null }; assetId?: string; storyStyle?: StoryStyle | null },
  ) => apiFetch<LaunchModuleView>(lm(jobId, `/posts/${encodeURIComponent(postId)}`), { method: 'PATCH', body }),
  /** A story from any launch file: a still, or a video (a 9:16 clip of up to 60s). */
  /** Stories dragged into a new order: the slots stay, the stories swap into them. */
  reorderStories: (jobId: string, ids: string[]) => apiFetch<LaunchModuleView>(lm(jobId, '/stories/reorder'), { method: 'POST', body: { ids } }),
  addStory: (jobId: string, assetId: string) => apiFetch<LaunchModuleView>(lm(jobId, '/stories/add'), { method: 'POST', body: { assetId } }),
  luts: () => apiFetch<{ luts: StoryLut[] }>('/api/launch/luts'),
  uploadLut: (file: File) => uploadBinary<{ luts: StoryLut[] }>('/api/launch/luts', file),
  deleteLut: (id: string) => apiFetch<{ luts: StoryLut[] }>(`/api/launch/luts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  cancelPost: (jobId: string, postId: string) => apiFetch<LaunchModuleView>(lm(jobId, `/posts/${encodeURIComponent(postId)}/cancel`), { method: 'POST' }),
  restorePost: (jobId: string, postId: string) => apiFetch<LaunchModuleView>(lm(jobId, `/posts/${encodeURIComponent(postId)}/restore`), { method: 'POST' }),
  setCredits: (jobId: string, credits: LaunchCredit[]) =>
    apiFetch<LaunchModuleView>(lm(jobId, '/credits'), { method: 'PUT', body: { credits: credits.map((c) => ({ role: c.role, name: c.name, handle: c.handle })) } }),
  approve: (jobId: string, confirm: boolean) => apiFetch<LaunchApproveSummary>(lm(jobId, '/approve'), { method: 'POST', body: { confirm } }),
  setManualSteps: (jobId: string, steps: Record<string, boolean>) => apiFetch<LaunchModuleView>(lm(jobId, '/manual-steps'), { method: 'PUT', body: { steps } }),
  /** Draft or redraft the campaign brief from the job's own records. Slow: a Claude call. */
  /** Owed list: not posting this one (or undo). */
  setSkipped: (jobId: string, skipped: boolean) => apiFetch<LaunchModuleView>(lm(jobId, '/skip'), { method: 'PUT', body: { skipped } }),
  draftCampaign: (jobId: string) => apiFetch<LaunchCampaign>(lm(jobId, '/campaign'), { method: 'POST' }),
  /** Claude writes the hook + story line of every reel/carousel still to go out. Credits and hashtags stay formula-made. */
  draftCaptions: (jobId: string) => apiFetch<{ written: number; view: LaunchModuleView }>(lm(jobId, '/captions'), { method: 'POST' }),
  /** Hook check: score a reel caption's first line (a draft when caption is given) and get rewrites. Haiku, cached per line. */
  hook: (jobId: string, postId: string, caption?: string) => apiFetch<LaunchHook>(lm(jobId, `/posts/${encodeURIComponent(postId)}/hook`), { method: 'POST', body: caption === undefined ? {} : { caption } }),
  bestTimes: (jobId: string) => apiFetch<LaunchBestTimes>(lm(jobId, '/best-times')),
  applyBestTimes: (jobId: string) => apiFetch<{ moved: number; bestTimes: LaunchBestTimes; view: LaunchModuleView }>(lm(jobId, '/best-times/apply'), { method: 'POST' }),
  patchCampaign: (jobId: string, body: { capture?: { index: number; done: boolean }[]; notes?: string }) => apiFetch<LaunchCampaign>(lm(jobId, '/campaign'), { method: 'PATCH', body }),
};

export type EditAgentReport = {
  resolveState?: 'running' | 'closed' | 'hung' | 'unknown';
  resolveError?: string | null;
  host?: { hostname?: string; uptimeSec?: number; diskFreeGb?: number | null; diskTotalGb?: number | null; cpuLoad?: number; memFreeGb?: number; memTotalGb?: number };
  daemonUptimeSec?: number;
  current?: { lane: string; kind: string; jobCode: string; since: string } | null;
  lastError?: { at: string; message: string } | null;
  update?: { state: string; version?: string; at: string; error?: string; reason?: string } | null;
  pollIntervalSec?: number;
  logFile?: string;
  currentProject?: string | null;
  priority?: number;
};

export type EditAgentStatus = {
  agentId: string;
  agentVersion: string;
  gitSha: string | null;
  resolveProduct: string | null;
  resolveVersion: string | null;
  capabilities: Record<string, boolean>;
  lastSeenAt: string;
  online: boolean;
  outdated: boolean;
  /** Minutes since the last heartbeat. */
  silentMin: number;
  report: EditAgentReport;
  updateRequested: boolean;
  restartRequested: boolean;
  /** One line a person can act on, or null when all is well. */
  attention: string | null;
};

/** The office robot's heartbeat (edit-agent daemon), and the two buttons it answers to. */
export const editAgent = {
  status: () => apiFetch<{ expectedVersion: string; allCurrent?: boolean; machines?: string[]; agents: EditAgentStatus[] }>('/api/agent/status'),
  updateNow: (agentId: string) => apiFetch<void>(`/api/agent/${encodeURIComponent(agentId)}/update-now`, { method: 'POST' }),
  restart: (agentId: string) => apiFetch<void>(`/api/agent/${encodeURIComponent(agentId)}/restart`, { method: 'POST' }),
};

export const launch = {
  get: (jobId: string) => apiFetch<LaunchView>(`/api/jobs/${encodeURIComponent(jobId)}/launch`),
  /** Build, or Rebuild when stale. */
  build: (jobId: string) =>
    apiFetch<{ id: string }>(`/api/jobs/${encodeURIComponent(jobId)}/launch/build`, { method: 'POST' }),
  setConsent: (jobId: string, showcaseConsent: ShowcaseConsent) =>
    apiFetch<{ showcaseConsent: ShowcaseConsent }>(`/api/jobs/${encodeURIComponent(jobId)}/launch/consent`, {
      method: 'PATCH',
      body: { showcaseConsent },
    }),
  /** Take the drafted piece live (and its staged Mux hero with it). */
  publish: (jobId: string) =>
    apiFetch<{ pieceId: string; slug: string; url: string }>(
      `/api/jobs/${encodeURIComponent(jobId)}/launch/publish`,
      { method: 'POST' },
    ),
  /** Queue the content cut for the office mini (plan stage 4). */
  cut: (jobId: string) =>
    apiFetch<{ queued: boolean; reason?: string }>(`/api/jobs/${encodeURIComponent(jobId)}/launch/cut`, { method: 'POST' }),
  /** Nick opened the project: the robot never writes to it again. */
  handover: (jobId: string) =>
    apiFetch<void>(`/api/jobs/${encodeURIComponent(jobId)}/launch/handover`, { method: 'POST' }),
  /** Queue the rollover lane: the mini proposes windows off the masters. */
  rollover: (jobId: string) =>
    apiFetch<{ queued: boolean; reason?: string }>(`/api/jobs/${encodeURIComponent(jobId)}/launch/rollover`, { method: 'POST' }),
  /** Nick's tick on the contact sheet: which windows have no text. */
  approveRollover: (jobId: string, windowIds: string[]) =>
    apiFetch<void>(`/api/jobs/${encodeURIComponent(jobId)}/launch/rollover/approve`, { method: 'POST', body: { windowIds } }),
  rolloverSheetUrl: (jobId: string) => `/api/jobs/${encodeURIComponent(jobId)}/launch/rollover/sheet`,
  /** The dashboard lane: every launch not yet done or archived, oldest delivery first, custom ones after. */
  list: () => apiFetch<LaunchRow[]>('/api/launch'),
  /** The archived ones, newest first, for restoring. */
  listArchived: () => apiFetch<LaunchRow[]>('/api/launch?archived=1'),
  /** Hide a launch from the lane and the board (or bring it back). Deletes nothing; scheduled posts still go out. */
  archive: (launchId: string, archived: boolean) =>
    apiFetch<{ launchId: string; archivedAt: string | null }>(`/api/launches/${encodeURIComponent(launchId)}/archive`, { method: 'PUT', body: { archived } }),
  /** A launch not tied to one job: footage from these jobs' Social Launch folders. */
  createCustom: (body: { title: string; sourceJobIds: string[] }) => apiFetch<{ id: string }>('/api/launches', { method: 'POST', body }),
  updateCustom: (launchId: string, body: { title?: string; sourceJobIds?: string[] }) =>
    apiFetch<LaunchModuleView>(`/api/launches/${encodeURIComponent(launchId)}`, { method: 'PATCH', body }),
  /** The left-nav Launch page: every plan + every post still to go out, across jobs. */
  board: () => apiFetch<LaunchBoard>('/api/launch/board'),
};

export type LaunchBoardCounts = { pending: number; scheduled: number; published: number; failed: number; nextAt: string | null; lastPublishedAt: string | null };
export type LaunchBoardLaunch = {
  launchId: string;
  /** Null on a custom launch: link to /launches/:launchId. */
  jobId: string | null;
  custom: boolean;
  code: string;
  title: string;
  clientName: string | null;
  launchDate: string | null;
  timezone: string;
  size: 'LIGHT' | 'FULL';
  planStatus: 'DRAFT' | 'APPROVED' | 'SCHEDULED' | 'LIVE' | 'DONE';
  btsApproved: boolean;
  account: { platform: string; username: string } | null;
  counts: LaunchBoardCounts;
};
export type LaunchBoardPost = {
  id: string;
  launchId: string;
  jobId: string | null;
  custom: boolean;
  code: string;
  title: string;
  clientName: string | null;
  timezone: string;
  role: string | null;
  type: LaunchPostType;
  platform: 'INSTAGRAM' | 'FACEBOOK';
  publishAt: string | null;
  status: LaunchPostStatus;
  permalink: string | null;
  error: string | null;
  thumbUrl: string | null;
  heldForBts: boolean;
};
export type LaunchBoardOwed = { jobId: string; code: string; title: string; clientName: string | null; deliveredAt: string | null; showcaseConsent: string; scanned: boolean };
export type LaunchBoard = {
  accounts: Array<{ id: string; platform: string; username: string; status: string; pageId: string | null }>;
  owed: LaunchBoardOwed[];
  launches: LaunchBoardLaunch[];
  posts: LaunchBoardPost[];
};

/** The client background check (clients/research.ts). */
export interface ClientResearchView {
  clientId: string;
  researchedAt: string | null;
  research: {
    sector: string; hq: string; parent: string; sizeSignal: string; audience: string; tone: string;
    channels: Array<{ channel: string; url?: string; note?: string }>;
    cadence: string; quality: string; examples: string[];
    verdict: string; position: string; intent: string;
    competitors: Array<{ name: string; content: string; edge: string }>;
    facts: Array<{ fact: string; source: string }>;
    evidence: Array<{ label: string; url: string; quote?: string }>;
    confidence: number;
    research: { status: 'RUNNING' | 'DONE' | 'FAILED'; startedAt: string; finishedAt: string | null; error: string | null; searchCalls: number };
  } | null;
}

export const clients = {
  list: () => apiFetch<Client[]>('/api/clients'),
  get: (id: string) => apiFetch<Client>(`/api/clients/${id}`),
  /** The background check: who they are, their content, their field, their intent. */
  research: (id: string) => apiFetch<ClientResearchView>(`/api/clients/${id}/research`),
  runResearch: (id: string) => apiFetch<ClientResearchView>(`/api/clients/${id}/research`, { method: 'POST' }),
  create: (input: CreateClientInput) =>
    apiFetch<{ client: Client; portalUser: unknown }>('/api/clients', {
      method: 'POST',
      body: input,
    }),
  update: (id: string, patch: Partial<Client>) =>
    apiFetch<Client>(`/api/clients/${id}`, { method: 'PATCH', body: patch }),
  /**
   * Soft-delete a client. Backend sets Client.deletedAt; jobs +
   * users + brand assets stay in place (they have their own soft-
   * delete flags). Re-creating a client with the same email
   * doesn't conflict because the email index allows duplicates
   * once the original is deletedAt-flagged. ADMIN-only.
   */
  delete: (id: string) =>
    apiFetch<void>(`/api/clients/${id}`, { method: 'DELETE' }),
  /** Every job on the client, pitches included, newest activity first. */
  jobs: (id: string) =>
    apiFetch<ClientJob[]>(`/api/clients/${encodeURIComponent(id)}/jobs`),
  /**
   * Fold this client into another: jobs, portal users, share links,
   * brand assets and the rest move across, blanks on the target fill
   * from this record, then this record is soft-deleted. ADMIN-only.
   */
  merge: (id: string, intoId: string) =>
    apiFetch<{
      targetId: string;
      moved: { jobs: number; users: number; shareTokens: number; brandAssets: number };
    }>(`/api/clients/${encodeURIComponent(id)}/merge`, {
      method: 'POST',
      body: { intoId },
    }),
  // Portal users (CLIENT-role accounts) attached to this client.
  listUsers: (id: string) =>
    apiFetch<
      Array<{
        id: string;
        email: string;
        name: string;
        createdAt: string;
        lastLoginAt: string | null;
      }>
    >(`/api/clients/${encodeURIComponent(id)}/users`),
  invite: (id: string, input: { email?: string; name?: string }) =>
    apiFetch<{ id: string; email: string; name: string; tempPassword: string }>(
      `/api/clients/${encodeURIComponent(id)}/invite`,
      { method: 'POST', body: input },
    ),
  removeUser: (id: string, userId: string) =>
    apiFetch<void>(
      `/api/clients/${encodeURIComponent(id)}/users/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    ),
  brandAssets: (id: string) =>
    apiFetch<import('./types').PortalUpload[]>(
      `/api/clients/${encodeURIComponent(id)}/brand-assets`,
    ).then((rows) =>
      // Backend returns the raw ClientAsset rows; flatten the
      // uploadedBy->name relation and uploadedAt to match PortalUpload.
      (rows as unknown as Array<{
        id: string;
        filename: string;
        mimeType: string;
        sizeBytes: number;
        sharedUrl: string | null;
        source: 'KG_UPLOAD' | 'CLIENT_UPLOAD';
        createdAt: string;
        uploadedBy: { name: string } | null;
      }>).map((u) => ({
        id: u.id,
        filename: u.filename,
        mimeType: u.mimeType,
        sizeBytes: u.sizeBytes,
        sharedUrl: u.sharedUrl,
        source: u.source,
        uploadedByName: u.uploadedBy?.name ?? null,
        uploadedAt: u.createdAt,
      })),
    ),
  deleteBrandAsset: (clientId: string, assetId: string) =>
    apiFetch<void>(
      `/api/clients/${encodeURIComponent(clientId)}/brand-assets/${encodeURIComponent(assetId)}`,
      { method: 'DELETE' },
    ),
  uploadLogo: (
    id: string,
    file: File,
    variant: 'light' | 'dark' = 'light',
  ): Promise<{ logoUrl: string | null; logoUrlDark: string | null }> =>
    uploadBinary<{ logoUrl: string | null; logoUrlDark: string | null }>(
      `/api/clients/${encodeURIComponent(id)}/logo?variant=${variant}`,
      file,
    ),
  uploadBrandAsset: async (
    id: string,
    file: File,
  ): Promise<import('./types').PortalUpload> => {
    const raw = await uploadBinary<{
      id: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      sharedUrl: string | null;
      source: 'KG_UPLOAD' | 'CLIENT_UPLOAD';
      createdAt: string;
    }>(`/api/clients/${encodeURIComponent(id)}/brand-assets/upload`, file);
    return {
      id: raw.id,
      filename: raw.filename,
      mimeType: raw.mimeType,
      sizeBytes: raw.sizeBytes,
      sharedUrl: raw.sharedUrl,
      source: raw.source,
      uploadedByName: null,
      uploadedAt: raw.createdAt,
    };
  },
  withStats: () =>
    apiFetch<import('./types').ClientWithStats[]>('/api/clients/with-stats'),
  stats: (id: string) =>
    apiFetch<import('./types').ClientStatsDetail>(
      `/api/clients/${encodeURIComponent(id)}/stats`,
    ),
  candidates: {
    list: () =>
      apiFetch<import('./types').ClientCandidate[]>('/api/clients/candidates'),
    discover: () =>
      apiFetch<import('./types').ClientCandidate[]>(
        '/api/clients/candidates/discover',
        { method: 'POST', body: {} },
      ),
    promote: (id: string) =>
      apiFetch<import('./types').Client>(
        `/api/clients/candidates/${encodeURIComponent(id)}/promote`,
        { method: 'POST', body: {} },
      ),
    dismiss: (id: string) =>
      apiFetch<void>(
        `/api/clients/candidates/${encodeURIComponent(id)}/dismiss`,
        { method: 'POST', body: {} },
      ),
  },
};

export const contractors = {
  list: () =>
    apiFetch<import('./types').Contractor[]>('/api/contractors'),
  get: (id: string) =>
    apiFetch<import('./types').Contractor>(
      `/api/contractors/${encodeURIComponent(id)}`,
    ),
  create: (input: {
    name: string;
    email?: string | null;
    phone?: string | null;
    role?: string | null;
    notes?: string | null;
    dayRate?: number | null;
  }) =>
    apiFetch<import('./types').Contractor>('/api/contractors', {
      method: 'POST',
      body: input,
    }),
  update: (
    id: string,
    patch: Partial<{
      name: string;
      email: string | null;
      phone: string | null;
      role: string | null;
      notes: string | null;
      dayRate: number | null;
    }>,
  ) =>
    apiFetch<import('./types').Contractor>(
      `/api/contractors/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  remove: (id: string) =>
    apiFetch<void>(`/api/contractors/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  candidates: {
    list: () =>
      apiFetch<import('./types').ContractorCandidate[]>(
        '/api/contractors/candidates',
      ),
    discover: (input?: { windowDays?: number; skipClassification?: boolean }) =>
      apiFetch<import('./types').DiscoverContractorsResult>(
        '/api/contractors/candidates/discover',
        { method: 'POST', body: input ?? {} },
      ),
    promote: (id: string) =>
      apiFetch<import('./types').Contractor>(
        `/api/contractors/candidates/${encodeURIComponent(id)}/promote`,
        { method: 'POST', body: {} },
      ),
    dismiss: (id: string) =>
      apiFetch<void>(
        `/api/contractors/candidates/${encodeURIComponent(id)}/dismiss`,
        { method: 'POST', body: {} },
      ),
  },
};

export interface FiledMeetingNote {
  assetId: string;
  filename: string;
  dropboxPath: string;
  digested: boolean;
  reason: string | null;
  briefVersion: number | null;
}

export interface WaitingMeetingNote {
  assetId: string;
  filename: string;
  dropboxPath: string;
  filedAt: string;
  /** A recording (a Zoom call's audio): Transcribe first. */
  audio: boolean;
  transcribing: boolean;
  transcribeError: string | null;
}

export const briefs = {
  listForJob: (jobId: string) =>
    apiFetch<Brief[]>(`/api/jobs/${jobId}/briefs`),
  get: (id: string) => apiFetch<Brief>(`/api/briefs/${id}`),
  create: (jobId: string, input: BriefInput) =>
    apiFetch<Brief>(`/api/jobs/${jobId}/briefs`, {
      method: 'POST',
      body: input,
    }),
  submit: (id: string) =>
    apiFetch<Brief>(`/api/briefs/${id}/submit`, { method: 'POST' }),
  reparse: (id: string) =>
    apiFetch<Brief>(`/api/briefs/${id}/parse`, { method: 'POST' }),
  /** Pull a brief from a Figma file link. Creates a new Brief version
   *  with the parsed fields. Reads only files the KG Figma account can
   *  access. */
  fromFigma: (jobId: string, url: string) =>
    apiFetch<Brief>(`/api/jobs/${jobId}/briefs/from-figma`, {
      method: 'POST',
      body: { url },
    }),
  /** Pull a brief from an uploaded PDF or Word doc (also the path for an
   *  exported Figma/Canva PDF). Creates a new Brief version. When the
   *  source is a PDF it's also kept + shown at the top of the brief tab. */
  fromDocument: (jobId: string, file: File) =>
    uploadBinary<Brief>(
      `/api/jobs/${encodeURIComponent(jobId)}/briefs/from-document`,
      file,
    ),
  /** Meeting notes (a Meet / Zoom transcript: txt, md, docx, pdf, vtt,
   *  srt) from the Brief tab. Saved in the private meeting-notes root
   *  under the client, filed on the job, digested into a Brief version
   *  tagged meeting-notes (so concept 1 is the meeting's idea). */
  fromMeetingNotes: (jobId: string, file: File) =>
    uploadBinary<FiledMeetingNote>(
      `/api/jobs/${encodeURIComponent(jobId)}/briefs/from-meeting-notes`,
      file,
    ),
  /** Meeting notes the Dropbox sweep filed on the job that no brief was
   *  made from yet (the sweep files only; the digest is on request). */
  waitingMeetingNotes: (jobId: string) =>
    apiFetch<WaitingMeetingNote[]>(`/api/jobs/${encodeURIComponent(jobId)}/meeting-notes/waiting`),
  /** "Transcribe" on a waiting recording (AssemblyAI). Returns at once;
   *  its transcript joins the waiting list when done. */
  transcribeMeetingNote: (jobId: string, assetId: string) =>
    apiFetch<{ started: boolean; reason: string | null }>(
      `/api/jobs/${encodeURIComponent(jobId)}/meeting-notes/${encodeURIComponent(assetId)}/transcribe`,
      { method: 'POST', body: {} },
    ),
  /** "Add to brief" on a waiting note: KGAI digests it into a Brief version. */
  digestMeetingNote: (jobId: string, assetId: string) =>
    apiFetch<Omit<FiledMeetingNote, 'assetId' | 'filename' | 'dropboxPath'>>(
      `/api/jobs/${encodeURIComponent(jobId)}/meeting-notes/${encodeURIComponent(assetId)}/digest`,
      { method: 'POST', body: {} },
    ),
  /** The same, pasted. */
  fromMeetingNotesText: (jobId: string, text: string, title?: string) =>
    apiFetch<FiledMeetingNote>(`/api/jobs/${encodeURIComponent(jobId)}/briefs/from-meeting-notes/text`, {
      method: 'POST',
      body: { text, title },
    }),
  /** Upload the client's designed brief PDF purely to display it at the
   *  top of the brief tab (no parsing). Returns the new shared link. */
  uploadBriefPdf: (jobId: string, file: File) =>
    uploadBinary<{ briefPdfUrl: string | null }>(
      `/api/jobs/${encodeURIComponent(jobId)}/brief-pdf`,
      file,
    ),
  /** Pin a PDF already in the job's files as the displayed brief PDF
   *  (pointer only, no copy). */
  setBriefPdfFromAsset: (jobId: string, assetId: string) =>
    apiFetch<{ briefPdfUrl: string | null }>(
      `/api/jobs/${encodeURIComponent(jobId)}/brief-pdf/from-asset`,
      { method: 'POST', body: { assetId } },
    ),
  /** Unpin the displayed brief PDF (the file itself stays in the job). */
  clearBriefPdf: (jobId: string) =>
    apiFetch<void>(`/api/jobs/${encodeURIComponent(jobId)}/brief-pdf`, {
      method: 'DELETE',
    }),
  /** Authed blob of the pinned brief PDF for the inline preview iframe.
   *  Bearer token in the header + one refresh retry (iframes can't send
   *  Authorization headers, so the caller wraps this in createObjectURL). */
  briefPdfBlob: async (jobId: string): Promise<Blob> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/brief-pdf/file`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** Digest the job's own emails (bodies, signatures/quoted chains
   *  stripped) into a new Brief version via Claude. */
  fromEmails: (jobId: string) =>
    apiFetch<Brief>(`/api/jobs/${encodeURIComponent(jobId)}/briefs/from-emails`, {
      method: 'POST',
    }),
  /** How many emails are linked to the job (labels the pull button). */
  emailCount: (jobId: string) =>
    apiFetch<{ count: number }>(
      `/api/jobs/${encodeURIComponent(jobId)}/briefs/email-count`,
    ).then((r) => r.count),
  updateFields: (
    id: string,
    patch: {
      body?: string | null;
      audience?: string | null;
      tone?: string | null;
      summary?: string | null;
      objective?: string | null;
      keyMessage?: string | null;
      callToAction?: string | null;
      styleNotes?: string | null;
      mustHaves?: string[];
      niceToHaves?: string[];
      avoid?: string[];
      videoReferences?: string[];
      voScript?: Array<{ vo: string; visual: string }>;
    },
  ) =>
    apiFetch<Brief>(`/api/briefs/${id}/fields`, {
      method: 'PATCH',
      body: patch,
    }),
};

export type BriefFieldsPatch = Parameters<typeof briefs.updateFields>[1];

// =====================================================================
// CREATIVE TREATMENTS
// =====================================================================

export type CreativeTreatment = {
  id: string;
  jobId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  boldness: string | null;
  aesthetic: string | null;
  pacing: string | null;
  narrative: string | null;
  references: string | null;
  otherDirection: string | null;
  bigIdea: string | null;
  concept: string | null;
  visualDirection: string | null;
  storyBeats: string | null;
  castingWardrobe: string | null;
  locations: string | null;
  soundMusic: string | null;
  whyThisWorks: string | null;
  model: string | null;
  /** 'KGAI' (written from the brief and the knobs) or 'CLIENT' (the
   *  client supplied the creative; filed in their words). */
  source: 'KGAI' | 'CLIENT';
  /** The project file the client's creative was filed from, if any. */
  sourceAssetId: string | null;
};

export type CreativeKnobs = {
  boldness?: string | null;
  aesthetic?: string | null;
  pacing?: string | null;
  narrative?: string | null;
  references?: string | null;
  otherDirection?: string | null;
};

export type CreativePatch = CreativeKnobs & {
  bigIdea?: string | null;
  concept?: string | null;
  visualDirection?: string | null;
  storyBeats?: string | null;
  castingWardrobe?: string | null;
  locations?: string | null;
  soundMusic?: string | null;
  whyThisWorks?: string | null;
};

export const creative = {
  /** Every version of the creative treatment for a job, newest first. */
  listForJob: (jobId: string) =>
    apiFetch<CreativeTreatment[]>(`/api/jobs/${jobId}/creative`),
  /** Generate a new version using Opus + the latest brief. */
  generate: (jobId: string, knobs: CreativeKnobs) =>
    apiFetch<CreativeTreatment>(`/api/jobs/${jobId}/creative/generate`, {
      method: 'POST',
      body: knobs,
    }),
  /** Inline-edit one or more fields on a treatment row. */
  /** The client supplied the creative: file a project file or pasted text as the next version, in their words. */
  fromClient: (jobId: string, input: { assetId?: string; text?: string }) =>
    apiFetch<CreativeTreatment>(`/api/jobs/${jobId}/creative/from-client`, { method: 'POST', body: input }),
  update: (id: string, patch: CreativePatch) =>
    apiFetch<CreativeTreatment>(`/api/creative/${id}`, {
      method: 'PATCH',
      body: patch,
    }),
  /** Delete one treatment version. */
  deleteVersion: (id: string) =>
    apiFetch<void>(`/api/creative/${id}`, { method: 'DELETE' }),
};

/** Base medium plus the cinematic realism flavours (ARRI / 35mm film /
 *  Cineon print emulation). Mirrors STORYBOARD_STYLE_KEYS in the backend. */
export type StoryboardRenderStyle =
  | 'illustrated'
  | 'cinematic'
  | 'cinematic-arri'
  | 'cinematic-film'
  | 'cinematic-cineon';
/** The Higgsfield model a board is written for and painted on
 *  (storyboard-look.ts). Old boards carry 'higgsfield_canvas',
 *  'higgsfield' or 'luma' in renderEngine; those are display-only now. */
export type StoryboardRenderEngine = StoryboardImageModel;

/** One frame's shot card: the fields the video models read (storyboard/spec.ts). */
export type StoryboardFrameSpec = {
  subject: string;
  beats: Array<{ at: number; what: string }>;
  moment: string;
  actionClient: string;
  setting: string;
  shotSize: 'wide' | 'mid-wide' | 'mid' | 'close' | 'insert';
  height: 'low' | 'eye' | 'high' | 'overhead';
  angle: 'front' | 'three-quarter' | 'side' | 'behind';
  move: { type: 'locked' | 'handheld' | 'dolly' | 'slider' | 'crane' | 'gimbal'; direction: string; speed: 'slow' | 'medium' | 'fast' };
  lens: number | null;
  lighting: string;
  eyeline: string;
  vo: string;
  /** On-camera dialogue (2026-10-27, synced from the Figma board). */
  dialogue?: Array<{ speaker: string; line: string }>;
  sound: string;
  source: 'STILL' | 'PLATE' | 'CLAY';
  face: 'KEEP' | 'CHARACTER';
  model: 'seedance-2.5' | 'kling-mc' | 'kling-4' | 'genjutsu' | null;
  tcIn: number | null;
  duration: number | null;
};
export type StoryboardView = 'dop' | 'client';

export type StoryboardFrame = {
  id: string;
  order: number;
  shotTitle: string | null;
  shotPrompt: string;
  /** The shot card (storyboard/spec.ts, 2026-10-26): the structured spec
   *  in Seedance order plus source/face/model. Null on older boards. */
  spec: StoryboardFrameSpec | null;
  /** Scene elements — filled by the brief digest, null on planned boards. */
  location: string | null;
  locationStyling: string | null;
  poseRef: string | null;
  cameraMove: string | null;
  lighting: string | null;
  talent: string[] | null;
  /** Brief pages this scene came from, e.g. "4-6". Digest boards only. */
  pageRange: string | null;
  /** Dropbox folder the digest filed this scene's brief images into. */
  dropboxFolder: string | null;
  imageCount: number;
};

/** A recurring on-screen talent digested from the brief — build this as
 *  a character node in Canvas first, then reuse it across scenes. */
export type StoryboardCharacter = {
  id: string;
  order: number;
  name: string;
  description: string;
  multiAnglePrompt: string;
  dropboxFolder: string | null;
  imageCount: number;
};

export type Storyboard = {
  id: string;
  jobId: string;
  version: number;
  renderStyle: string;
  renderEngine: string;
  /** Model + camera package (null on boards planned before 2026-10-09). */
  look: Partial<StoryboardLook> | null;
  styleBible: string | null;
  planModel: string | null;
  /** Dropbox path of the brief PDF this board was digested from (digest boards only). */
  digestSource: string | null;
  /** Root of the per-scene Dropbox image pack (digest boards only). */
  canvasPackFolder: string | null;
  frames: StoryboardFrame[];
  characters: StoryboardCharacter[];
  createdAt: string;
  updatedAt: string;
};

export const storyboard = {
  /** Every storyboard version for a job, newest first (with shots). */
  listForJob: (jobId: string) =>
    apiFetch<Storyboard[]>(`/api/jobs/${jobId}/storyboard`),
  /** Plan a new board from the latest treatment. Returns it ready. */
  generate: (
    jobId: string,
    opts?: {
      frameCount?: number;
      renderStyle?: StoryboardRenderStyle;
      look?: StoryboardLook;
    },
  ) =>
    apiFetch<Storyboard>(`/api/jobs/${jobId}/storyboard/generate`, {
      method: 'POST',
      body: opts ?? {},
    }),
  /** Digest the job's brief PDF into a Canvas-ready board (scenes with
   *  element fields + character sheets + a per-scene Dropbox image pack). */
  digestBrief: (
    jobId: string,
    opts?: {
      renderStyle?: StoryboardRenderStyle;
      look?: StoryboardLook;
    },
  ) =>
    apiFetch<Storyboard>(`/api/jobs/${jobId}/storyboard/digest`, {
      method: 'POST',
      body: opts ?? {},
    }),
  /** Hand-edit a planned shot's title or prompt (any board). */
  updateFrame: (frameId: string, patch: { shotTitle?: string | null; shotPrompt?: string; spec?: Partial<StoryboardFrameSpec> }) =>
    apiFetch<StoryboardFrame>(`/api/storyboard/frames/${frameId}`, { method: 'PATCH', body: patch }),
  /** Which view of the frame cards the deck prints: dop (every field) or client (image, action, VO). */
  updateView: (storyboardId: string, view: StoryboardView) =>
    apiFetch<{ id: string; view: StoryboardView }>(`/api/storyboard/${storyboardId}/view`, { method: 'PATCH', body: { view } }),
  /** Change a board's look (model + camera package). No re-plan: the next Paint uses it. */
  updateLook: (storyboardId: string, look: Partial<StoryboardLook>) =>
    apiFetch<Storyboard>(`/api/storyboard/${storyboardId}/look`, { method: 'PATCH', body: { look } }),
  /** The storyboard models and whether the app's Higgsfield key can call each
   *  (null = could not tell), with Higgsfield's reason when it refused. */
  models: () =>
    apiFetch<{ models: Array<{ id: StoryboardImageModel; label: string; available: boolean | null; reason?: string | null }> }>('/api/storyboard-models'),
  /** Hand-edit the board's style bible (the master prompt every frame is painted from). */
  updateStyleBible: (storyboardId: string, styleBible: string) =>
    apiFetch<Storyboard>(`/api/storyboard/${storyboardId}/style-bible`, { method: 'PATCH', body: { styleBible } }),
  /** Edit a digested scene's elements; the prompt recomposes server-side. */
  updateFrameElements: (
    frameId: string,
    patch: Partial<{
      shotTitle: string | null;
      location: string | null;
      locationStyling: string | null;
      poseRef: string | null;
      cameraMove: string | null;
      lighting: string | null;
      talent: string[];
    }>,
  ) =>
    apiFetch<StoryboardFrame>(`/api/storyboard/frames/${frameId}/elements`, {
      method: 'PATCH',
      body: patch,
    }),
};

// =====================================================================
// KG Virtual Production — candidate locations + client sign-off.
// =====================================================================

/** A candidate shooting location attached to a job for client sign-off. */
export type VirtualProductionLocation = {
  id: string;
  jobId: string;
  label: string | null;
  note: string | null;
  imageUrl: string;
  imageDropboxPath: string | null;
  /** Single full-screen cutaway companion (replaces the old gallery). */
  cutawayImageUrl: string | null;
  cutawayDropboxPath: string | null;
  /** @deprecated legacy multi-image gallery — emptied by the wipe. */
  extraImageUrls: string[];
  extraImageDropboxPaths: string[];
  status: string; // 'PENDING' | 'APPROVED'
  approvedAt: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
};

export const virtualProductionLocations = {
  /** Candidate locations attached to a job, newest first. Works for the
   *  internal tab and the portal client (withShareToken is a no-op when no
   *  share token is active). */
  listForJob: (jobId: string) =>
    apiFetch<VirtualProductionLocation[]>(
      withShareToken(`/api/jobs/${jobId}/virtual-production/locations`),
    ),
  /** Sweep the job's Dropbox Locations folder and import any new renders as
   *  pending locations. Internal-only. Returns the full list. */
  syncFromDropbox: (jobId: string) =>
    apiFetch<VirtualProductionLocation[]>(
      `/api/jobs/${jobId}/virtual-production/locations/sync`,
      { method: 'POST' },
    ),
  /** Attach a location hero image (optionally with a label). */
  upload: (jobId: string, file: File, label?: string) =>
    uploadBinary<VirtualProductionLocation>(
      `/api/jobs/${jobId}/virtual-production/locations${label ? `?label=${encodeURIComponent(label)}` : ''}`,
      file,
    ),
  /** Set (or replace) a location's single full-screen cutaway image. */
  setCutaway: (jobId: string, locationId: string, file: File) =>
    uploadBinary<VirtualProductionLocation>(
      `/api/jobs/${jobId}/virtual-production/locations/${locationId}/images`,
      file,
    ),
  /** KG only: remove just the cutaway image (+ its Dropbox file). */
  deleteCutaway: (jobId: string, locationId: string) =>
    apiFetch<VirtualProductionLocation>(
      `/api/jobs/${jobId}/virtual-production/locations/${locationId}/cutaway`,
      { method: 'DELETE' },
    ),
  /** KG only: one-time wipe of all legacy gallery cutaways (Dropbox + DB). */
  wipeLegacyCutaways: () =>
    apiFetch<{ locations: number; filesDeleted: number }>(
      `/api/virtual-production/wipe-legacy-cutaways`,
      { method: 'POST' },
    ),
  /** Read a reference image with Haiku vision; returns a "look" descriptor to
   *  fold into the prompt builder. Internal-only, budget-guarded. */
  analyzeReference: (file: File) =>
    uploadBinary<{ look: string | null }>(
      `/api/virtual-production/analyze-reference`,
      file,
    ),
  /** "Fuel" rough dressing notes into vivid set-dressing detail (Haiku).
   *  Internal-only, budget-guarded. */
  enhanceDetails: (subject: string, details: string) =>
    apiFetch<{ details: string | null }>(
      `/api/virtual-production/enhance-details`,
      { method: 'POST', body: { subject, details } },
    ),
  /** Edit a location's label / note. */
  update: (
    jobId: string,
    locationId: string,
    patch: { label?: string; note?: string },
  ) =>
    apiFetch<VirtualProductionLocation>(
      `/api/jobs/${jobId}/virtual-production/locations/${locationId}`,
      { method: 'PATCH', body: patch },
    ),
  /** KG only: delete a location + its hero & cutaway images in Dropbox. */
  remove: (jobId: string, locationId: string) =>
    apiFetch<void>(
      `/api/jobs/${jobId}/virtual-production/locations/${locationId}`,
      { method: 'DELETE' },
    ),
  /** Approve / un-approve a location. The client sign-off (also used by the
   *  internal tab). Carries the share token through when in the portal. */
  setApproval: (jobId: string, locationId: string, approved: boolean) =>
    apiFetch<VirtualProductionLocation>(
      withShareToken(
        `/api/jobs/${jobId}/virtual-production/locations/${locationId}/approval`,
      ),
      { method: 'POST', body: { approved } },
    ),
};

export type TreatmentColumn = { label: string; subhead: string; body: string };
export type TreatmentSlide = {
  section: string;
  kicker: string;
  headline: string;
  emphasis: string;
  body: string;
  columns: TreatmentColumn[];
  frameOrders: number[];
};
export type TreatmentContent = {
  headline: string;
  emphasis: string;
  logline: string;
  slides: TreatmentSlide[];
  /** Every storyboard frame order available in Dropbox for this deck.
   *  The renderer distributes these across visual slides when Opus didn't
   *  tag slides with their own frameOrders. Optional for older decks. */
  availableFrameOrders?: number[];
};
export type TreatmentDeck = {
  id: string;
  version: number;
  content: TreatmentContent;
  model: string | null;
  frameCount: number;
  createdAt: string;
};

export const treatment = {
  /** Every treatment-deck version for a job, newest first. */
  listForJob: (jobId: string) =>
    apiFetch<TreatmentDeck[]>(`/api/jobs/${jobId}/treatment`),
  /** Compose a new deck from brief + creative + storyboard + frames. */
  generate: (jobId: string) =>
    apiFetch<TreatmentDeck>(`/api/jobs/${jobId}/treatment/generate`, {
      method: 'POST',
    }),
  /** Delete one treatment-deck version. */
  deleteVersion: (id: string) =>
    apiFetch<void>(`/api/treatment/${id}`, { method: 'DELETE' }),
  /** Shot orders with a rendered frame in Dropbox right now (swept live),
   *  so any deck shows current frames without regenerating. */
  frameOrders: (jobId: string) =>
    apiFetch<{ orders: number[] }>(
      `/api/jobs/${encodeURIComponent(jobId)}/treatment/frames`,
    ).then((r) => r.orders),
  /** Authed blob of a storyboard frame (by shot order) for an <img src>.
   *  Bearer token in the header + one refresh retry, like the playback
   *  thumbnail. Caller wraps the blob in createObjectURL. */
  frameBlob: async (jobId: string, order: number): Promise<Blob> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/treatment/frames/${order}`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
};

export type MusicSweepResult = {
  scannedFolder: string | null;
  matched: Array<{ trackId: string; title: string; filename: string; path: string }>;
  autoCreated: Array<{ trackId: string; title: string; filename: string; path: string }>;
};

export type GuideTrackHit = {
  trackId: number;
  title: string;
  artist: string;
  album: string | null;
  previewUrl: string;
  artworkUrl: string | null;
  durationMs: number | null;
  url: string | null;
};

export const music = {
  list: (jobId: string) =>
    apiFetch<MusicTrack[]>(`/api/jobs/${jobId}/music`),
  triggerSearch: (jobId: string) =>
    apiFetch<{ queueJobId: string; briefId: string }>(
      `/api/jobs/${jobId}/music/search`,
      { method: 'POST', body: {} },
    ),
  select: (id: string) =>
    apiFetch<MusicTrack>(`/api/music/${id}/select`, { method: 'POST' }),
  reject: (id: string) =>
    apiFetch<MusicTrack>(`/api/music/${id}/reject`, { method: 'POST' }),
  sweep: (jobId: string) =>
    apiFetch<MusicSweepResult>(`/api/jobs/${jobId}/music/sweep`, {
      method: 'POST',
      body: {},
    }),
  assignFile: (trackId: string, path: string) =>
    apiFetch<MusicTrack>(`/api/music/${trackId}/assign-file`, {
      method: 'POST',
      body: { path },
    }),
  /** Whether the server has a Suno key; gates the Generate button. */
  sunoStatus: () =>
    apiFetch<{ configured: boolean; model: string }>('/api/music/suno/status'),
  generate: (jobId: string, body: { direction?: string; stylePrompt?: string; title?: string }) =>
    apiFetch<{ generation: MusicGeneration; queueJobId: string }>(
      `/api/jobs/${jobId}/music/generate`,
      { method: 'POST', body },
    ),
  generations: (jobId: string) =>
    apiFetch<MusicGeneration[]>(`/api/jobs/${jobId}/music/generations`),
  /** Musical directions on the job, newest first. */
  rounds: (jobId: string) => apiFetch<MusicRound[]>(`/api/jobs/${jobId}/music/rounds`),
  /** Apple's 30s song previews: search, then save one into 3-AUDIO/Music
   *  as a cutting guide (the licensed song goes on in Instagram). */
  guideSearch: (jobId: string, term: string) =>
    apiFetch<{ results: GuideTrackHit[] }>(
      `/api/jobs/${jobId}/music/guide/search?term=${encodeURIComponent(term)}`,
    ),
  guideSave: (jobId: string, hit: GuideTrackHit) =>
    apiFetch<{ track: MusicTrack }>(`/api/jobs/${jobId}/music/guide`, {
      method: 'POST',
      body: { trackId: hit.trackId, title: hit.title, artist: hit.artist, previewUrl: hit.previewUrl, url: hit.url ?? undefined },
    }),
  moveToRound: (trackId: string, roundId: string | null) =>
    apiFetch<MusicTrack>(`/api/music/${trackId}/round`, {
      method: 'PATCH',
      body: { roundId },
    }),
  renameRound: (roundId: string, label: string) =>
    apiFetch<{ id: string; label: string }>(`/api/music/rounds/${roundId}`, {
      method: 'PATCH',
      body: { label },
    }),
};

export const feedback = {
  list: (jobId: string) =>
    apiFetch<FeedbackComment[]>(`/api/jobs/${jobId}/feedback`),
  setStatus: (id: string, status: CommentStatus) =>
    apiFetch<FeedbackComment>(`/api/feedback/${id}/status`, {
      method: 'POST',
      body: { status },
    }),
  reclassify: (id: string) =>
    apiFetch<FeedbackComment>(`/api/feedback/${id}/classify`, {
      method: 'POST',
    }),
};

export const tasks = {
  /** Cross-job overview for the global Tasks page. */
  overview: () => apiFetch<TasksOverview>('/api/tasks/overview'),
  list: (jobId: string) => apiFetch<TaskBoard>(`/api/jobs/${jobId}/tasks`),
  create: (
    jobId: string,
    input: {
      title: string;
      detail?: string | null;
      priority?: TaskPriority;
      dueDate?: string | null;
      assignedToId?: string | null;
    },
  ) =>
    apiFetch<Task>(`/api/jobs/${jobId}/tasks`, { method: 'POST', body: input }),
  update: (
    id: string,
    patch: Partial<{
      title: string;
      detail: string | null;
      status: TaskStatus;
      priority: TaskPriority;
      dueDate: string | null;
      assignedToId: string | null;
      orderIndex: number;
      estimateMinutes: number | null;
    }>,
  ) => apiFetch<Task>(`/api/tasks/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) =>
    apiFetch<void>(`/api/tasks/${id}`, { method: 'DELETE' }),
  acceptSuggestion: (jobId: string, commentId: string, assignedToId?: string | null) =>
    apiFetch<Task>(`/api/jobs/${jobId}/tasks/suggestions/${commentId}/accept`, {
      method: 'POST',
      body: { assignedToId: assignedToId ?? null },
    }),
  dismissSuggestion: (jobId: string, commentId: string) =>
    apiFetch<void>(`/api/jobs/${jobId}/tasks/suggestions/${commentId}/dismiss`, {
      method: 'POST',
    }),
  /** "Keep" a sweep-suggested task: clear the "looks done?" nudge
   *  without completing it. */
  dismissSuggestedComplete: (id: string) =>
    apiFetch<Task>(`/api/tasks/${id}/dismiss-suggested-complete`, {
      method: 'POST',
    }),
  /** Restore an auto-archived (stale, unallocated) task to its column. */
  restore: (id: string) =>
    apiFetch<Task>(`/api/tasks/${id}/restore`, { method: 'POST' }),
  // Task comments — internal team back-and-forth on a single task.
  listComments: (taskId: string) =>
    apiFetch<TaskComment[]>(`/api/tasks/${taskId}/comments`),
  addComment: (taskId: string, body: string, taggedUserId?: string | null) =>
    apiFetch<TaskComment>(`/api/tasks/${taskId}/comments`, {
      method: 'POST',
      body: { body, taggedUserId: taggedUserId ?? null },
    }),
  // Task Centre week view: one person's Mon-Fri in 15-minute blocks,
  // mirrored to their own Google Calendar.
  week: (userId: string, weekStart: string) =>
    apiFetch<TaskWeek>(
      `/api/tasks/week?userId=${encodeURIComponent(userId)}&weekStart=${encodeURIComponent(weekStart)}`,
    ),
  schedule: (
    id: string,
    input: { start: string; end: string; userId?: string | null; blockType?: TaskBlockType | null },
  ) =>
    apiFetch<{ task: WeekTask; google: TaskCalendarSync }>(`/api/tasks/${id}/schedule`, {
      method: 'POST',
      body: input,
    }),
  unschedule: (id: string) =>
    apiFetch<{ task: WeekTask; google: TaskCalendarSync }>(`/api/tasks/${id}/unschedule`, {
      method: 'POST',
    }),
  /** Drop a task into the first free stretch of its estimate on a day. */
  scheduleNext: (id: string, input: { day?: string | null; userId?: string | null }) =>
    apiFetch<{ task: WeekTask; google: TaskCalendarSync }>(`/api/tasks/${id}/schedule-next`, {
      method: 'POST',
      body: input,
    }),
  /** Decide an unfinished block: next day, back to the list, done, drop. */
  replan: (id: string, action: 'next' | 'list' | 'done' | 'drop') =>
    apiFetch<WeekTask>(`/api/tasks/${id}/replan`, { method: 'POST', body: { action } }),
  /** Write a person's draft blocks to their Google Calendar: one day
   *  (days 1) or the week (Monday + days 5). */
  commit: (userId: string, from: string, days: number) =>
    apiFetch<CommitPlanResult>('/api/tasks/week/commit', { method: 'POST', body: { userId, from, days } }),
  setBlockType: (id: string, blockType: TaskBlockType | null) =>
    apiFetch<WeekTask>(`/api/tasks/${id}/block-type`, { method: 'PUT', body: { blockType } }),
  dayReview: (userId: string, date: string) =>
    apiFetch<DayReview>(
      `/api/tasks/day-review?userId=${encodeURIComponent(userId)}&date=${encodeURIComponent(date)}`,
    ),
  /** A business-development backlog task, on the BD KG job. */
  createBd: (input: {
    title: string;
    detail?: string | null;
    priority?: TaskPriority;
    dueDate?: string | null;
    assignedToId?: string | null;
    estimateMinutes?: number | null;
  }) => apiFetch<WeekTask>('/api/tasks/bd', { method: 'POST', body: input }),
  // Internal ("KG") job tasks — same shape, no suggestions.
  listForInternal: (internalJobId: string) =>
    apiFetch<TaskBoard>(`/api/internal-jobs/${internalJobId}/tasks`),
  createForInternal: (
    internalJobId: string,
    input: {
      title: string;
      detail?: string | null;
      priority?: TaskPriority;
      dueDate?: string | null;
      assignedToId?: string | null;
    },
  ) =>
    apiFetch<Task>(`/api/internal-jobs/${internalJobId}/tasks`, {
      method: 'POST',
      body: input,
    }),
};

export const internalJobs = {
  list: () => apiFetch<InternalJob[]>('/api/internal-jobs'),
  create: (input: { title: string; brief?: string | null }) =>
    apiFetch<InternalJob>('/api/internal-jobs', { method: 'POST', body: input }),
  update: (
    id: string,
    patch: Partial<{ title: string; brief: string | null; status: InternalJobStatus }>,
  ) => apiFetch<InternalJob>(`/api/internal-jobs/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) =>
    apiFetch<void>(`/api/internal-jobs/${id}`, { method: 'DELETE' }),
};

export const coach = {
  overview: () => apiFetch<CoachOverview>('/api/coach'),
  plan: (force = false) => apiFetch<{ weekOf: string; skipped?: string }>('/api/coach/plan', { method: 'POST', body: { force } }),
  review: (force = false) => apiFetch<{ weekOf: string; skipped?: string }>('/api/coach/review', { method: 'POST', body: { force } }),
  saveSettings: (input: Partial<CoachSettings>) => apiFetch<CoachSettings>('/api/coach/settings', { method: 'PUT', body: input }),
};

export const quietDays = {
  overview: () => apiFetch<QuietDaysOverview>('/api/quiet-days'),
  pulse: () => apiFetch<QuietPulse>('/api/quiet-days/pulse'),
  create: (input: {
    title: string;
    detail?: string | null;
    category?: string;
    effort?: QuietIdeaEffort;
    impact?: QuietIdeaImpact;
    steps?: string[];
    suggestedForId?: string | null;
  }) => apiFetch<QuietIdea>('/api/quiet-days/ideas', { method: 'POST', body: input }),
  update: (
    id: string,
    patch: Partial<{
      title: string;
      detail: string | null;
      category: string;
      effort: QuietIdeaEffort;
      impact: QuietIdeaImpact;
      steps: string[];
      suggestedForId: string | null;
      status: 'BACKLOG' | 'DISMISSED' | 'DONE';
      orderIndex: number;
    }>,
  ) => apiFetch<QuietIdea>(`/api/quiet-days/ideas/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => apiFetch<void>(`/api/quiet-days/ideas/${id}`, { method: 'DELETE' }),
  /** Promote to a KG job with the steps as tasks. */
  start: (id: string, input: { assigneeId?: string | null; dueDate?: string | null }) =>
    apiFetch<QuietIdea>(`/api/quiet-days/ideas/${id}/start`, { method: 'POST', body: input }),
  /** KGAI proposes fresh ideas from the CRM. Returns the new SUGGESTED rows. */
  suggest: () => apiFetch<QuietIdea[]>('/api/quiet-days/suggest', { method: 'POST' }),
};

export const personalTasks = {
  list: () => apiFetch<PersonalTask[]>('/api/personal-tasks'),
  create: (input: {
    title: string;
    detail?: string | null;
    priority?: TaskPriority;
    dueDate?: string | null;
    category?: string | null;
  }) => apiFetch<PersonalTask>('/api/personal-tasks', { method: 'POST', body: input }),
  update: (
    id: string,
    patch: Partial<{
      title: string;
      detail: string | null;
      status: TaskStatus;
      priority: TaskPriority;
      dueDate: string | null;
      category: string | null;
      orderIndex: number;
    }>,
  ) => apiFetch<PersonalTask>(`/api/personal-tasks/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => apiFetch<void>(`/api/personal-tasks/${id}`, { method: 'DELETE' }),
};

/** Studio docs: SOPs / how-tos. Staff read, owner writes (backend gates). */
export const studioDocs = {
  list: (q?: string) =>
    apiFetch<StudioDocSummary[]>(
      q && q.trim() ? `/api/studio-docs?q=${encodeURIComponent(q.trim())}` : '/api/studio-docs',
    ),
  get: (idOrSlug: string) => apiFetch<StudioDoc>(`/api/studio-docs/${encodeURIComponent(idOrSlug)}`),
  create: (input: { title: string; category?: string; body?: string; tags?: string[] }) =>
    apiFetch<StudioDoc>('/api/studio-docs', { method: 'POST', body: input }),
  update: (
    idOrSlug: string,
    patch: Partial<{ title: string; category: string; body: string; tags: string[]; sortOrder: number }>,
  ) =>
    apiFetch<StudioDoc>(`/api/studio-docs/${encodeURIComponent(idOrSlug)}`, {
      method: 'PATCH',
      body: patch,
    }),
  archive: (idOrSlug: string) =>
    apiFetch<void>(`/api/studio-docs/${encodeURIComponent(idOrSlug)}`, { method: 'DELETE' }),
};

// --- Pitches (new-business workspace; team-visible, no money) ---

export type PitchDecision = 'PENDING' | 'PURSUE' | 'PASS' | 'SNOOZE';
export type PitchOutcome = 'OPEN' | 'SENT' | 'WON' | 'LOST';
export interface PitchEvidence { label: string; url: string; quote?: string }
export interface PitchContentAudit {
  /** Who holds the brand today, and how Kilogram gets in. */
  agencyStatus?: 'none' | 'in-house' | 'small agency' | 'major agency' | null;
  agencyNote?: string | null;
  wayIn?: 'front door' | 'back door' | 'none' | null;
  channels?: Array<{ channel: string; url?: string; note?: string }>;
  cadence?: string;
  quality?: string;
  examples?: string[];
  verdict?: string;
  /** From the prospect research pass (hand-added prospects run it on add). */
  audience?: string;
  tone?: string;
  parent?: string | null;
  competitors?: Array<{ name: string; content: string; edge?: string }>;
  facts?: Array<{ fact: string; source: string }>;
  research?: { status: 'RUNNING' | 'DONE' | 'FAILED'; startedAt: string; finishedAt: string | null; error: string | null; searchCalls: number };
}
export interface PitchCandidate {
  id: string;
  runId: string;
  weekOf: string;
  rank: number;
  company: string;
  website: string | null;
  sector: string | null;
  hq: string | null;
  sizeSignal: string | null;
  budgetSignal: string | null;
  contentAudit: PitchContentAudit;
  angle: string;
  whyNow: string | null;
  evidence: PitchEvidence[];
  confidence: number;
  decision: PitchDecision;
  decisionNote: string | null;
  decidedAt: string | null;
  jobId: string | null;
  createdAt: string;
}
export interface PitchScoutRun {
  id: string;
  weekOf: string;
  status: 'RUNNING' | 'DONE' | 'FAILED';
  error: string | null;
  triggeredById: string | null;
  discoveryModel: string | null;
  judgeModel: string | null;
  inputTokens: number;
  outputTokens: number;
  searchCalls: number;
  candidateCount: number;
  startedAt: string;
  finishedAt: string | null;
  candidates?: PitchCandidate[];
}
export interface PitchProfile {
  regions: string[];
  sectors: string[];
  sizeFloor: string;
  exclusions: string[];
  steering: string;
}
export interface PitchWeek {
  weekOf: string;
  run: PitchScoutRun | null;
  candidates: PitchCandidate[];
  snoozed: PitchCandidate[];
  profile: PitchProfile;
}
export interface PitchActive {
  id: string;
  code: string;
  title: string;
  client: { id: string; name: string; company: string | null };
  outcome: PitchOutcome;
  wonJobId: string | null;
  stage: 'NEW' | 'BRIEF' | 'CONCEPTS' | 'CREATIVE' | 'STORYBOARD' | 'DECK' | 'OUTREACH';
  figmaBoardUrl: string | null;
  deckVersion?: number;
  dropboxFolder: string | null;
  lastActivityAt: string;
  createdAt: string;
  counts: { briefs: number; creativeTreatments: number; storyboards: number };
}
export interface PitchPursueResult { jobId: string; code: string | null; warnings: string[] }
export interface PitchOutcomeResult { outcome: PitchOutcome; wonJobId: string | null; warnings: string[] }
export interface PitchScoutSummary { runId: string; weekOf: string; status: 'DONE' | 'FAILED' | 'SKIPPED'; candidates: number; error?: string }

export interface PitchRelatedPiece {
  slug: string; title: string; subtitle: string | null; sector: string | null;
  category: string; tags: string[]; posterUrl: string | null; posterPath: string | null; url: string;
  /** Nick's strength score, 1-5, null when unrated. Suggestions come strongest first. */
  rating: number | null;
}
export interface PitchRelatedWork {
  sector: FolioSector | null;
  suggestions: PitchRelatedPiece[];
  chosen: PitchRelatedPiece[];
  sectors: readonly FolioSector[];
  /** What Kilogram has already made for this prospect (folio match). */
  prior: Array<{ slug: string; title: string; subtitle: string | null; year: number | null }>;
  /** Real client jobs on the books for a client of that name. */
  priorJobs: number;
  returning: boolean;
  /** The Selected work heading: flexes on prior history (spec 2026-10-07 #9). */
  heading: string;
  /** The one credibility line the Selected work page carries. */
  credibility: string;
  /** The four pieces that best fit the prospect's world, for when nothing is picked. */
  curated: PitchRelatedPiece[];
}

export interface PitchConcept {
  title: string; logline: string; idea: string; format: string; tone: string; whyItWorks: string; watchOut: string;
  /** Who wrote it: KGAI (default), the team (added from chat), or the
   *  client's own idea from the meeting. */
  source?: 'KGAI' | 'TEAM' | 'MEETING';
}
export type PitchStyle = 'photoreal' | 'gouache';
export interface PitchConcepts {
  concepts: PitchConcept[];
  generatedAt: string | null;
  model: string | null;
  chosenIndex: number | null;
  /** The frame look the storyboard is generated in (photoreal default). */
  style: PitchStyle;
  /** Set once the treatment + shot list have been planned from the chosen concept. */
  planned: { shotCount: number | null; plannedAt: string | null } | null;
}

/** The storyboard plan of a single-concept pitch (see pitches/plan.ts). */
export interface PitchPoolFrame {
  name: string; path: string; title: string; url: string | null; modifiedAt: string | null;
  selected: boolean; deckOrder: number | null;
  /** What should change on this frame, saved on the pitch. */
  note?: string | null;
  /** The last redo of this frame (an edit from the note, in the frame's own style). */
  redo?: PitchFrameRedo | null;
  /** The frame this one was redone from, and the note that did it. */
  redoneFrom?: { name: string; note: string } | null;
}
export interface PitchFrameRedo { status: 'RUNNING' | 'DONE' | 'FAILED'; note: string; startedAt: string; finishedAt?: string | null; newName?: string | null; error?: string | null }
export interface PitchShot { n: number; frameId?: string; title: string; description: string; cast: string[]; spec?: StoryboardFrameSpec | null }
/** A deck frame that is not a shot: the hybrid hero, the AI locations, the TV room (house.ts). */
export type PitchHouseKind = 'hybrid-hero' | 'ai-location' | 'tv-room';
/** The server paint run (paint.ts): one frame per shot and the deck assets, painted in Higgsfield and filed in the bag. */
export interface PitchPaintItem {
  id: string; kind: 'shot' | PitchHouseKind; n: number; title: string;
  status: 'queued' | 'painting' | 'filed' | 'failed'; name: string | null; error: string | null;
  /** The model that painted it (storyboard-look.ts id). */
  model?: string | null;
  /** The taste guard: 1-5 for stock-photo posing, the tell, whether it was painted again. */
  taste?: { score: number; reason: string; rerolled: boolean } | null;
}
export interface PitchPaint {
  status: 'RUNNING' | 'DONE' | 'FAILED'; what: 'all' | 'shots' | 'assets';
  done: number; total: number; message: string; items: PitchPaintItem[];
  startedAt: string; finishedAt: string | null; error: string | null; ticked: number; note: string | null;
}
export interface PitchHouseFrame {
  kind: PitchHouseKind; label: string; slot: string; page: string; required: boolean; wanted: number;
  shots: Array<{ title: string; prompt: string }>;
  files: Array<{ name: string; path: string; url: string | null; modifiedAt: string | null }>;
  state: 'ready' | 'short' | 'missing';
}
/** Page 2, "Your world", drafted from the research (world.ts). */
export interface PitchWorldStat { value: string; label: string; source: string }
/** The words the fourteen-page template still needs, drafted by Opus (house-deck.ts). */
export interface PitchHouseCopy {
  cover: { kicker: string; dateLine: string };
  world: { statement: string };
  direct: { body: string };
  idea: { logline: string; body: string; shoot: string; yields: string };
  vo: { label: 'Voiceover' | 'Sound design' | 'The words'; line1: string; line2: string; line3: string; endline: string; note: string };
  storyboard: { beats: Array<{ time: string; label: string }>; captions: string[] };
  closing: { endline: string; months: string[] };
  social: { lede: string; handle: string };
  tv: { lede: string };
  hybrid: { subhead: string };
  locations: { heading: string; intro: string; captions: string[] };
  picks: { cover: number; idea: number; vo: number; closing: number; story: number; texture: number };
  writtenAt: string;
  handEdited: boolean;
}
/** The house deck as the CRM draws it: role -> value plus readiness. */
export interface PitchHouseDeck {
  jobId: string;
  prospect: string;
  concept: string | null;
  copy: PitchHouseCopy | null;
  values: { text: Record<string, string>; images: Record<string, string | null>; hidden: string[] };
  readiness: { ok: boolean; missing: string[]; warnings: string[] };
  frames: Array<{ n: number; name: string; title: string; url: string | null }>;
}
/** The Investment page and the close, over the house copy (deck-words.ts). */
export interface PitchDeckWords {
  investment: { heading: string; intro: string; drivers: Array<{ title: string; body: string }>; nextStep: string };
  close: { label: string; nextStep: string };
  handEdited: boolean;
}
export interface PitchWorldPage {
  ownWords: string; tension: string; currentWork: string; opening: string;
  stats: PitchWorldStat[];
  writtenAt: string; handEdited: boolean;
}
export interface PitchPlan {
  campaign: false;
  jobId: string; code: string; prospect: string;
  concept: { index: number; title: string; logline: string } | null;
  concepts: string[];
  style: PitchStyle;
  treatment: {
    id: string; version: number; bigIdea: string | null; concept: string | null; visualDirection: string | null; storyBeats: string | null;
    castingWardrobe: string | null; locations: string | null; soundMusic: string | null; whyThisWorks: string | null; handEdited: boolean;
  } | null;
  shots: string[];
  frames: PitchShot[];
  styleBible: string | null;
  /** The board's look: the model Paint uses and the camera package. */
  look?: StoryboardLook;
  storyboardId?: string | null;
  /** Which view of the frame cards the deck prints. */
  view?: StoryboardView;
  cast: PitchCastMember[];
  framePool: { folder: string | null; castFolder: string | null };
  pool: PitchPoolFrame[];
  missing: string[];
  house: PitchHouseFrame[];
  houseCheck: { missing: string[]; short: string[]; ok: boolean };
  world: PitchWorldPage | null;
  words: PitchDeckWords;
  figmaBoardUrl: string | null;
  deckVersion: number;
  plan: { conceptIndex: number; conceptTitle: string; style: PitchStyle; shotCount: number; notes: string | null; plannedAt: string } | null;
  note?: string;
}
export interface PitchPlanInput { concept: number | string; style?: PitchStyle; shotCount?: number; notes?: string; regenerate?: boolean }

export type OutreachRole = 'decision-maker' | 'influencer' | 'champion' | 'gatekeeper';
export type OutreachStatus = 'NEW' | 'CONTACTED' | 'REPLIED' | 'MEETING' | 'NO';
export type OutreachChannel = 'linkedin-connect' | 'linkedin-message' | 'email' | 'call' | 'in-person' | 'send-deck';
export interface OutreachContact {
  id: string; name: string; title: string; role: OutreachRole; whyThem: string;
  linkedinUrl: string | null; email: string | null; evidence: Array<{ label: string; url: string }>;
  confidence: number; status: OutreachStatus; note: string;
  lastTouchAt?: string | null;
}
export interface OutreachStep {
  id: string; order: number; day: number; channel: OutreachChannel; contactId: string | null;
  purpose: string; subject: string | null; message: string; done: boolean; doneAt: string | null; draftId: string | null;
  logged?: boolean;
}
export interface PitchOutreach {
  status: 'RUNNING' | 'DONE' | 'FAILED';
  startedAt: string; generatedAt: string | null; error: string | null; model: string | null; searchCalls: number;
  company: string; summary: string; hooks: string[]; contacts: OutreachContact[]; plan: OutreachStep[];
  researchNotes?: string;
  /** Who the plan is written for (signs the messages, owns the Gmail drafts). Absent on older plans = Nick. */
  sender?: { userId: string; name: string; email: string } | null;
}
export type PitchOutreachUpdate = Partial<Pick<PitchOutreach, 'summary' | 'hooks' | 'contacts' | 'plan'>>;
/** Warm intros on a pitch: the mutual-contact scan logged before the first touch. */
export type WarmIntroSource = 'gmail' | 'crm' | 'apollo' | 'linkedin' | 'personal' | 'other';
export type WarmIntroStrength = 'strong' | 'medium' | 'weak';
export type WarmIntroStatus = 'to-ask' | 'asked' | 'intro-made' | 'no-go';
export interface WarmIntro {
  id: string; connector: string; howWeKnow: string; target: string | null; targetRole: string | null;
  source: WarmIntroSource; strength: WarmIntroStrength; status: WarmIntroStatus; notes: string; updatedAt: string;
}
export interface PitchWarmIntros { scannedAt: string | null; noWarmPath: boolean; sourcesChecked: WarmIntroSource[]; intros: WarmIntro[] }
export interface PitchAdvance {
  status: 'RUNNING' | 'DONE' | 'FAILED';
  step: 'prompts' | 'frames' | 'copy' | 'deck' | 'done';
  done: number; total: number; message: string;
  startedAt: string; finishedAt: string | null; error: string | null;
  frames: Array<{ order: number; title: string; path: string; concept?: number }>;
  deckPath: string | null; deckVersion: number | null;
  campaign?: { concepts: number } | null;
}

/** Campaign pitches (several concepts in one deck). Mirrors campaign.ts. */
export interface PitchCampaignAudio { kind: 'vo' | 'sound'; script: string; voice: string; notes: string }
export interface PitchCampaignPoolFrame { name: string; path: string; title: string; url: string | null; modifiedAt: string | null; selected: boolean; deckOrder: number | null }
export interface PitchCampaignConcept {
  id: string; title: string; logline: string; idea: string; whyItHits: string; followsOn: string; shoot: string; format: string;
  audio: PitchCampaignAudio; shots: string[]; selected: string[];
  folder: string; pool: PitchCampaignPoolFrame[]; missing: string[];
}
export interface PitchCampaignShared { background: string; market: string; directArgument: string; thread: string; products: string[]; tagline: string }
export interface PitchCampaignConceptCopy {
  eyebrow: string; title: string; logline: string; body: string;
  audioLabel: string; audioIntro: string; audioLines: string[]; audioCredit: string;
  beats: Array<{ time: string; label: string }>; frameCaptions: string[]; heroFrame: number; shoot: string; yields: string;
}
export interface PitchCampaignCopy {
  cover: { title: string; kicker: string; dateLine: string };
  world: { statement: string; stats: Array<{ value: string; label: string }> };
  history: { statement: string; points: Array<{ label: string; body: string }> };
  thread: { statement: string };
  concepts: PitchCampaignConceptCopy[];
  compare: { statement: string; rows: Array<{ label: string; values: string[] }> };
  direct: { statement: string; points: Array<{ label: string; body: string }> };
  process: { statement: string; steps: Array<{ when: string; title: string; body: string }>; footer: string };
  investment: { statement: string; options: Array<{ label: string; price: string; body: string }>; footer: string };
  next: { statement: string; steps: Array<{ label: string; body: string }> };
  closing: { statement: string };
  coverConcept: number;
}
export interface PitchCampaign {
  version: 1; title: string; concepts: PitchCampaignConcept[]; shared: PitchCampaignShared;
  copy: PitchCampaignCopy | null; design: { pages: Array<{ kind: string }>; designedAt: string; rationale: string } | null;
  updatedAt: string; bag: string | null; figmaBoardUrl?: string | null;
}
export type PitchCampaignConceptInput = Partial<Omit<PitchCampaignConcept, 'folder' | 'pool' | 'missing' | 'audio'>> & { title: string; logline: string; idea: string; whyItHits: string; followsOn: string; shoot: string; format: string; audio: Partial<PitchCampaignAudio> };

export interface PitchBagFrame {
  order: number; title: string; path: string; name: string; url: string | null; modifiedAt: string | null;
}
export type PitchSheetLook = 'painted' | 'photoreal';
export interface PitchCastSheet { name: string; path: string; url: string | null; look: PitchSheetLook | null; modifiedAt: string | null }
export interface PitchCastMember {
  /** StoryboardCharacter id (the cast record). */
  id?: string;
  name: string; description: string; sheetPrompt: string;
  /** The current sheet: the newest in the pitch's style. */
  sheet: { name: string; path: string; url: string | null; look?: PitchSheetLook | null } | null;
  /** Every sheet filed for this character, newest first. */
  sheets?: PitchCastSheet[];
}
/** The cast record of a pitch: its characters and every sheet filed for them. */
export interface PitchCastRecord { style: PitchSheetLook; castFolder: string | null; cast: PitchCastMember[]; filed?: { character: string; look: PitchSheetLook; name: string; path: string } }
export interface PitchBagFrames { frames: PitchBagFrame[]; expected: number; folder: string | null; cast: PitchCastMember[]; castFolder: string | null }

export interface FigmaPushState {
  pending: { jobId: string; code: string; title: string; requestedAt: string; by: string } | null;
  templateUrl: string | null;
}

export interface PitchOutreachDeck {
  url: string; path: string; filename: string; version: number;
  sizeBytes: number | null; attachable: boolean;
}

export type PitchStageId = 'BRIEF' | 'CONCEPTS' | 'CREATIVE' | 'STORYBOARD' | 'DECK' | 'OUTREACH';

/** Every line of the pitch deck, as the server stores it (mirrors DeckCopy). */
export type PitchDeckCopy = {
  cover: { title: string; kicker: string; dateLine: string };
  world: { statement: string; stats: Array<{ value: string; label: string }> };
  insight: { statement: string };
  idea: { title: string; logline: string; body: string; heroCaption: string; smallCaptions: string[] };
  feel: { statement: string; points: Array<{ label: string; body: string }> };
  film: { statement: string; beats: Array<{ time: string; label: string }>; frameCaptions: string[] };
  closing: { statement: string };
  deliverables: { statement: string; items: Array<{ value: string; name: string; body: string }>; footer: string };
  process: { statement: string; steps: Array<{ when: string; title: string; body: string }>; footer: string };
  why: { statement: string; points: Array<{ label: string; body: string }> };
  competition?: { statement: string; points: Array<{ label: string; body: string }> };
  investment: { statement: string; primary: { price: string; body: string }; secondary: { price: string; body: string }; replaces: string };
  next: { statement: string; steps: Array<{ label: string; body: string }>; imageCaption: string };
  coverFrameIndex: number;
};
export type PitchDeckCopyPatch = { [K in keyof PitchDeckCopy]?: PitchDeckCopy[K] extends object ? Partial<PitchDeckCopy[K]> : PitchDeckCopy[K] };
export type PitchNextAction =
  | 'write-brief' | 'draft-concepts' | 'choose-concept' | 'write-creative' | 'plan-storyboard'
  | 'paint-frames' | 'tick-frames' | 'build-deck' | 'figma-deck' | 'find-people' | 'work-sequence' | 'call-it'
  // Pitch Outreach, warm before cold: run the warm intro scan, then ask the connector.
  | 'warm-intros' | 'ask-intro'
  // Client job only: paste the Figma board link; every stage that is on is done.
  | 'link-board' | 'handoff';
/** The creative pathway of a job or a pitch (GET /jobs/:id/pathway). One
 *  shape for both: a pitch walks six stages ending in Outreach; a client
 *  job walks Brief and Creative, with Storyboard and Deck switched on by
 *  button ('off' until then). The pitch-only fields are null on a job. */
export interface PitchPipeline {
  /** Client job: a brief is on the job (the Treatment is written from it). */
  briefReady?: boolean;
  kind: 'CLIENT' | 'PITCH';
  current: PitchStageId;
  /** Set when the pitch is a campaign: several concepts in one deck. */
  campaign: { concepts: number; ticked: number[] } | null;
  outcome: PitchOutcome;
  /** The client job made from this pitch ("Make it a job"), if any: a link only. */
  job: { id: string; code: string } | null;
  stages: Array<{ id: PitchStageId; label: string; state: 'done' | 'current' | 'todo' | 'off'; detail: string; optional: boolean }>;
  next: { stage: PitchStageId; action: PitchNextAction; label: string; hint: string };
  /** Set once the pitch is being run again as a new version. */
  round: PitchRound | null;
  /** Every stage that is on is done. */
  complete: boolean;
  /** Client job with Deck on: what the client has and whether a newer
   *  deck is waiting to go. Null on a pitch or with Deck off. */
  deck: { version: number; figma: boolean; share: TreatmentShare | null; sendable: boolean; newer: boolean } | null;
}
/** What of the Treatment has been sent to the client, and when. */
export interface TreatmentShare { sentAt: string; version: number; pdfAssetId: string | null; pdfUrl: string | null; figmaUrl: string | null }
export type Pathway = PitchPipeline;
export type PathwayStageId = PitchStageId;
export type PathwayAction = PitchNextAction;
export interface PitchRound {
  n: number;
  from: PitchStageId;
  at: string;
  steer: string | null;
  history: Array<{ n: number; startedAt: string | null; endedAt: string; from: PitchStageId | null; concept: string | null; deckVersion: number; outcome: string }>;
}
export interface PitchNewRoundResult { n: number; from: PitchStageId; shelvedFrames: number; shelvedCast: number; shelvedAssets: number; warnings: string[]; pipeline: PitchPipeline }

export const pitches = {
  /** Run the pitch again as a new version from a stage; earlier versions stay on file. */
  newRound: (jobId: string, from: PitchStageId, steer?: string) =>
    apiFetch<PitchNewRoundResult>(`/api/pitches/${jobId}/round`, { method: 'POST', body: { from, steer: steer?.trim() || undefined } }),
  pipeline: (jobId: string) => apiFetch<PitchPipeline>(`/api/pitches/${jobId}/pipeline`),
  advance: (jobId: string) => apiFetch<PitchAdvance | null>(`/api/pitches/${jobId}/advance`),
  outreachDeck: (jobId: string) => apiFetch<PitchOutreachDeck | null>(`/api/pitches/${jobId}/outreach/deck`),
  startAdvance: (jobId: string, mode: 'bag' | 'reuse' | 'rewrite' | 'redesign' | 'repainted' | 'words' | 'rewords' = 'bag') =>
    apiFetch<PitchAdvance>(`/api/pitches/${jobId}/advance${mode === 'reuse' ? '?reuseFrames=true' : mode === 'rewrite' ? '?reuseFrames=true&rewriteCopy=true' : mode === 'redesign' ? '?reuseFrames=true&redesign=true' : mode === 'repainted' ? '?keepWords=true' : mode === 'words' ? '?wordsOnly=true&keepWords=true' : mode === 'rewords' ? '?wordsOnly=true&rewriteCopy=true' : ''}`, { method: 'POST' }),
  campaign: (jobId: string) => apiFetch<PitchCampaign | null>(`/api/pitches/${jobId}/campaign`),
  updateCampaign: (jobId: string, input: { title?: string; concepts?: PitchCampaignConceptInput[]; shared?: Partial<PitchCampaignShared> }) =>
    apiFetch<PitchCampaign>(`/api/pitches/${jobId}/campaign`, { method: 'PUT', body: input }),
  /** Narrow the campaign to one concept: it then runs the classic Creative → Storyboard flow. */
  focusCampaignConcept: (jobId: string, concept: string) =>
    apiFetch<{ chosenIndex: number; title: string }>(`/api/pitches/${jobId}/campaign/focus`, { method: 'PUT', body: { concept } }),
  selectCampaignFrames: (jobId: string, concept: string, names: string[]) =>
    apiFetch<PitchCampaign>(`/api/pitches/${jobId}/campaign/select`, { method: 'PUT', body: { concept, names } }),
  campaignCopy: (jobId: string) => apiFetch<{ copy: PitchCampaignCopy | null; version: number; designedAt: string | null }>(`/api/pitches/${jobId}/campaign/copy`),
  updateCampaignCopy: (jobId: string, patch: Record<string, unknown>) =>
    apiFetch<{ copy: PitchCampaignCopy }>(`/api/pitches/${jobId}/campaign/copy`, { method: 'PUT', body: patch }),
  deckPdf: (jobId: string) => apiFetch<{ url: string; version: number; path: string; design: { rationale: string; pages: number; designedAt: string } | null }>(`/api/pitches/${jobId}/deck-pdf`),
  /** Export the deck PDF from the pitch's Figma board as the next deck version. */
  deckFromFigma: (jobId: string) => apiFetch<{ version: number; path: string; pages: number; figmaPage: string; frames: string[] }>(`/api/pitches/${jobId}/deck-from-figma`, { method: 'POST' }),
  /** The deck's pages rendered by Figma (numbered frames, viewing size), for the Deck stage. */
  figmaPages: (jobId: string, fresh = false) => apiFetch<FigmaDeckPages>(`/api/pitches/${jobId}/figma-pages${fresh ? '?fresh=1' : ''}`),
  figmaPush: () => apiFetch<FigmaPushState>('/api/pitches/figma-push'),
  requestFigmaPush: (jobId: string) => apiFetch<FigmaPushState>(`/api/pitches/${jobId}/figma-push`, { method: 'POST' }),
  clearFigmaPush: () => apiFetch<FigmaPushState>('/api/pitches/figma-push', { method: 'DELETE' }),
  setFigmaTemplate: (url: string | null) => apiFetch<FigmaPushState>('/api/pitches/figma-push/template', { method: 'PUT', body: { url } }),
  bagFrames: (jobId: string) => apiFetch<PitchBagFrames>(`/api/pitches/${jobId}/frames`),
  /** The deck's words (null until a deck has been built) and hand edits to them. */
  deckCopy: (jobId: string) => apiFetch<{ copy: PitchDeckCopy | null; version: number; designedAt: string | null }>(`/api/pitches/${jobId}/deck-copy`),
  updateDeckCopy: (jobId: string, patch: PitchDeckCopyPatch) =>
    apiFetch<{ copy: PitchDeckCopy }>(`/api/pitches/${jobId}/deck-copy`, { method: 'PUT', body: patch }),
  /** The deck PDF as a blob (an <iframe> can't send a bearer token). */
  deckPdfBlob: async (jobId: string): Promise<Blob> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(`/api/pitches/${encodeURIComponent(jobId)}/deck-pdf/file`, { headers });
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** The paint brief as text (cast sheets, then every frame with its locked characters). */
  paintBrief: async (jobId: string): Promise<string> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(`/api/pitches/${encodeURIComponent(jobId)}/paint-brief`, { headers });
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.text();
  },
  outreach: (jobId: string) => apiFetch<PitchOutreach | null>(`/api/pitches/${jobId}/outreach`),
  warmIntros: (jobId: string) => apiFetch<PitchWarmIntros | null>(`/api/pitches/${jobId}/warm-intros`),
  generateOutreach: (jobId: string, reuseNotes = false) =>
    apiFetch<PitchOutreach>(`/api/pitches/${jobId}/outreach/generate${reuseNotes ? '?reuseNotes=true' : ''}`, { method: 'POST' }),
  updateOutreach: (jobId: string, input: PitchOutreachUpdate) =>
    apiFetch<PitchOutreach>(`/api/pitches/${jobId}/outreach`, { method: 'PUT', body: input }),
  draftOutreachStep: (jobId: string, stepId: string) =>
    apiFetch<{ draftId: string; to: string; subject: string; mailbox: string | null; mailboxIsSender: boolean }>(`/api/pitches/${jobId}/outreach/draft`, { method: 'POST', body: { stepId } }),
  concepts: (jobId: string) => apiFetch<PitchConcepts>(`/api/pitches/${jobId}/concepts`),
  generateConcepts: (jobId: string) => apiFetch<PitchConcepts>(`/api/pitches/${jobId}/concepts/generate`, { method: 'POST' }),
  /** Choose a concept and write the creative from it (no shot list): a client job whose Storyboard stage is off. */
  chooseConcept: (jobId: string, index: number, opts: { notes?: string; style?: PitchStyle } = {}) =>
    apiFetch<{ chosenIndex: number; treatmentId: string | null }>(`/api/pitches/${jobId}/concepts/choose`, { method: 'PUT', body: { index, ...opts } }),
  /** The plan of a single-concept pitch: chosen concept, treatment, shots, cast, frame pool with ticks. */
  plan: (jobId: string) => apiFetch<PitchPlan>(`/api/pitches/${jobId}/plan`),
  /** Choose a concept AND plan from it: treatment + numbered shot list + frame pool (about a minute). */
  planStoryboard: (jobId: string, input: PitchPlanInput) =>
    apiFetch<PitchPlan>(`/api/pitches/${jobId}/plan`, { method: 'POST', body: input }),
  setPitchStyle: (jobId: string, style: PitchStyle) =>
    apiFetch<{ style: PitchStyle }>(`/api/pitches/${jobId}/plan/style`, { method: 'PUT', body: { style } }),
  /** The cast record: every character with every sheet filed for them. */
  cast: (jobId: string) => apiFetch<PitchCastRecord>(`/api/pitches/${jobId}/cast`),
  /** Change a cast member's description or sheet prompt (by id or name). */
  updateCast: (jobId: string, character: string, input: { description?: string; sheetPrompt?: string }) =>
    apiFetch<PitchCastRecord>(`/api/pitches/${jobId}/cast/${encodeURIComponent(character)}`, { method: 'PATCH', body: input }),
  /** File a sheet from a public image URL (a Higgsfield result) as the next version. */
  fileCastSheetUrl: (jobId: string, character: string, url: string, look?: PitchSheetLook) =>
    apiFetch<PitchCastRecord>(`/api/pitches/${jobId}/cast/${encodeURIComponent(character)}/sheet`, { method: 'POST', body: { url, look } }),
  /** Upload a sheet image as the next version for a cast member. */
  uploadCastSheet: (jobId: string, character: string, file: File, look: PitchSheetLook) =>
    uploadBinary<PitchCastRecord>(`/api/pitches/${jobId}/cast/${encodeURIComponent(character)}/sheet/upload`, file, { 'X-Sheet-Look': look }),
  /** Save what should change on one pool frame (an empty note clears it). */
  saveFrameNote: (jobId: string, name: string, note: string) =>
    apiFetch<{ name: string; note: string | null }>(`/api/pitches/${jobId}/plan/note`, { method: 'PUT', body: { name, note } }),
  /** Redo one pool frame from its note, in the frame's own style (about a minute). */
  redoFrame: (jobId: string, name: string, note: string) =>
    apiFetch<{ name: string; redo: PitchFrameRedo }>(`/api/pitches/${jobId}/plan/redo`, { method: 'POST', body: { name, note } }),
  /** Tick frames in the single-concept pool for the deck, in deck order. */
  selectPoolFrames: (jobId: string, names: string[]) =>
    apiFetch<PitchPlan>(`/api/pitches/${jobId}/plan/select`, { method: 'PUT', body: { names } }),
  /** Page 2, "Your world": read, draft from the research, edit by hand. */
  world: (jobId: string) => apiFetch<{ world: PitchWorldPage | null }>(`/api/pitches/${jobId}/world`),
  writeWorld: (jobId: string, regenerate = false) =>
    apiFetch<{ world: PitchWorldPage }>(`/api/pitches/${jobId}/world`, { method: 'POST', body: { regenerate } }),
  updateWorld: (jobId: string, patch: Partial<Pick<PitchWorldPage, 'ownWords' | 'tension' | 'currentWork' | 'opening' | 'stats'>>) =>
    apiFetch<{ world: PitchWorldPage }>(`/api/pitches/${jobId}/world`, { method: 'PUT', body: patch }),
  /** Paint the frames (paint.ts): POST starts, GET is polled. */
  paint: (jobId: string) => apiFetch<{ paint: PitchPaint | null }>(`/api/pitches/${jobId}/paint`),
  startPaint: (jobId: string, body: { what?: 'all' | 'shots' | 'assets'; only?: string[] } = {}) =>
    apiFetch<{ paint: PitchPaint }>(`/api/pitches/${jobId}/paint`, { method: 'POST', body }),
  /** The house deck drawn in the CRM (Deck stage Preview): values by template role + readiness. */
  houseDeck: (jobId: string) => apiFetch<PitchHouseDeck>(`/api/pitches/${jobId}/house-deck`),
  writeHouseCopy: (jobId: string, regenerate = false) =>
    apiFetch<{ copy: PitchHouseCopy }>(`/api/pitches/${jobId}/house-copy`, { method: 'POST', body: { regenerate } }),
  updateHouseCopy: (jobId: string, patch: Record<string, unknown>) =>
    apiFetch<{ copy: PitchHouseCopy }>(`/api/pitches/${jobId}/house-copy`, { method: 'PUT', body: patch }),
  /** The Investment page and the close; a line that reads like money is refused. */
  updateWords: (jobId: string, patch: { investment?: Partial<PitchDeckWords['investment']>; close?: Partial<PitchDeckWords['close']> }) =>
    apiFetch<{ words: PitchDeckWords }>(`/api/pitches/${jobId}/words`, { method: 'PUT', body: patch }),
  relatedWork: (jobId: string) => apiFetch<PitchRelatedWork>(`/api/pitches/${jobId}/related-work`),
  setRelatedWork: (jobId: string, input: { sector?: FolioSector | null; slugs?: string[] }) =>
    apiFetch<PitchRelatedWork>(`/api/pitches/${jobId}/related-work`, { method: 'PUT', body: input }),
  week: (weekOf?: string) =>
    apiFetch<PitchWeek>(`/api/pitches/week${weekOf ? `?weekOf=${encodeURIComponent(weekOf)}` : ''}`),
  history: () => apiFetch<PitchScoutRun[]>('/api/pitches/history'),
  active: () => apiFetch<PitchActive[]>('/api/pitches/active'),
  saveProfile: (profile: PitchProfile) =>
    apiFetch<PitchProfile>('/api/pitches/profile', { method: 'PUT', body: profile }),
  runScout: (force = false) =>
    apiFetch<PitchScoutSummary>(`/api/pitches/scout/run${force ? '?force=true' : ''}`, { method: 'POST' }),
  addProspect: (input: { company: string; website?: string; sector?: string; angle?: string; notes?: string }) =>
    apiFetch<PitchCandidate>('/api/pitches/candidates/manual', { method: 'POST', body: input }),
  pursue: (candidateId: string, force = false) =>
    apiFetch<PitchPursueResult>(`/api/pitches/candidates/${candidateId}/decide`, {
      method: 'POST', body: force ? { decision: 'PURSUE', force: true } : { decision: 'PURSUE' },
    }),
  /** Research one prospect (starts it; the card polls the week). */
  researchProspect: (candidateId: string) =>
    apiFetch<{ candidateId: string }>(`/api/pitches/candidates/${candidateId}/research`, { method: 'POST' }),
  decide: (candidateId: string, decision: 'PASS' | 'SNOOZE', note?: string) =>
    apiFetch<PitchCandidate>(`/api/pitches/candidates/${candidateId}/decide`, {
      method: 'POST', body: { decision, note },
    }),
  outcome: (jobId: string, outcome: PitchOutcome, note?: string) =>
    apiFetch<PitchOutcomeResult>(`/api/pitches/${jobId}/outcome`, { method: 'POST', body: { outcome, note } }),
  /** Later, on purpose: mint a client job for this pitch and link the two. Nothing is copied. */
  makeJob: (jobId: string, title?: string) =>
    apiFetch<{ jobId: string; jobCode: string; created: boolean }>(`/api/pitches/${jobId}/make-job`, { method: 'POST', body: title ? { title } : {} }),
};

/** The job's final presentation board in Figma (see modules/jobs/figma-board.ts). */
export type FigmaDeckPage = { id: string; name: string; order: number; image: string | null; url: string };
export type FigmaDeckPages = { fileKey: string; page: string; pages: FigmaDeckPage[]; renderedAt: string };

export const figmaBoard = {
  get: (jobId: string) =>
    apiFetch<JobFigmaBoard>(`/api/jobs/${jobId}/figma-board`),
  set: (jobId: string, url: string | null) =>
    apiFetch<JobFigmaBoard>(`/api/jobs/${jobId}/figma-board`, {
      method: 'PUT',
      body: { url },
    }),
  refresh: (jobId: string) =>
    apiFetch<JobFigmaBoard>(`/api/jobs/${jobId}/figma-board/refresh`, {
      method: 'POST',
    }),
};

export const emails = {
  forJob: (jobId: string) =>
    apiFetch<EmailThread[]>(`/api/jobs/${jobId}/emails`),
  threads: (opts: { unlinkedOnly?: boolean; q?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.unlinkedOnly) params.set('unlinked', 'true');
    if (opts.q) params.set('q', opts.q);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return apiFetch<EmailThreadSummary[]>(
      `/api/email-threads${qs ? `?${qs}` : ''}`,
    );
  },
  link: (threadId: string, jobId: string | null) =>
    apiFetch<EmailThreadSummary>(`/api/email-threads/${threadId}/link`, {
      method: 'POST',
      body: { jobId },
    }),
  autoLink: () =>
    apiFetch<{
      linked: number;
      ambiguous: number;
      unmatched: number;
      byJob: Array<{ jobId: string; code: string; count: number }>;
    }>('/api/email-threads/auto-link', { method: 'POST' }),
  /** Run Haiku enquiry classification over un-judged unlinked threads
   *  (owner-only). */
  classifyEnquiries: () =>
    apiFetch<{ classified: number; enquiries: number }>(
      '/api/email-threads/classify-enquiries',
      { method: 'POST' },
    ),
  /** Unlinked threads that look like new-business enquiries (owner-only). */
  enquiries: () =>
    apiFetch<
      Array<{
        threadId: string;
        subject: string;
        from: string;
        client: string | null;
        title: string | null;
        confidence: number | null;
        lastMessageAt: string | null;
        preview: string;
      }>
    >('/api/email-threads/enquiries'),
  /** Auto-linked threads flagged as possible new business hiding under
   *  an existing job (the Christina blind spot). Owner-only. */
  newBusiness: () =>
    apiFetch<
      Array<{
        threadId: string;
        subject: string;
        from: string;
        client: string | null;
        title: string | null;
        confidence: number | null;
        lastMessageAt: string | null;
        preview: string;
        currentJob: { id: string; code: string; title: string } | null;
      }>
    >('/api/email-threads/new-business'),
  /** Dismiss a new-business flag ("this really is part of the existing
   *  job"). Owner-only. */
  dismissNewBusiness: (threadId: string) =>
    apiFetch<{ ok: true }>(
      `/api/email-threads/${encodeURIComponent(threadId)}/dismiss-new-business`,
      { method: 'POST' },
    ),
  mute: (value: string, note?: string) =>
    apiFetch<{ id: string; value: string; kind: string }>(
      '/api/muted-senders',
      { method: 'POST', body: { value, note } },
    ),
  listMuted: () =>
    apiFetch<
      Array<{
        id: string;
        value: string;
        kind: string;
        note: string | null;
        createdAt: string;
      }>
    >('/api/muted-senders'),
  unmute: (id: string) =>
    apiFetch<void>(`/api/muted-senders/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  draftJob: (threadId: string) =>
    apiFetch<{
      draft: {
        title: string;
        clientName: string;
        clientDomain: string | null;
        clientCode: string | null;
        briefBody: string;
        audience: string | null;
        tone: string | null;
        deliverables: string[];
        mustHaves: string[];
        niceToHaves: string[];
        avoid: string[];
        reasoning: string;
      };
      matchingClients: Array<{
        id: string;
        name: string;
        company: string | null;
        email: string | null;
      }>;
    }>(`/api/email-threads/${encodeURIComponent(threadId)}/draft-job`, {
      method: 'POST',
    }),
  /** Ask Claude to draft a reply in Nick's voice. Returns the
   *  suggested body + pre-filled to/subject. Nothing is sent. Pass
   *  lead=true for the warm first-reply to a new-business enquiry. */
  draftReply: (threadId: string, instructions?: string, lead?: boolean) =>
    apiFetch<{ body: string; to: string; subject: string }>(
      `/api/email-threads/${encodeURIComponent(threadId)}/draft-reply`,
      {
        method: 'POST',
        body: {
          ...(instructions ? { instructions } : {}),
          ...(lead ? { lead: true } : {}),
        },
      },
    ),
  /** Save the (edited) reply as a Gmail draft. Never sends. */
  saveDraft: (
    threadId: string,
    input: { to: string; subject: string; body: string },
  ) =>
    apiFetch<{ draftId: string; gmailThreadId: string }>(
      `/api/email-threads/${encodeURIComponent(threadId)}/save-draft`,
      { method: 'POST', body: input },
    ),
};

export type SubscriptionView = {
  id: string;
  vendor: string;
  amount: number | null;
  currency: string | null;
  cadence: 'MONTHLY' | 'ANNUAL' | 'QUARTERLY' | 'WEEKLY' | 'UNKNOWN';
  category: string | null;
  status: string;
  lastChargedAt: string | null;
  nextRenewalAt: string | null;
  monthlyEquivalent: number | null;
  stale: boolean;
  confidence: number | null;
  notes: string | null;
  sourceSubject: string | null;
  pinned: boolean;
  personal: boolean;
  /** Page to cancel / change it (saved link, else the built-in default). */
  manageUrl: string | null;
  manageUrlSaved: boolean;
};

export type SubscriptionsResponse = {
  items: SubscriptionView[];
  totalsByCurrency: Array<{ currency: string; monthly: number; annual: number }>;
  businessTotalsByCurrency: Array<{
    currency: string;
    monthly: number;
    annual: number;
  }>;
  unpricedCount: number;
};

export type SubscriptionCrawlResult = {
  scannedThreads: number;
  candidates: number;
  vendorsExamined: number;
  detected: number;
  created: number;
  updated: number;
  skippedPinned: number;
  oneOffsSkipped: number;
  budgetStopped: boolean;
};

export const subscriptions = {
  list: () => apiFetch<SubscriptionsResponse>('/api/subscriptions'),
  /** Owner-only: crawl the last year of Gmail receipts. Synchronous. */
  crawl: () =>
    apiFetch<SubscriptionCrawlResult>('/api/subscriptions/crawl', {
      method: 'POST',
    }),
  update: (
    id: string,
    patch: Partial<{
      vendor: string;
      amount: number | null;
      currency: string | null;
      cadence: SubscriptionView['cadence'];
      category: string | null;
      status: string;
      notes: string | null;
      personal: boolean;
      manageUrl: string | null;
    }>,
  ) =>
    apiFetch<SubscriptionView>(`/api/subscriptions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
    }),
  dismiss: (id: string) =>
    apiFetch<{ ok: true }>(
      `/api/subscriptions/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST' },
    ),
};

export const deliverables = {
  list: (jobId: string) =>
    apiFetch<import('./types').Deliverable[]>(
      `/api/jobs/${encodeURIComponent(jobId)}/deliverables`,
    ),
  create: (
    jobId: string,
    input: {
      name: string;
      format: string;
      duration?: number | null;
      status?: import('./types').DeliverableStatus;
    },
  ) =>
    apiFetch<import('./types').Deliverable>(
      `/api/jobs/${encodeURIComponent(jobId)}/deliverables`,
      { method: 'POST', body: input },
    ),
  update: (
    id: string,
    patch: Partial<{
      name: string;
      format: string;
      duration: number | null;
      status: import('./types').DeliverableStatus;
    }>,
  ) =>
    apiFetch<import('./types').Deliverable>(
      `/api/deliverables/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  remove: (id: string) =>
    apiFetch<void>(`/api/deliverables/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

export const portal = {
  dashboard: (code?: string) =>
    apiFetch<import('./types').PortalDashboardPayload>(
      `/api/portal/dashboard${code ? `?code=${encodeURIComponent(code)}` : ''}`,
    ),
  thumbMusic: (trackId: string, direction: 'UP' | 'DOWN' | null) =>
    apiFetch<import('./types').PortalMusicTrack>(
      `/api/music/${encodeURIComponent(trackId)}/thumb`,
      { method: 'POST', body: { direction } },
    ),
  /** Public: read the full client portal payload via a share token. */
  share: (token: string, code?: string) =>
    apiFetch<import('./types').PortalDashboardPayload>(
      `/api/portal/share/${encodeURIComponent(token)}${
        code ? `?code=${encodeURIComponent(code)}` : ''
      }`,
      { unauthenticated: true },
    ),
  /** Public: upload a file to a job's Dropbox folder via a share token. */
  uploadFileViaShare: async (
    token: string,
    jobId: string,
    file: File,
    kind: 'PROJECT_FILE' | 'JOB_BRIEF' | 'SCHEDULE_BRIEF' = 'PROJECT_FILE',
  ): Promise<import('./types').PortalUpload> => {
    const raw = await uploadBinary<{
      id: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      sharedUrl: string | null;
      source: 'KG_UPLOAD' | 'CLIENT_UPLOAD';
      createdAt: string;
    }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/assets/upload`,
      file,
      { 'X-Asset-Kind': kind },
    );
    return {
      id: raw.id,
      filename: raw.filename,
      mimeType: raw.mimeType,
      sizeBytes: raw.sizeBytes,
      sharedUrl: raw.sharedUrl,
      source: raw.source,
      uploadedByName: null,
      uploadedAt: raw.createdAt,
    };
  },
  /** Public: upload a brand asset (logo, brand guidelines, fonts) via a
   *  share token. Lands in the client's brand folder in Dropbox. */
  uploadBrandAssetViaShare: async (
    token: string,
    file: File,
  ): Promise<import('./types').PortalUpload> => {
    const raw = await uploadBinary<{
      id: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      sharedUrl: string | null;
      source: 'KG_UPLOAD' | 'CLIENT_UPLOAD';
      createdAt: string;
    }>(
      `/api/portal/share/${encodeURIComponent(token)}/brand-assets/upload`,
      file,
    );
    return {
      id: raw.id,
      filename: raw.filename,
      mimeType: raw.mimeType,
      sizeBytes: raw.sizeBytes,
      sharedUrl: raw.sharedUrl,
      source: raw.source,
      uploadedByName: null,
      uploadedAt: raw.createdAt,
    };
  },
  /** Public: set a music thumb via a share token. */
  thumbMusicViaShare: (
    token: string,
    trackId: string,
    direction: 'UP' | 'DOWN' | null,
  ) =>
    apiFetch<{ id: string; clientPreference: 'UP' | 'DOWN' | null }>(
      `/api/portal/share/${encodeURIComponent(token)}/music/${encodeURIComponent(trackId)}/thumb`,
      { method: 'POST', body: { direction }, unauthenticated: true },
    ),
  /** Public: patch brief fields via a share token. Same field
   *  allowlist as the logged-in updateFields route. */
  updateBriefFieldsViaShare: (
    token: string,
    briefId: string,
    patch: BriefFieldsPatch,
  ) =>
    apiFetch<unknown>(
      `/api/portal/share/${encodeURIComponent(token)}/briefs/${encodeURIComponent(briefId)}/fields`,
      { method: 'PATCH', body: patch, unauthenticated: true },
    ),
  /** Public: update the job's free-form Deliverables notes via a
   *  share token. Lives on the Job, not the Brief, so it gets its
   *  own route — but reads as "another brief field" to the client. */
  updateDeliverablesNotesViaShare: (
    token: string,
    jobId: string,
    value: string | null,
  ) =>
    apiFetch<{ id: string; deliverablesNotes: string | null }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/deliverables-notes`,
      { method: 'PATCH', body: { value }, unauthenticated: true },
    ),
  /** Public: approve a shared quote via a share token. The token itself
   *  is the credential — mirrors the logged-in quotes.approve. */
  approveQuoteViaShare: (token: string, quoteId: string) =>
    apiFetch<QuoteRecord>(
      `/api/portal/share/${encodeURIComponent(token)}/quotes/${encodeURIComponent(quoteId)}/decision`,
      { method: 'POST', body: { approved: true }, unauthenticated: true },
    ),
  /** Public: push back on a shared quote ("Request changes" + note). */
  declineQuoteViaShare: (token: string, quoteId: string, note?: string) =>
    apiFetch<QuoteRecord>(
      `/api/portal/share/${encodeURIComponent(token)}/quotes/${encodeURIComponent(quoteId)}/decision`,
      { method: 'POST', body: { approved: false, note }, unauthenticated: true },
    ),
  /** Public: same-origin URL of a shared quote's frozen branded PDF via
   *  a share token — the exact document the email attached. Iframe src
   *  or download href (append download=1). */
  quotePdfUrlViaShare: (
    token: string,
    quoteId: string,
    download = false,
  ): string =>
    `/api/portal/share/${encodeURIComponent(token)}/quotes/${encodeURIComponent(quoteId)}/pdf${
      download ? '?download=1' : ''
    }`,
  /** Public: same-origin URL of a raised invoice's branded PDF via a
   *  share token. Usable directly as an <iframe> src (inline view) or as
   *  a download href (append download=1) — the token is the credential,
   *  so no bearer header is needed. */
  invoicePdfUrlViaShare: (
    token: string,
    invoiceId: string,
    download = false,
  ): string =>
    `/api/portal/share/${encodeURIComponent(token)}/invoices/${encodeURIComponent(invoiceId)}/pdf${
      download ? '?download=1' : ''
    }`,
  /** Auth: create a magic-link share for a client. ADMIN / EDITOR only.
   *  Returns the raw token + a slugified client name so the caller can
   *  build a brandable URL: `/portal/<clientSlug>/<token>`. */
  createShare: (
    clientId: string,
    expiresInDays?: number,
    arollAccess?: 'VIEW' | 'COMMENT' | 'EDIT',
    /** Stamp the link with the Ballast world it was minted in, so it
     *  resolves that project and no other. Omitted by the CRM, whose
     *  portal links are client-wide by design. */
    module?: 'AROLL' | 'PLAYBACK',
    /** Whether the link may fetch full-quality originals. Omitted =>
     *  downloads on (the pre-existing behaviour). */
    allowDownload?: boolean,
    /** Who the link is for ("Motherbird", "Kellie"). Names the link in
     *  the list and pre-fills the reviewer's name on the player. */
    recipientName?: string,
    /** Whether the link may mark range selects on a review version.
     *  Omitted => selects on (the column default). */
    allowSelects?: boolean,
    /** PictureUp: whether the link also shows the project's A-Roll.
     *  Omitted => the server's default (on for the CRM's portal links,
     *  off for a workspace link). */
    allowAroll?: boolean,
  ) =>
    apiFetch<{
      id: string;
      token: string;
      expiresAt: string;
      clientName: string;
      recipientName: string | null;
      clientSlug: string;
    }>(
      `/api/clients/${encodeURIComponent(clientId)}/portal-share`,
      {
        method: 'POST',
        body: {
          ...(expiresInDays ? { expiresInDays } : {}),
          ...(arollAccess ? { arollAccess } : {}),
          ...(module ? { module } : {}),
          ...(allowDownload === undefined ? {} : { allowDownload }),
          ...(recipientName?.trim() ? { recipientName: recipientName.trim() } : {}),
          ...(allowSelects === undefined ? {} : { allowSelects }),
          ...(allowAroll === undefined ? {} : { allowAroll }),
        },
      },
    ),
  /** Auth: list share tokens issued for a client. Pass a module to see
   *  only the links that open that world (plus any legacy client-wide
   *  links, which open it too). */
  listShares: (clientId: string, module?: 'AROLL' | 'PLAYBACK') =>
    apiFetch<
      Array<{
        id: string;
        createdAt: string;
        expiresAt: string;
        lastUsedAt: string | null;
        revokedAt: string | null;
        createdBy: { name: string } | null;
      }>
    >(
      `/api/clients/${encodeURIComponent(clientId)}/portal-share${
        module ? `?module=${module}` : ''
      }`,
    ),
  /** Auth: revoke a share token by id. Idempotent. */
  revokeShare: (tokenId: string) =>
    apiFetch<{ id: string; revokedAt: string | null }>(
      `/api/portal-share/${encodeURIComponent(tokenId)}/revoke`,
      { method: 'POST', body: {} },
    ),
};

export const gmail = {
  start: () => apiFetch<{ url: string; state: string }>('/api/gmail/auth/start', { method: 'POST' }),
  status: () => apiFetch<GmailStatus>('/api/gmail/status'),
  disconnect: (connectionId?: string) =>
    apiFetch<void>('/api/gmail/disconnect', {
      method: 'POST',
      body: connectionId ? { connectionId } : undefined,
    }),
  sync: (input: { q?: string; maxThreads?: number } = {}) =>
    apiFetch<GmailSyncResult>('/api/gmail/sync', {
      method: 'POST',
      body: input,
    }),
  backfill: () =>
    apiFetch<GmailSyncResult>('/api/gmail/backfill', {
      method: 'POST',
      body: {},
    }),
  reset: () =>
    apiFetch<{ deletedThreads: number; deletedMessages: number }>(
      '/api/gmail/reset',
      { method: 'POST' },
    ),
};

// All preproduction routes share the same `requireAuthOrShareToken`
// middleware on the backend, so we wrap every path with `withShareToken`
// to thread the active share-token (if any) through as a query param.
// For logged-in users this is a no-op; for portal share guests it lets
// them edit without a JWT.
const ppPath = (p: string) => withShareToken(p);

// Post-production is editable from the client portal too, so every path
// threads the active share-token (no-op for logged-in users) like the
// preproduction routes.
export const postproduction = {
  rounds: (jobId: string) =>
    apiFetch<import('./types').PostRound[]>(
      withShareToken(`/api/jobs/${encodeURIComponent(jobId)}/postproduction/rounds`),
    ),
  addRound: (
    jobId: string,
    input: { kind?: 'AGENCY' | 'CLIENT' | 'KILOGRAM'; date?: string | null; label?: string | null },
  ) =>
    apiFetch<import('./types').PostRound>(
      withShareToken(`/api/jobs/${encodeURIComponent(jobId)}/postproduction/rounds`),
      { method: 'POST', body: input },
    ),
  /** Read a client schedule document (xlsx / csv / pdf / docx) into a
   *  timeline PROPOSAL (rounds + delivery). Nothing is written — the
   *  caller shows the proposal and the user accepts explicitly. */
  roundsFromDocument: (jobId: string, file: File) =>
    uploadBinary<{
      deliveryDate: string | null;
      rounds: Array<{
        kind: 'AGENCY' | 'CLIENT' | 'KILOGRAM';
        date: string | null;
        label: string;
      }>;
      note: string | null;
    }>(
      `/api/jobs/${encodeURIComponent(jobId)}/postproduction/rounds/from-document`,
      file,
    ),
  updateRound: (
    id: string,
    patch: {
      kind?: 'AGENCY' | 'CLIENT' | 'KILOGRAM';
      date?: string | null;
      label?: string | null;
      done?: boolean;
      orderIndex?: number;
    },
  ) =>
    apiFetch<import('./types').PostRound>(
      withShareToken(`/api/postproduction/rounds/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  deleteRound: (id: string) =>
    apiFetch<void>(withShareToken(`/api/postproduction/rounds/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    }),
  setDelivery: (jobId: string, date: string | null) =>
    apiFetch<{ id: string; dueDate: string | null }>(
      withShareToken(`/api/jobs/${encodeURIComponent(jobId)}/postproduction/delivery`),
      { method: 'PATCH', body: { date } },
    ),
};

/** Team-wide Timelines overview (ADMIN + EDITOR): every job's delivery
 *  runway in one read-only payload. No money fields. */
export const timelines = {
  overview: () =>
    apiFetch<{ jobs: import('./types').TimelinesOverviewJob[] }>('/api/timelines'),
};

export const preproduction = {
  load: (jobId: string) =>
    apiFetch<import('./types').PreproductionPayload>(
      ppPath(`/api/jobs/${encodeURIComponent(jobId)}/preproduction`),
    ),

  createFrame: (
    jobId: string,
    input: {
      label: string;
      title?: string;
      description?: string;
      storyboardImageUrl?: string;
    },
  ) =>
    apiFetch<import('./types').PreproductionFrame>(
      ppPath(`/api/jobs/${encodeURIComponent(jobId)}/preproduction/frames`),
      { method: 'POST', body: input },
    ),
  updateFrame: (
    id: string,
    patch: Partial<{
      label: string;
      title: string | null;
      description: string | null;
      storyboardImageUrl: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionFrame>(
      ppPath(`/api/preproduction/frames/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  deleteFrame: (id: string) =>
    apiFetch<void>(ppPath(`/api/preproduction/frames/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    }),

  createLocation: (
    jobId: string,
    input: {
      name: string;
      kind: 'INT' | 'EXT';
      address?: string;
      notes?: string;
      /** Shoot day to attach this location to (each day owns its own). */
      shootDayId?: string | null;
    },
  ) =>
    apiFetch<import('./types').PreproductionLocation>(
      ppPath(`/api/jobs/${encodeURIComponent(jobId)}/preproduction/locations`),
      { method: 'POST', body: input },
    ),
  updateLocation: (
    id: string,
    patch: Partial<{
      name: string;
      kind: 'INT' | 'EXT';
      address: string | null;
      notes: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionLocation>(
      ppPath(`/api/preproduction/locations/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  deleteLocation: (id: string) =>
    apiFetch<void>(ppPath(`/api/preproduction/locations/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    }),
  linkLocationFrame: (locationId: string, frameId: string) =>
    apiFetch<import('./types').PreproductionLocation>(
      ppPath(
        `/api/preproduction/locations/${encodeURIComponent(locationId)}/frames/${encodeURIComponent(frameId)}`,
      ),
      { method: 'POST', body: {} },
    ),
  unlinkLocationFrame: (locationId: string, frameId: string) =>
    apiFetch<import('./types').PreproductionLocation>(
      ppPath(
        `/api/preproduction/locations/${encodeURIComponent(locationId)}/frames/${encodeURIComponent(frameId)}`,
      ),
      { method: 'DELETE' },
    ),
  uploadRecciImage: (locationId: string, file: File) =>
    uploadBinary<import('./types').PreproductionRecciImage>(
      ppPath(
        `/api/preproduction/locations/${encodeURIComponent(locationId)}/recci/upload`,
      ),
      file,
    ),
  deleteRecciImage: (imageId: string) =>
    apiFetch<void>(ppPath(`/api/preproduction/recci/${encodeURIComponent(imageId)}`), {
      method: 'DELETE',
    }),
  refreshLocationContext: (locationId: string) =>
    apiFetch<import('./types').LocationContext>(
      ppPath(
        `/api/preproduction/locations/${encodeURIComponent(locationId)}/refresh-context`,
      ),
      { method: 'POST', body: {} },
    ),
  refreshShootDayContext: (shootDayId: string) =>
    apiFetch<import('./types').ShootDayContext>(
      ppPath(
        `/api/preproduction/shoot-days/${encodeURIComponent(shootDayId)}/refresh-context`,
      ),
      { method: 'POST', body: {} },
    ),

  createCrew: (
    jobId: string,
    input: {
      roleTitle: string;
      contractorId?: string | null;
      notes?: string;
      /** Shoot day to attach this crew row to (each day owns its crew). */
      shootDayId?: string | null;
    },
  ) =>
    apiFetch<import('./types').PreproductionCrew>(
      ppPath(`/api/jobs/${encodeURIComponent(jobId)}/preproduction/crew`),
      { method: 'POST', body: input },
    ),
  updateCrew: (
    id: string,
    patch: Partial<{
      roleTitle: string;
      contractorId: string | null;
      notes: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionCrew>(
      ppPath(`/api/preproduction/crew/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  deleteCrew: (id: string) =>
    apiFetch<void>(ppPath(`/api/preproduction/crew/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    }),

  // Additional crew — freeform clients / externals / actors.
  createAdditionalCrew: (
    jobId: string,
    input: {
      name: string;
      role?: string | null;
      email?: string | null;
      phone?: string | null;
      notes?: string | null;
      /** Shoot day to attach this contact to (each day owns its list). */
      shootDayId?: string | null;
    },
  ) =>
    apiFetch<import('./types').PreproductionAdditionalCrew>(
      ppPath(
        `/api/jobs/${encodeURIComponent(jobId)}/preproduction/additional-crew`,
      ),
      { method: 'POST', body: input },
    ),
  updateAdditionalCrew: (
    id: string,
    patch: Partial<{
      name: string;
      role: string | null;
      email: string | null;
      phone: string | null;
      notes: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionAdditionalCrew>(
      ppPath(`/api/preproduction/additional-crew/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  deleteAdditionalCrew: (id: string) =>
    apiFetch<void>(
      ppPath(`/api/preproduction/additional-crew/${encodeURIComponent(id)}`),
      { method: 'DELETE' },
    ),

  createShootDay: (
    jobId: string,
    input: {
      date: string;
      /** HOLD = penciled-in / tentative. CONFIRMED = locked (call
       *  sheet territory). Defaults to CONFIRMED on the backend when
       *  omitted, mirroring the legacy "if it exists, it's real"
       *  semantics. Pass HOLD when adding a hold-date directly. */
      status?: 'HOLD' | 'CONFIRMED';
      callTime?: string;
      wrapTime?: string;
      locationSummary?: string;
      notes?: string;
      nearestHospital?: string;
    },
  ) =>
    apiFetch<import('./types').PreproductionShootDay>(
      ppPath(`/api/jobs/${encodeURIComponent(jobId)}/preproduction/shoot-days`),
      { method: 'POST', body: input },
    ),
  updateShootDay: (
    id: string,
    patch: Partial<{
      date: string;
      status: 'HOLD' | 'CONFIRMED';
      callTime: string | null;
      wrapTime: string | null;
      locationSummary: string | null;
      notes: string | null;
      nearestHospital: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionShootDay>(
      ppPath(`/api/preproduction/shoot-days/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  /** Download the call sheet PDF for a shoot day. We can't use a
   *  simple <a href> because auth is JWT Bearer in headers, not
   *  cookies — so fetch with the auth header, convert the response
   *  to a blob, then trigger a save via a hidden anchor. The
   *  Content-Disposition filename from the backend is honoured by
   *  parsing the response header (falls back to a synthesised name
   *  when the header isn't surfaced by the browser e.g. CORS).
   *
   *  Returns the Dropbox path (when the backend also uploaded a copy
   *  to the job's 8-PRE-PROD/Call Sheets folder) so the caller can
   *  mention it in the success toast. Null when the job hasn't been
   *  Dropbox-linked yet or the upload silently failed. */
  downloadCallSheetPdf: async (
    shootDayId: string,
    fallbackName: string,
  ): Promise<{ filename: string; dropboxPath: string | null }> => {
    const url = ppPath(
      `/api/preproduction/shoot-days/${encodeURIComponent(shootDayId)}/call-sheet.pdf`,
    );
    // Bearer + one refresh retry, like every other authed binary helper:
    // without the retry this silently failed after the 15-minute token
    // expiry (the standing uploadBinary gotcha).
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(url, { headers });
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(
        `Call sheet download failed (${res.status}): ${body.slice(0, 200) || res.statusText}`,
      );
    }
    // Pull the filename out of Content-Disposition if surfaced.
    const cd = res.headers.get('content-disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(cd);
    const filename = match?.[1] ?? fallbackName;
    // Server URL-encodes the path (headers are ISO-8859-1; job folders
    // carry non-ASCII). Decode for display, tolerating legacy raw values.
    const rawDropboxPath = res.headers.get('x-dropbox-path');
    let dropboxPath: string | null = rawDropboxPath;
    if (rawDropboxPath) {
      try {
        dropboxPath = decodeURIComponent(rawDropboxPath);
      } catch {
        dropboxPath = rawDropboxPath;
      }
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    return { filename, dropboxPath };
  },
  deleteShootDay: (id: string) =>
    apiFetch<void>(ppPath(`/api/preproduction/shoot-days/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    }),
  /** Copy Locations + Crew + Additional-crew from `sourceId` onto
   *  `targetId` (both days on the same job). "Copy from previous day". */
  copyDayResources: (targetId: string, sourceId: string) =>
    apiFetch<{ copied: { locations: number; crew: number; additionalCrew: number } }>(
      ppPath(
        `/api/preproduction/shoot-days/${encodeURIComponent(targetId)}/copy-from/${encodeURIComponent(sourceId)}`,
      ),
      { method: 'POST', body: {} },
    ),

  // ---- Finalize → send call sheet to crew ---------------------------

  /** Lock in a shoot day (finalizedAt + CONFIRMED). Returns the day. */
  finalizeShootDay: (id: string) =>
    apiFetch<import('./types').PreproductionShootDay>(
      ppPath(`/api/preproduction/shoot-days/${encodeURIComponent(id)}/finalize`),
      { method: 'POST', body: {} },
    ),
  /** The day's emailable crew (production + additional with an email). */
  crewRecipients: (id: string) =>
    apiFetch<Array<import('./types').CrewRecipient>>(
      ppPath(`/api/preproduction/shoot-days/${encodeURIComponent(id)}/crew-recipients`),
    ),
  /** Email the call-sheet PDF to the selected crew. */
  sendCallSheet: (
    id: string,
    input: { emails: string[]; message?: string | null; subject?: string | null },
  ) =>
    apiFetch<{
      sent: number;
      failed: number;
      recipients: Array<{ email: string; name: string; ok: boolean; reason?: string }>;
    }>(
      ppPath(`/api/preproduction/shoot-days/${encodeURIComponent(id)}/send-call-sheet`),
      { method: 'POST', body: input },
    ),

  // ---- PendingShootDate (AI-suggested review queue) -----------------

  /** List every Claude-suggested candidate shoot date awaiting the
   *  editor's verdict for this job. */
  listPendingShootDates: (jobId: string) =>
    apiFetch<Array<import('./types').PendingShootDate>>(
      ppPath(
        `/api/jobs/${encodeURIComponent(jobId)}/preproduction/pending-shoot-dates`,
      ),
    ),
  /** Promote a pending candidate into a real ShootDay (default status
   *  CONFIRMED — editor's verdict overrides Claude's suggestedStatus).
   *  Pass `status: 'HOLD'` to accept as a hold instead. */
  acceptPendingShootDate: (
    pendingId: string,
    input: { status?: 'HOLD' | 'CONFIRMED' } = {},
  ) =>
    apiFetch<
      import('./types').PreproductionShootDay & {
        /** Whether the Kilogram-calendar Hold/Shoot event was written.
         *  SKIPPED usually means the Google connection needs the
         *  one-time reconnect that grants calendar write access. */
        calendarEvent?: 'CREATED' | 'ALREADY_LINKED' | 'SKIPPED';
      }
    >(
      ppPath(
        `/api/preproduction/pending-shoot-dates/${encodeURIComponent(pendingId)}/accept`,
      ),
      { method: 'POST', body: input },
    ),
  /** Dismiss a pending candidate (Claude mis-read / wrong job / etc).
   *  Just deletes the row; nothing else happens. */
  /** Accept a pending date as a MEETING: job Task + best-effort
   *  calendar event, never a ShootDay. Consumes the suggestion. */
  acceptPendingDateAsMeeting: (pendingId: string) =>
    apiFetch<{ task: unknown; calendarEvent: 'CREATED' | 'SKIPPED' }>(
      ppPath(
        `/api/preproduction/pending-shoot-dates/${encodeURIComponent(pendingId)}/accept-meeting`,
      ),
      { method: 'POST' },
    ),
  dismissPendingShootDate: (pendingId: string) =>
    apiFetch<void>(
      ppPath(
        `/api/preproduction/pending-shoot-dates/${encodeURIComponent(pendingId)}/dismiss`,
      ),
      { method: 'POST' },
    ),

  createEntry: (
    shootDayId: string,
    input: {
      startTime: string;
      durationMin?: number | null;
      activity: string;
      frameId?: string | null;
      cameraLens?: string | null;
      locationId?: string | null;
      notes?: string | null;
      blockLabel?: string | null;
    },
  ) =>
    apiFetch<import('./types').PreproductionScheduleEntry>(
      ppPath(
        `/api/preproduction/shoot-days/${encodeURIComponent(shootDayId)}/entries`,
      ),
      { method: 'POST', body: input },
    ),
  updateEntry: (
    id: string,
    patch: Partial<{
      startTime: string;
      durationMin: number | null;
      activity: string;
      frameId: string | null;
      cameraLens: string | null;
      locationId: string | null;
      notes: string | null;
      blockLabel: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionScheduleEntry>(
      ppPath(`/api/preproduction/entries/${encodeURIComponent(id)}`),
      { method: 'PATCH', body: patch },
    ),
  deleteEntry: (id: string) =>
    apiFetch<void>(ppPath(`/api/preproduction/entries/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    }),
  /**
   * Insert a blank schedule entry immediately BELOW `referenceEntryId`.
   * Backend renumbers orderIndex in one transaction. Powers the "+"
   * button next to the drag handle on each row.
   */
  insertEntryBelow: (referenceEntryId: string) =>
    apiFetch<import('./types').PreproductionScheduleEntry>(
      ppPath(
        `/api/preproduction/entries/${encodeURIComponent(referenceEntryId)}/insert-below`,
      ),
      { method: 'POST' },
    ),

  /**
   * Ask Claude to draft a run sheet from a free-text brief. Returns
   * the proposed rows for the caller to render as a preview - nothing
   * is inserted server-side. Commit the chosen rows via `bulkEntries`.
   */
  draftFromText: (
    shootDayId: string,
    input: { text: string; mode: 'CONTENT' | 'TVC' },
  ) =>
    apiFetch<{
      entries: Array<{
        startTime: string;
        durationMin: number | null;
        activity: string;
        locationId: string | null;
        locationName: string | null;
        frameId: string | null;
        frameLabel: string | null;
        cameraLens: string | null;
        blockLabel: string | null;
      }>;
    }>(
      ppPath(
        `/api/preproduction/shoot-days/${encodeURIComponent(shootDayId)}/draft-from-text`,
      ),
      { method: 'POST', body: input },
    ),
  /**
   * Google Places (New) autocomplete proxy. Backend forwards to
   * Google with the server-side key — the browser never sees it.
   * `sessionToken` should be a UUID generated once per typing
   * burst (we group a single field's keystrokes into one Google
   * "session") so Google's billing groups them too.
   *
   * Returns an empty list when the API key isn't configured, when
   * the query is too short, or when the upstream call fails — the
   * caller should treat all three the same way: no dropdown.
   */
  placesAutocomplete: (input: { query: string; sessionToken: string | null }) =>
    apiFetch<{
      suggestions: Array<{
        placeId: string;
        text: string;
        mainText: string;
        secondaryText: string;
      }>;
    }>(ppPath('/api/preproduction/places/autocomplete'), {
      method: 'POST',
      body: input,
    }),

  /**
   * Upload a call sheet PDF or run sheet DOCX. Backend hands it to
   * Claude, replaces the day's existing entries with the parsed
   * rows, and appends the parsed "additional info" to ShootDay.notes
   * with a "--- from <filename> ---" divider. Returns the count of
   * entries created and the new notes string so the caller can
   * refresh the schedule view without a separate fetch.
   *
   * Mode is sent as an X-Schedule-Mode header so the raw-body upload
   * stays a pure binary stream (the standard uploadBinary helper
   * doesn't carry JSON alongside the file).
   */
  populateFromDocument: (
    shootDayId: string,
    file: File,
    mode: 'CONTENT' | 'TVC',
  ) =>
    uploadBinary<{
      entriesCreated: number;
      notesAppended: boolean;
      notes: string | null;
      contactsAdded: number;
    }>(
      ppPath(
        `/api/preproduction/shoot-days/${encodeURIComponent(shootDayId)}/populate-from-document`,
      ),
      file,
      { 'X-Schedule-Mode': mode },
    ),
  bulkEntries: (
    shootDayId: string,
    rows: Array<{
      startTime: string;
      durationMin: number | null;
      activity: string;
      frameId: string | null;
      cameraLens: string | null;
      locationId: string | null;
      notes: string | null;
      blockLabel: string | null;
    }>,
  ) =>
    apiFetch<{ created: number }>(
      ppPath(
        `/api/preproduction/shoot-days/${encodeURIComponent(shootDayId)}/bulk-entries`,
      ),
      { method: 'POST', body: { rows } },
    ),
};

// Wave 2 extension: Talent, FrameDressing (Wardrobe + Props), Budget.
// Re-uses the existing `preproduction` namespace conceptually but
// declared separately to avoid a giant single object.
/** AI-drafted quote line items (proposed, not saved). */
export type QuoteDraft = {
  lineItems: Array<{ description: string; quantity: number; rate: number }>;
  notes: string;
  taxRate: number;
  model: string;
};

export type RateCardItem = {
  id: string;
  /** Short item name (the hero/title on a quote). */
  name: string | null;
  description: string;
  rate: number;
  category: string | null;
  unit: string | null;
  /** Matching Xero inventory item code (the shared key with Xero). */
  xeroItemCode: string | null;
  defaultQty: number;
  orderIndex: number;
  active: boolean;
};
export type RateCardInput = Partial<Omit<RateCardItem, 'id' | 'orderIndex'>>;

export const rateCard = {
  list: (includeInactive = false) =>
    apiFetch<RateCardItem[]>(
      `/api/rate-card${includeInactive ? '?includeInactive=1' : ''}`,
    ),
  create: (input: RateCardInput) =>
    apiFetch<RateCardItem>('/api/rate-card', { method: 'POST', body: input }),
  update: (id: string, patch: RateCardInput) =>
    apiFetch<RateCardItem>(`/api/rate-card/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/rate-card/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  bulk: (items: RateCardInput[]) =>
    apiFetch<RateCardItem[]>('/api/rate-card/bulk', {
      method: 'POST',
      body: { items },
    }),
};

export const quotes = {
  /** AI-draft quote line items from the job's brief + pricing history.
   *  Spends on Anthropic (budget-guarded). Returns a proposal to review. */
  draft: (jobId: string) =>
    apiFetch<QuoteDraft>(
      `/api/jobs/${encodeURIComponent(jobId)}/quotes/draft`,
      { method: 'POST' },
    ),
  /** All quote versions for a job (newest version first). */
  listForJob: (jobId: string) =>
    apiFetch<QuoteRecord[]>(`/api/jobs/${encodeURIComponent(jobId)}/quotes`),
  /** Persist an edited/drafted quote as a new version. */
  create: (
    jobId: string,
    input: {
      lineItems: QuoteLineInput[];
      taxRate?: number;
      lineAmountType?: LineAmountType;
      notes?: string;
      /** Optional label so the job can hold multiple distinct quotes. */
      name?: string | null;
      /** Bulk discount line under the work lines; null = none. */
      discount?: DiscountSpecInput | null;
    },
  ) =>
    apiFetch<QuoteRecord>(`/api/jobs/${encodeURIComponent(jobId)}/quotes`, {
      method: 'POST',
      body: input,
    }),
  /** Issue a quote by snapshotting the job's current budget lines. */
  fromBudget: (
    jobId: string,
    input: {
      taxRate?: number;
      lineAmountType?: LineAmountType;
      notes?: string;
      name?: string | null;
      /** Snapshot only this budget group; omit for the whole budget. */
      quoteGroup?: string | null;
    } = {},
  ) =>
    apiFetch<QuoteRecord>(
      `/api/jobs/${encodeURIComponent(jobId)}/quotes/from-budget`,
      { method: 'POST', body: input },
    ),
  /** One quote version (any team surface can prefill a revision from it). */
  get: (quoteId: string) =>
    apiFetch<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}`),
  /** Revise an issued quote: issues the NEXT VERSION, cloning anything not
   *  overridden. The portal shows the latest version per named quote, so
   *  the revision supersedes the source for the client automatically. */
  revise: (
    quoteId: string,
    input: {
      lineItems?: QuoteLineInput[];
      taxRate?: number;
      lineAmountType?: LineAmountType;
      notes?: string;
      name?: string | null;
      /** Delete the source version once the revision is issued ("just
       *  replace it"). Ignored server-side for approved sources. */
      replaceSource?: boolean;
      /** Omitted keeps the source's discount; null removes it. */
      discount?: DiscountSpecInput | null;
    } = {},
  ) =>
    apiFetch<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}/revise`, {
      method: 'POST',
      body: input,
    }),
  approve: (quoteId: string) =>
    apiFetch<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}/approve`, {
      method: 'POST',
    }),
  /** Permanently delete a quote version (production team only). */
  delete: (quoteId: string) =>
    apiFetch<void>(`/api/quotes/${encodeURIComponent(quoteId)}`, {
      method: 'DELETE',
    }),
  /** Record client pushback ("Request changes") with an optional note.
   *  Also usable by the team to log pushback relayed by phone/email. */
  decline: (quoteId: string, note?: string) =>
    apiFetch<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}/decline`, {
      method: 'POST',
      body: { note },
    }),
  /** Prefill for the in-app email composer (same covering note the
   *  one-click draft used; subject already "Re: …" when it threads). */
  emailPrefill: (quoteId: string) =>
    apiFetch<DocEmailPrefill>(
      `/api/quotes/${encodeURIComponent(quoteId)}/email-draft`,
    ),
  /** Create the Gmail draft from the composer's personalised content
   *  (branded quote PDF attached, replying into the picked thread).
   *  Never sends. */
  emailDraft: (quoteId: string, input: DocEmailDraftInput) =>
    apiFetch<{ draftId: string; gmailThreadId: string; threaded: boolean }>(
      `/api/quotes/${encodeURIComponent(quoteId)}/email-draft`,
      { method: 'POST', body: input },
    ),
  /** Mark a quote to show on the client portal (or hide it). Owner-only. */
  setShared: (quoteId: string, shared: boolean) =>
    apiFetch<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}/share`, {
      method: 'POST',
      body: { shared },
    }),
  /**
   * Fetch the branded quote PDF (auth'd) — the SAME document
   * "Email to client" attaches, with the server's filename. Callers
   * trigger a download from the returned Blob; never window.print(),
   * which stamps the browser's date/title header on every page.
   */
  pdf: async (quoteId: string): Promise<{ blob: Blob; filename: string }> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'application/pdf' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/quotes/${encodeURIComponent(quoteId)}/pdf?download=1`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    const cd = res.headers.get('Content-Disposition') ?? '';
    const m = /filename="([^"]+)"/.exec(cd);
    return { blob: await res.blob(), filename: m?.[1] ?? 'quote.pdf' };
  },
};

/** One of the job's email chains, pickable in the composer. */
export type DocEmailThread = {
  id: string;
  subject: string;
  lastMessageAt: string | null;
};

/** Prefill for the quote/invoice email composer ("draft box"). */
export type DocEmailPrefill = {
  to: string;
  subject: string;
  body: string;
  /** Filename of the branded PDF that rides along automatically. */
  attachmentName: string;
  /** Quotes only: the draft will thread into the client conversation. */
  threaded?: boolean;
  /** Subject to use when "New email" is picked (no thread). */
  freshSubject: string;
  /** The job's recent email chains for the thread picker. */
  threads: DocEmailThread[];
  /** Chain preselected in the picker (null = new email). */
  defaultThreadId: string | null;
};

/** What the composer sends when saving the Gmail draft. */
export type DocEmailDraftInput = {
  to: string;
  subject: string;
  body: string;
  /** Chain to reply into; null = start a new email. */
  threadId: string | null;
};

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'VOID';

/** Xero tax types (per line). */
export type TaxType = 'GST on Income' | 'GST Free Income' | 'BAS Excluded';

/** The bulk discount on a quote or invoice (backend lib/tax
 *  resolveDiscount): 'percent' of the ex-GST subtotal of the work lines,
 *  or 'amount' dollars off the client's final total, GST-inclusive. */
export type DiscountSpecInput = {
  type: 'amount' | 'percent';
  value: number;
  label?: string | null;
};
/** Document-level "Amounts are". */
export type LineAmountType = 'EXCLUSIVE' | 'INCLUSIVE' | 'NOTAX';

export type QuoteLineInput = {
  description: string;
  /** Item name rendered as the line's heading on client documents;
   *  older stored quotes/invoices predate it. */
  name?: string | null;
  quantity: number;
  /** NET (post-discount) unit rate — what the totals are built on. */
  rate: number;
  /** Pre-discount unit price + the discount as entered, display-only.
   *  Present only on lines issued from a discounted budget; carried
   *  through a revise so the documents keep showing the discount. */
  listRate?: number | null;
  discountPct?: number | null;
  discountAmt?: number | null;
  taxType?: TaxType;
  /** Xero inventory item code, carried from the rate card. */
  itemCode?: string | null;
  /** Budget section heading; the portal quote renders lines grouped by it. */
  category?: string | null;
  /** Set only on the stored bulk discount line (rate negative). The
   *  editor lifts it out into its own control and sends `discount`. */
  kind?: 'discount';
  discountType?: 'amount' | 'percent';
  discountValue?: number;
  discountLabel?: string | null;
  taxAmount?: number;
};

/** Where a quote is in its life (backend QuoteLifecycle). */
export type QuoteLifecycleStatus =
  | 'DRAFT'
  | 'SENT'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'LAPSED'
  | 'SUPERSEDED'
  | 'INVOICED';

export type QuoteRecord = {
  id: string;
  jobId: string;
  version: number;
  name: string | null;
  /** A/B option group; null = the job's quote. */
  optionGroup: string | null;
  status: QuoteLifecycleStatus;
  statusReason: string | null;
  statusChangedAt: string | null;
  /** One latest per (job, optionGroup). Present on job quote lists. */
  isLatest?: boolean;
  lineItems: QuoteLineInput[];
  subtotal: string;
  taxRate: string;
  lineAmountType: LineAmountType;
  total: string;
  notes: string | null;
  sharedAt: string | null;
  approvedAt: string | null;
  /** Client pushback ("Request changes") — cleared by approving; a
   *  revision starts a clean new version. */
  declinedAt: string | null;
  declineNote: string | null;
  createdAt: string;
};

export type InvoiceRecord = {
  id: string;
  jobId: string;
  number: string;
  lineItems: QuoteLineInput[];
  subtotal: string;
  taxRate: string;
  lineAmountType: LineAmountType;
  total: string;
  status: InvoiceStatus;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  xeroInvoiceId: string | null;
  xeroPushedAt: string | null;
  /** The number Xero holds it under (differs from `number` only after a
   *  re-push collided with a voided original). */
  xeroInvoiceNumber: string | null;
  /** Why the last push to Xero failed; null = in sync. */
  xeroPushError: string | null;
  /** Xero mirror — the ledger's view once synced (null until then).
   *  From SENT onward these are the real figures; `total` is what we
   *  sent. See InvoiceAmount for how the two are shown together. */
  xeroStatus: string | null;
  xeroTotal: string | null;
  xeroAmountDue: string | null;
  xeroAmountPaid: string | null;
  xeroAmountCredited: string | null;
  xeroSyncedAt: string | null;
  /** Changed in Xero after the push: the lines here are Xero's now and
   *  the frozen PDF re-renders on the next open. */
  editedInXero?: boolean;
  xeroEditedAt?: string | null;
  /** From send_invoice: e.g. the client's Xero contact has no email. */
  warnings?: string[];
  /** The quote this invoice was raised from (soft pointer) — powers
   *  deposit/balance maths and the revision-drift nudge. */
  quoteId: string | null;
  createdAt: string;
};

export const invoices = {
  listForJob: (jobId: string) =>
    apiFetch<InvoiceRecord[]>(`/api/jobs/${encodeURIComponent(jobId)}/invoices`),
  /** One invoice with its lines (the hub rows don't carry them). */
  get: (invoiceId: string) =>
    apiFetch<InvoiceRecord>(`/api/invoices/${encodeURIComponent(invoiceId)}`),
  /** Raise an invoice — from an approved quote (default) or manual
   *  lines. mode DEPOSIT bills depositPct% (default 50) of the quote as
   *  one line; BALANCE bills whatever prior invoices haven't covered. */
  create: (
    jobId: string,
    input: {
      quoteId?: string;
      lineItems?: QuoteLineInput[];
      dueInDays?: number;
      taxRate?: number;
      lineAmountType?: LineAmountType;
      mode?: 'FULL' | 'DEPOSIT' | 'BALANCE';
      depositPct?: number;
      /** FULL only. Omitted carries the quote's discount; null drops it. */
      discount?: DiscountSpecInput | null;
    } = {},
  ) =>
    apiFetch<InvoiceRecord>(`/api/jobs/${encodeURIComponent(jobId)}/invoices`, {
      method: 'POST',
      body: input,
    }),
  /** Edit an invoice's lines / tax settings. A sent invoice's edit goes
   *  to Xero first and is refused once a payment or credit has landed. */
  update: (
    invoiceId: string,
    input: {
      lineItems?: QuoteLineInput[];
      taxRate?: number;
      lineAmountType?: LineAmountType;
      /** Omitted keeps the invoice's discount; null removes it. */
      discount?: DiscountSpecInput | null;
    },
  ) =>
    apiFetch<InvoiceRecord>(`/api/invoices/${encodeURIComponent(invoiceId)}`, {
      method: 'PATCH',
      body: input,
    }),
  send: (invoiceId: string, dueInDays?: number) =>
    apiFetch<InvoiceRecord>(`/api/invoices/${encodeURIComponent(invoiceId)}/send`, {
      method: 'POST',
      body: dueInDays != null ? { dueInDays } : {},
    }),
  markPaid: (invoiceId: string) =>
    apiFetch<InvoiceRecord>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/mark-paid`,
      { method: 'POST' },
    ),
  /** Credit note in Xero against a sent/paid invoice (owner-only). Amount
   *  defaults to whatever is left to credit; the reason prints on it. */
  creditNote: (invoiceId: string, input: { amount?: number; reason: string }) =>
    apiFetch<{ invoice: InvoiceRecord; creditNoteNumber: string | null; amount: number }>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/credit-note`,
      { method: 'POST', body: input },
    ),
  /** Un-send: flip a SENT invoice back to DRAFT. A pushed invoice is
   *  voided in Xero and unlinked, so a re-send creates a fresh one. */
  revertToDraft: (invoiceId: string) =>
    apiFetch<InvoiceRecord>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/revert-to-draft`,
      { method: 'POST' },
    ),
  void: (invoiceId: string) =>
    apiFetch<InvoiceRecord>(`/api/invoices/${encodeURIComponent(invoiceId)}/void`, {
      method: 'POST',
    }),
  /** Draft a friendly payment reminder for an unpaid invoice -> Gmail draft. */
  paymentReminder: (invoiceId: string) =>
    apiFetch<{ draftId: string; to: string }>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/payment-reminder`,
      { method: 'POST' },
    ),
  /** Prefill for the in-app email composer (standard covering note). */
  emailPrefill: (invoiceId: string) =>
    apiFetch<DocEmailPrefill>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/email-draft`,
    ),
  /** Create the Gmail draft from the composer's personalised content
   *  (branded invoice PDF attached, replying into the picked thread
   *  when one is chosen). Never sends. */
  emailDraft: (invoiceId: string, input: DocEmailDraftInput) =>
    apiFetch<{ draftId: string; to: string; threaded: boolean }>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/email-draft`,
      { method: 'POST', body: input },
    ),
  /** Push this invoice into Xero (creates a draft there). */
  pushToXero: (invoiceId: string) =>
    apiFetch<InvoiceRecord>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/push-to-xero`,
      { method: 'POST' },
    ),
  /**
   * Fetch the branded invoice PDF bytes (auth'd). Returns a Blob the
   * caller wraps in URL.createObjectURL for an inline <iframe> preview or
   * a download. Used by the logged-in client portal where the iframe
   * can't carry the bearer header itself.
   */
  pdfBlob: async (invoiceId: string): Promise<Blob> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'application/pdf' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(`/api/invoices/${encodeURIComponent(invoiceId)}/pdf`, {
        headers,
      });
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
};

export type AccountingSummary = {
  currency: string;
  outstanding: number;
  overdue: { amount: number; count: number };
  paid: number;
  draft: { amount: number; count: number };
  invoicedTotal: number;
  /** The live pipeline: the highest live option per open job. */
  quoted: { amount: number; count: number };
  /** Receivables before credit on account was netted off. */
  outstandingGross?: number;
  /** Credit notes, overpayments and prepayments Xero still holds for
   *  clients, and the Xero contacts that match no CRM client. */
  creditOnAccount?: {
    total: number;
    byClient: Array<{ clientId: string | null; client: string; amount: number; kinds: string[]; oldest: string | null }>;
    unlinkedContacts: Array<{ xeroContactId: string | null; contactName: string; credit: number; invoices: number }>;
  };
  /** The pipeline in full, so nothing silently disappears from `quoted`. */
  quotePipeline?: {
    live: { amount: number; count: number };
    options: { amount: number; count: number };
    lapsed: { amount: number; count: number };
    onClosedJobs: { amount: number; count: number };
    jobs: Array<{
      jobId: string;
      jobCode: string;
      jobTitle: string;
      clientId: string;
      client: string;
      amount: number;
      quoteId: string;
      version: number;
      name: string | null;
      optionsCount: number;
      otherOptions: Array<{ quoteId: string; name: string | null; version: number; amount: number }>;
      sharedAt: string;
      daysWaiting: number;
    }>;
  };
  aging: { current: number; d1_30: number; d31_60: number; d60plus: number };
};

export type AccountingClient = {
  clientId: string;
  clientName: string;
  billed: number;
  outstanding: number;
  paid: number;
  count: number;
  /** Credit still unallocated in Xero for this client. */
  credit?: number;
};

export type AccountingInvoice = {
  id: string;
  /** CRM = raised here (editable); XERO = lives only in Xero (read-only,
   *  job/client only when the Reference or contact matched). */
  source: 'CRM' | 'XERO';
  number: string;
  /** A Xero-raised invoice whose reference named no job and whose contact
   *  no client owns: for Nick to link or merge. */
  unlinked?: boolean;
  /** Changed in Xero after the push; the CRM lines are now Xero's. */
  editedInXero?: boolean;
  xeroEditedAt?: string | null;
  jobId: string | null;
  jobCode: string | null;
  jobTitle: string | null;
  clientId: string | null;
  clientName: string;
  status: InvoiceStatus;
  rawStatus: InvoiceStatus;
  subtotal: number;
  total: number;
  taxRate: number;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  xeroInvoiceId: string | null;
  xeroInvoiceNumber: string | null;
  xeroPushError: string | null;
  /** Xero mirror (null until the first sync after push). */
  xeroStatus: string | null;
  xeroTotal: number | null;
  xeroAmountDue: number | null;
  xeroAmountPaid: number | null;
  xeroAmountCredited: number | null;
  xeroSyncedAt: string | null;
  createdAt: string;
};

export type AccountingQuote = {
  id: string;
  jobId: string;
  jobCode: string;
  jobTitle: string;
  clientId: string;
  clientName: string;
  version: number;
  name: string | null;
  optionGroup: string | null;
  status: QuoteLifecycleStatus;
  statusReason: string | null;
  isLatest: boolean;
  declinedAt: string | null;
  subtotal: number;
  total: number;
  taxRate: number;
  sharedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
};

export const accounting = {
  summary: () => apiFetch<AccountingSummary>('/api/accounting/summary'),
  profitability: () => apiFetch<import('./types').ProfitabilityOverview>('/api/accounting/profitability'),
  byClient: () => apiFetch<AccountingClient[]>('/api/accounting/by-client'),
  invoices: (opts: { status?: string; q?: string; clientId?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.q) p.set('q', opts.q);
    if (opts.clientId) p.set('clientId', opts.clientId);
    const qs = p.toString();
    return apiFetch<AccountingInvoice[]>(
      `/api/accounting/invoices${qs ? `?${qs}` : ''}`,
    );
  },
  quotes: (opts: { status?: string; q?: string; clientId?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.q) p.set('q', opts.q);
    if (opts.clientId) p.set('clientId', opts.clientId);
    const qs = p.toString();
    return apiFetch<AccountingQuote[]>(
      `/api/accounting/quotes${qs ? `?${qs}` : ''}`,
    );
  },
  /** Mirror every pushed invoice from Xero now (status + amounts). */
  syncXeroPayments: () =>
    apiFetch<{ checked: number; changed: number; paid: number }>(
      '/api/accounting/xero/sync-payments',
      { method: 'POST' },
    ),
};

// --- Business Manager (owner-only BD analytics) ----------------------

export type BusinessTier = 'A' | 'B' | 'C';
export type BusinessDormancy = 'ACTIVE' | 'DORMANT_3M' | 'DORMANT_6M' | 'DORMANT_12M';

export interface BusinessClient {
  /** Stable row key: the CRM client id, or xero:<contact> when unlinked. */
  key: string;
  clientId: string | null;
  name: string;
  xeroContactIds: string[];
  /** Invoiced in the selected financial year. */
  revenue: number;
  /** Invoiced in the whole prior financial year. */
  priorRevenue: number;
  /** Invoices issued in the selected financial year. */
  invoiceCount: number;
  firstInvoicedAt: string | null;
  lastInvoicedAt: string | null;
  avgDaysToPayment: number | null;
  /** Owing today, whenever the invoice was raised. */
  outstanding: number;
  tier: BusinessTier;
  tierManual: boolean;
  dormancy: BusinessDormancy;
}

export type BusinessConcentrationLevel = 'HIGH' | 'MODERATE' | 'LOW';

export interface BusinessConcentration {
  topClientName: string | null;
  topClientSharePct: number | null;
  top5SharePct: number | null;
  level: BusinessConcentrationLevel;
}

export type BusinessChaseSeverity = 'OVERDUE' | 'SLOW' | 'WATCH';

export interface BusinessChaseRow {
  clientId: string | null;
  name: string;
  outstanding: number;
  avgDaysToPayment: number | null;
  dormancy: BusinessDormancy;
  severity: BusinessChaseSeverity;
}

export interface BusinessOverview {
  generatedAt: string;
  fy: { fy: number; label: string; start: string; end: string; current: boolean };
  /** Every FY the book touches (plus the current one), newest first. */
  fys: Array<{ fy: number; label: string; revenue: number }>;
  totalRevenue: number;
  prior: { label: string; revenue: number; toDate: number | null };
  invoiceCount: number;
  clientsBilled: number;
  outstanding: number;
  topClientSharePct: number | null;
  top5SharePct: number | null;
  concentration: BusinessConcentration;
  chaseList: BusinessChaseRow[];
  clients: BusinessClient[];
  /** The Xero mirror's last run (null = never synced). */
  xeroSync: { at: string; ok: boolean; error: string | null } | null;
}

export interface BusinessIngestResult {
  ranAt: string;
  windowStart: string;
  invoicesScanned: number;
  contactsSnapshotted: number;
  matchedToClients: number;
  countedRevenue: number;
  statusBreakdown: Record<string, { count: number; total: number }>;
}

export type BusinessOutreachKind = 'reengage' | 'chase';

export interface BusinessOutreachResult {
  draftId: string;
  gmailThreadId: string;
  to: string;
  subject: string;
  body: string;
}

/** Owner money snapshot from Xero's report endpoints (owner-only). */
export type XeroPulse =
  | { available: false; reason: 'not-configured' | 'scope' | 'error'; message: string }
  | {
      available: true;
      asOf: string;
      cachedAt?: string;
      fy: {
        start: string;
        end: string;
        netProfit: number | null;
        totalIncome: number | null;
        totalExpenses: number | null;
      };
      cash: { total: number; accounts: Array<{ name: string; balance: number }> };
      gst: { accountName: string; balance: number } | null;
    };

export const businessManager = {
  /** The client book for one financial year (fy = the year it ends in,
   *  e.g. 2027 for 1 Jul 2026 to 30 Jun 2027); default = current FY. */
  clients: (fy?: number) =>
    apiFetch<BusinessOverview>(`/api/business-manager/clients${fy ? `?fy=${fy}` : ''}`),
  xeroPulse: (refresh = false) =>
    apiFetch<XeroPulse>(
      `/api/business-manager/xero-pulse${refresh ? '?refresh=true' : ''}`,
    ),
  ingest: () =>
    apiFetch<BusinessIngestResult>('/api/business-manager/ingest', { method: 'POST' }),
  /** Draft a re-engagement or payment-chase email, saved as a Gmail draft. */
  outreach: (clientId: string, kind: BusinessOutreachKind) =>
    apiFetch<BusinessOutreachResult>(
      `/api/business-manager/clients/${clientId}/outreach`,
      { method: 'POST', body: { kind } },
    ),
};

export const preproductionExtras = {
  createTalent: (
    jobId: string,
    input: {
      character: string;
      name: string;
      status?: 'SHORTLISTED' | 'CONFIRMED' | 'REJECTED';
      profileLink?: string;
      notes?: string;
    },
  ) =>
    apiFetch<import('./types').PreproductionTalent>(
      `/api/jobs/${encodeURIComponent(jobId)}/preproduction/talent`,
      { method: 'POST', body: input },
    ),
  updateTalent: (
    id: string,
    patch: Partial<{
      character: string;
      name: string;
      status: 'SHORTLISTED' | 'CONFIRMED' | 'REJECTED';
      profileLink: string | null;
      notes: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionTalent>(
      `/api/preproduction/talent/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  deleteTalent: (id: string) =>
    apiFetch<void>(`/api/preproduction/talent/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  createDressing: (
    jobId: string,
    input: {
      kind: 'WARDROBE' | 'PROPS';
      frameId?: string | null;
      sceneDescription?: string;
      brief?: string;
    },
  ) =>
    apiFetch<import('./types').PreproductionDressing>(
      `/api/jobs/${encodeURIComponent(jobId)}/preproduction/dressings`,
      { method: 'POST', body: input },
    ),
  updateDressing: (
    id: string,
    patch: Partial<{
      kind: 'WARDROBE' | 'PROPS';
      frameId: string | null;
      sceneDescription: string | null;
      brief: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionDressing>(
      `/api/preproduction/dressings/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  deleteDressing: (id: string) =>
    apiFetch<void>(`/api/preproduction/dressings/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  createBudgetLine: (
    jobId: string,
    input: {
      name?: string | null;
      description: string;
      qty?: number;
      price?: number;
      discountPct?: number;
      discountAmt?: number;
      category?: string;
      quoteGroup?: string | null;
      xeroItemCode?: string | null;
      taxType?: TaxType;
    },
  ) =>
    apiFetch<import('./types').PreproductionBudgetLine>(
      `/api/jobs/${encodeURIComponent(jobId)}/preproduction/budget-lines`,
      { method: 'POST', body: input },
    ),
  updateBudgetLine: (
    id: string,
    patch: Partial<{
      name: string | null;
      description: string;
      qty: number;
      price: number;
      discountPct: number;
      discountAmt: number;
      category: string | null;
      quoteGroup: string | null;
      taxType: TaxType;
      xeroItemCode: string | null;
      orderIndex: number;
    }>,
  ) =>
    apiFetch<import('./types').PreproductionBudgetLine>(
      `/api/preproduction/budget-lines/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  deleteBudgetLine: (id: string) =>
    apiFetch<void>(
      `/api/preproduction/budget-lines/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  /** Drag-reorder: the job's COMPLETE line order in one atomic write.
   *  One request, not a PATCH per shifted line. */
  reorderBudgetLines: (jobId: string, orderedIds: string[]) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/preproduction/budget-lines/reorder`,
      { method: 'PATCH', body: { orderedIds } },
    ),
};

// ---------------------------------------------------------------------
// KG Playback
// ---------------------------------------------------------------------

export type PlaybackApproval = {
  id: string;
  versionId: string;
  audience: 'CLIENT' | 'KG';
  status: 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED';
  message: string | null;
  decidedByEmail: string | null;
  decidedAt: string | null;
};

/** Mirror of kilogram-app lib/playback-qc.ts QcReport (the parts the
 *  player renders). */
export type QcCheck = { code: string; level: 'pass' | 'warn' | 'fail'; message: string };
export type QcReport = {
  rev: number;
  status: 'PASS' | 'WARN' | 'FAIL';
  checks: QcCheck[];
  probe?: {
    width: number | null;
    height: number | null;
    fps: number | null;
    durationSec: number | null;
    hasAudio: boolean;
    integratedLufs: number | null;
  };
  specs?: string[];
};

export type PlaybackVersion = {
  id: string;
  assetId: string;
  versionNumber: number;
  /** Null for Mux-lane versions (A-Roll SaaS uploads). */
  dropboxPath: string | null;
  /** Mux ingest state (SaaS lane): 'PENDING' until the encode lands
   *  (chip shows processing), 'READY', or 'FAILED'. Null for Dropbox
   *  rows, whose media is ready the moment the row exists. */
  muxStatus?: string | null;
  /** Set when the retention sweep (or a delete) removed the Mux
   *  media; the version's history stays, the video is gone. */
  mediaExpiredAt?: string | null;
  mimeType: string;
  sizeBytes: number;
  durationMs: number | null;
  frameRate: number;
  label: string | null;
  uploadedAt: string;
  /** Selects flow-through stamps (all optional/nullable): when the
   *  picks last changed, when KG last exported them (the pair powers
   *  the "changed since your last export" badge), and who last
   *  dragged the reel into a custom order. */
  selectsChangedAt?: string | null;
  selectsExportedAt?: string | null;
  selectsArrangedBy?: string | null;
  selectsArrangedAt?: string | null;
  approvals: PlaybackApproval[];
  /** Set when the client clicked "Review complete · Send to
   *  Kilogram" on this version. Drives the banner state ("Awaiting
   *  review from {client}" → "Review submitted · Kilogram editing")
   *  and hides the submit button once stamped. */
  clientReviewSubmittedAt?: string | null;
  clientReviewSubmittedById?: string | null;
  /** Fast-start (web-optimised MP4) flag, stamped by the scanner's
   *  background check. true = moov atom at start (browser plays
   *  instantly), false = mdat first (browser has to download whole
   *  file before playback - shows a re-export warning in the UI),
   *  null = check pending / unparseable / not an MP4 (no UI noise). */
  webOptimized?: boolean | null;
  /** Streaming-proxy state. 'READY' means the player streams a lean
   *  faststart proxy, so a false webOptimized describes a file nobody
   *  is streaming any more (the warning stays hidden). */
  proxyStatus?: string | null;
  /** Poster frame on R2 (playback-proxy worker), null on rows made
   *  before the move until the boot backfill reaches them. */
  posterUrl?: string | null;
  /** Auto-QC gate (KG-side only): PASS | WARN | FAIL once the
   *  playback-proxy worker has probed the source against the job's
   *  deliverables and scanned for black frames / silence / loudness.
   *  Null until it has looked. Advisory: drives the QC banner. */
  qcStatus?: 'PASS' | 'WARN' | 'FAIL' | null;
  qcReport?: QcReport | null;
  qcCheckedAt?: string | null;
  /** Prisma `_count` aggregate: number of ReviewComment rows on
   *  this version. Lets V chips show "V2 · 5 notes" without having
   *  to load every version's comments client-side. */
  _count?: { comments: number };
  /** Internal-only draft (auto-edit pipeline): never client-facing.
   *  The backend filters internal versions out of BOTH portal paths
   *  (client JWT + magic-link share token), so only KG-side responses
   *  ever carry `internal: true`. The owner promotes it to a client
   *  draft or discards it from the version reel. */
  internal?: boolean;
};

/** One file in the {job}/7-EXPORTS/FINAL/ folder, as returned by
 *  the /jobs/:id/exports/finals listing. */
export type FinalExportFile = {
  name: string;
  /** Subfolder inside FINAL/ (e.g. "JC_LIVE_16.9"), null for root
   *  files. Drives the grouped sections in the FinalExports grid. */
  group?: string | null;
  size: number;
  mimeType: string;
  isVideo: boolean;
  dropboxPath: string;
  /** Derivatives on R2, null until the worker has been through. */
  posterUrl?: string | null;
  proxyUrl?: string | null;
  mediaStatus?: string | null;
  /** Shadow JobAsset id (FINAL_EXPORT) a post can attach; null until made. */
  assetId?: string | null;
};

export type PlaybackAsset = {
  /** Selects (the "use this bit" reel) are opt-in per cut, switched
   *  on from the studio side. Off = no selects UI anywhere. */
  selectsEnabled?: boolean;
  /** When the studio sent this cut to the client. In a PictureUp
   *  workspace null = private. */
  clientSentAt?: string | null;
  id: string;
  jobId: string;
  name: string;
  slug: string;
  archivedAt: string | null;
  createdAt: string;
  versions: PlaybackVersion[];
};

/** What a Drafts-folder scan found. `blocked` and `ignoredRootFiles`
 *  are the two ways a cut can sit in Dropbox and never appear in the
 *  reel; the tab surfaces both so the fix is one sentence away. */
export type PlaybackScanResult = {
  added: number;
  skipped: number;
  replaced: number;
  throttled?: boolean;
  /** Tracked files whose bytes changed but whose version already has
   *  notes or a decision, so the reviewed cut was kept. */
  blocked: Array<{ versionNumber: number; path: string; name: string }>;
  /** Video files at the Drafts root, ignored because the job uses
   *  per-cut subfolders. */
  ignoredRootFiles: string[];
  /** New files still syncing to Dropbox; imported by a later sweep
   *  once their latest revision is a few minutes old. */
  pending?: string[];
};

export type PlaybackComment = {
  id: string;
  versionId: string;
  /** Self-FK for thread replies. `null` for top-level comments, or
   *  the parent's id for a reply. Replies inherit the parent's
   *  timecodeMs at insert time. */
  parentId?: string | null;
  timecodeMs: number;
  /** Optional out-point (ms) for a RANGE comment. Null/absent = a
   *  single-frame point comment. When set, the comment spans
   *  [timecodeMs, endMs] and renders as a bar on the scrubber. */
  endMs?: number | null;
  author: string;
  authorUserId: string | null;
  audience: 'CLIENT' | 'KG';
  body: string;
  /** KG-side internal note: visible to the team only, hidden from
   *  share-token clients. Older records may not have this field set;
   *  treat absent as `false`. */
  internal?: boolean;
  resolvedAt: string | null;
  resolvedById?: string | null;
  /** Emoji reactions rolled up per emoji. `reacted` = whether the
   *  current (logged-in) user reacted. Absent/empty = no reactions. */
  reactions?: Array<{ emoji: string; count: number; reacted: boolean }>;
  /** Optional freehand drawing pinned to the comment's frame. */
  drawing?: CommentDrawing | null;
  /** Files pinned to the comment (reference images, heading docs).
   *  Metadata only — bytes stream via the attachment endpoints. */
  attachments?: PlaybackCommentAttachment[];
  createdAt: string;
};

export type PlaybackCommentAttachment = {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
};

/** A range SELECT on a review version: "use this bit" as a work
 *  product (the rushes-stringout flow), distinct from a range
 *  comment, which stays conversation. Always a real span. */
export type PlaybackSelect = {
  id: string;
  versionId: string;
  inMs: number;
  outMs: number;
  /** Optional caption ("hero take"). Becomes the clip name on export. */
  label: string | null;
  author: string;
  authorUserId: string | null;
  guestKey?: string | null;
  audience: 'CLIENT' | 'KG';
  sortOrder: number;
  createdAt: string;
};

/** Scrub-preview filmstrip for a long cut: one tiled JPEG + layout.
 *  The player shows the frame under the cursor while dragging. */
export type PlaybackSpriteInfo = {
  url: string;
  meta: {
    intervalSec: number;
    cols: number;
    rows: number;
    count: number;
    frameW: number;
    frameH: number;
  };
};

/** A freehand annotation pinned to a comment's frame. `points` is a flat
 *  [x0,y0,x1,y1,...] list normalised 0..1 to the video frame, so it
 *  scales to any player size. */
export type CommentStroke = { color: string; width: number; points: number[] };
export type CommentDrawing = { strokes: CommentStroke[] };

export const playback = {
  listAssets: (jobId: string) =>
    apiFetch<PlaybackAsset[]>(`/api/jobs/${encodeURIComponent(jobId)}/playback/assets`),
  scan: (jobId: string, force = false) =>
    apiFetch<PlaybackScanResult>(
      `/api/jobs/${encodeURIComponent(jobId)}/playback/scan${force ? '?force=1' : ''}`,
      { method: 'POST' },
    ),
  createAsset: (jobId: string, name: string) =>
    apiFetch<PlaybackAsset>(`/api/jobs/${encodeURIComponent(jobId)}/playback/assets`, {
      method: 'POST',
      body: { name },
    }),
  renameAsset: (assetId: string, name: string) =>
    apiFetch<PlaybackAsset>(`/api/playback/assets/${encodeURIComponent(assetId)}`, {
      method: 'PATCH',
      body: { name },
    }),
  /** Per-cut selects switch (KG-side): most jobs never need the reel;
   *  rushes jobs do. */
  setAssetSelects: (assetId: string, selectsEnabled: boolean) =>
    apiFetch<PlaybackAsset>(`/api/playback/assets/${encodeURIComponent(assetId)}`, {
      method: 'PATCH',
      body: { selectsEnabled },
    }),
  archiveAsset: (assetId: string) =>
    apiFetch<PlaybackAsset>(`/api/playback/assets/${encodeURIComponent(assetId)}`, {
      method: 'DELETE',
    }),
  uploadVersion: async (
    jobId: string,
    file: File,
    opts: { assetId?: string; label?: string; frameRate?: number } = {},
  ): Promise<{ asset: PlaybackAsset; version: PlaybackVersion }> => {
    const extra: Record<string, string> = {};
    if (opts.assetId) extra['X-Asset-Id'] = opts.assetId;
    if (opts.label) extra['X-Version-Label'] = opts.label;
    if (opts.frameRate) extra['X-Frame-Rate'] = String(opts.frameRate);
    return uploadBinary<{ asset: PlaybackAsset; version: PlaybackVersion }>(
      `/api/jobs/${encodeURIComponent(jobId)}/playback/versions/upload`,
      file,
      extra,
    );
  },
  deleteVersion: (versionId: string) =>
    apiFetch<void>(`/api/playback/versions/${encodeURIComponent(versionId)}`, {
      method: 'DELETE',
    }),
  /** Set / clear the human label on a version ("colour pass",
   *  "small fixes to v2"). The V-number is the spine and never
   *  changes; the label is where nuance goes. Team-only server-side. */
  updateVersionLabel: (versionId: string, label: string | null) =>
    apiFetch<PlaybackVersion>(`/api/playback/versions/${encodeURIComponent(versionId)}`, {
      method: 'PATCH',
      body: { label },
    }),
  /** Move a version to another cut on the same job (Frame.io-style
   *  "Move to…"). Re-points the row so comments + approvals ride
   *  along, and shifts the Dropbox file into the target cut's
   *  folder. Exactly one of targetAssetId / newAssetName. */
  moveVersion: (
    versionId: string,
    target: { targetAssetId?: string; newAssetName?: string },
  ) =>
    apiFetch<{
      version: PlaybackVersion;
      targetAsset: { id: string; name: string };
    }>(`/api/playback/versions/${encodeURIComponent(versionId)}/move`, {
      method: 'POST',
      body: target,
    }),
  /** Owner-only: promote an internal draft to a client-visible
   *  version (flips `internal` false + mints the next client
   *  V-number). 403 for non-owner team members. */
  promoteVersion: (versionId: string) =>
    apiFetch<PlaybackVersion>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/promote`,
      { method: 'POST' },
    ),
  /** Owner-only: discard an internal draft (soft-delete + Dropbox
   *  source cleanup). Refuses client-visible versions server-side. */
  discardVersion: (versionId: string) =>
    apiFetch<void>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/discard`,
      { method: 'POST' },
    ),
  streamUrl: (versionId: string) =>
    apiFetch<{ url: string; hlsUrl?: string | null }>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/stream`,
    ),
  /** Full-quality original for a SIGNED-IN viewer. The share-link path
   *  has its own equivalent; this is the one a logged-in client uses. */
  downloadUrl: (versionId: string) =>
    apiFetch<{ url: string; filename: string }>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/download`,
    ),
  /** Mux lane (A-Roll SaaS): mint a direct browser upload for a
   *  rendered review cut. PUT the file to uploadUrl with UpChunk;
   *  the version row shows muxStatus PENDING until Mux encodes. */
  createUpload: (
    jobId: string,
    input: {
      assetId?: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      label?: string | null;
      sourceStartTc?: string | null;
    },
  ) =>
    apiFetch<{ versionId: string; assetId: string; uploadUrl: string }>(
      `/api/jobs/${encodeURIComponent(jobId)}/playback/uploads`,
      // apiFetch stringifies `body` itself — pass the object raw.
      { method: 'POST', body: input },
    ),
  /**
   * List the files in {job}/7-EXPORTS/FINAL/. Each entry carries
   * the Dropbox path so callers can pass it back to the thumbnail
   * + stream endpoints below.
   */
  finalExports: (jobId: string) =>
    apiFetch<{ files: FinalExportFile[] }>(
      `/api/jobs/${encodeURIComponent(jobId)}/exports/finals`,
    ),
  /**
   * JPEG thumbnail for a file in the FINAL folder. Same Dropbox-
   * native thumbnail flow as version thumbnails; returns null when
   * Dropbox can't generate one (small file, unsupported codec,
   * non-media type).
   */
  finalExportThumbnail: async (
    jobId: string,
    dropboxPath: string,
  ): Promise<Blob | null> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'image/jpeg' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/exports/finals/thumbnail?path=${encodeURIComponent(dropboxPath)}`,
        { headers },
      );
    };
    let res = await fetchThumbnailGated(send);
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await fetchThumbnailGated(send);
    }
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /**
   * Dropbox temporary stream URL for a file in the FINAL folder.
   * Used by the hover-video preview on the file cards. Cached for
   * the component's lifetime so repeat hovers don't re-roundtrip.
   */
  finalExportStreamUrl: (jobId: string, dropboxPath: string) =>
    apiFetch<{ url: string }>(
      `/api/jobs/${encodeURIComponent(jobId)}/exports/finals/stream?path=${encodeURIComponent(dropboxPath)}`,
    ),
  /**
   * Dropbox shared link for a subfolder of FINAL/ (or the FINAL root).
   * The caller appends `dl=1` so Dropbox serves the whole folder as
   * one zip - the portal's per-folder Download button.
   */
  finalExportFolderLink: (jobId: string, folderPath: string) =>
    apiFetch<{ url: string }>(
      `/api/jobs/${encodeURIComponent(jobId)}/exports/finals/folder-link?path=${encodeURIComponent(folderPath)}`,
    ),

  /**
   * Fetch a Dropbox-generated JPEG thumb for a version. Returns a
   * Blob the caller wraps in URL.createObjectURL for an <img src>.
   * Resolves null when Dropbox can't generate one (small file,
   * unsupported codec) so the UI shows a placeholder instead of
   * a broken image.
   */
  thumbnail: async (versionId: string): Promise<Blob | null> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'image/jpeg' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/playback/versions/${encodeURIComponent(versionId)}/thumbnail`,
        { headers },
      );
    };
    let res = await fetchThumbnailGated(send);
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await fetchThumbnailGated(send);
    }
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  recordDuration: (versionId: string, durationMs: number) =>
    apiFetch<void>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/duration`,
      { method: 'POST', body: { durationMs } },
    ),
  listComments: (versionId: string) =>
    apiFetch<PlaybackComment[]>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/comments`,
    ),
  /** Scrub-preview filmstrip (long cuts). Null when none exists. */
  spriteInfo: (versionId: string) =>
    apiFetch<PlaybackSpriteInfo | null>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/sprite`,
    ),
  postComment: (
    versionId: string,
    body: string,
    timecodeMs: number,
    author?: string,
    internal?: boolean,
    parentId?: string,
    endMs?: number | null,
    drawing?: CommentDrawing | null,
    attachmentIds?: string[],
  ) =>
    apiFetch<PlaybackComment>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/comments`,
      {
        method: 'POST',
        body: { body, timecodeMs, author, internal, parentId, endMs, drawing, attachmentIds },
      },
    ),
  deleteComment: (commentId: string) =>
    apiFetch<void>(
      `/api/playback/comments/${encodeURIComponent(commentId)}`,
      { method: 'DELETE' },
    ),
  /** Range selects ("use this bit" as a work product). */
  listSelects: (versionId: string) =>
    apiFetch<PlaybackSelect[]>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/selects`,
    ),
  createSelect: (
    versionId: string,
    inMs: number,
    outMs: number,
    label?: string | null,
    author?: string,
  ) =>
    apiFetch<PlaybackSelect>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/selects`,
      { method: 'POST', body: { inMs, outMs, label, author } },
    ),
  updateSelect: (
    selectId: string,
    patch: { inMs?: number; outMs?: number; label?: string | null },
  ) =>
    apiFetch<PlaybackSelect>(
      `/api/playback/selects/${encodeURIComponent(selectId)}`,
      { method: 'PATCH', body: patch },
    ),
  /** Reorder the version's selects (Arrange drawer). Full id list in
   *  the new order; returns the re-sorted rows. `author` feeds the
   *  drawer's "Arranged by …" line. */
  reorderSelects: (versionId: string, order: string[], author?: string) =>
    apiFetch<PlaybackSelect[]>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/selects/reorder`,
      { method: 'POST', body: { order, author } },
    ),
  deleteSelect: (selectId: string) =>
    apiFetch<void>(
      `/api/playback/selects/${encodeURIComponent(selectId)}`,
      { method: 'DELETE' },
    ),
  /** KG-only: promote a range comment's span to a select. */
  promoteCommentToSelect: (commentId: string) =>
    apiFetch<PlaybackSelect>(
      `/api/playback/comments/${encodeURIComponent(commentId)}/promote-select`,
      { method: 'POST' },
    ),
  /** Upload one composer attachment (pending until a comment claims it). */
  uploadCommentAttachment: (versionId: string, file: File) =>
    uploadBinary<PlaybackCommentAttachment>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/attachments`,
      file,
    ),
  /** Remove a pending attachment (composer × before posting). */
  deleteCommentAttachment: (attachmentId: string) =>
    apiFetch<void>(
      `/api/playback/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),
  /** Fetch an attachment's bytes as a Blob (same auth dance as
   *  `thumbnail`: bearer header + one refresh retry). */
  commentAttachmentBlob: async (attachmentId: string): Promise<Blob> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = {};
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/playback/attachments/${encodeURIComponent(attachmentId)}/stream`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** Toggle the resolved state on a comment. KG-only. */
  resolveComment: (commentId: string, resolved: boolean) =>
    apiFetch<PlaybackComment>(
      `/api/playback/comments/${encodeURIComponent(commentId)}/resolve`,
      { method: 'POST', body: { resolved } },
    ),
  /** Toggle an emoji reaction on a comment for the current user. */
  reactComment: (commentId: string, emoji: string) =>
    apiFetch<PlaybackComment>(
      `/api/playback/comments/${encodeURIComponent(commentId)}/reactions`,
      { method: 'POST', body: { emoji } },
    ),
  decide: (
    versionId: string,
    status: 'APPROVED' | 'CHANGES_REQUESTED',
    message?: string,
  ) =>
    apiFetch<PlaybackApproval>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/decision`,
      { method: 'POST', body: { status, message } },
    ),
  /** Client signals they've finished their review pass on this
   *  version. KG team gets a notification. CLIENT role only. */
  submitReview: (versionId: string) =>
    apiFetch<void>(
      `/api/playback/versions/${encodeURIComponent(versionId)}/submit-review`,
      { method: 'POST' },
    ),
  /**
   * Download the EDL file as a Blob so the browser triggers a "save
   * as" prompt. apiFetch can't return non-JSON, so we hit fetch
   * directly with the bearer token and turn the body into a Blob.
   */
  /**
   * Download this version + its notes for an NLE. `resolve` is the EDL
   * the old Download-EDL button served; `premiere` and `fcpx` are the
   * XML flavours, which carry a range note's SPAN natively rather than
   * leaning on event length the way an EDL has to.
   *
   * apiFetch can't return non-JSON, so this hits fetch directly with
   * the bearer token and turns the body into a Blob for a save-as.
   */
  downloadNleExport: async (
    versionId: string,
    format: 'resolve' | 'premiere' | 'fcpx',
  ): Promise<void> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'text/plain' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/playback/versions/${encodeURIComponent(versionId)}/export?format=${format}`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') ?? '';
    const fallback =
      format === 'premiere' ? 'notes.xml' : format === 'fcpx' ? 'notes.fcpxml' : 'notes.edl';
    const fname = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  downloadMarkersEdl: async (versionId: string): Promise<void> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'text/plain' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/playback/versions/${encodeURIComponent(versionId)}/markers.edl`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') ?? '';
    const fname = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'markers.edl';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  /**
   * Download the version's SELECTS as a timeline for an NLE — the
   * "use this bit" reel, conformed against the draft render (phase 3's
   * timeline map upgrades this to the original rushes). Same blob +
   * save-as dance as downloadNleExport.
   */
  downloadSelectsExport: async (
    versionId: string,
    format: 'resolve' | 'premiere' | 'fcpx',
  ): Promise<void> => {
    const send = async () => {
      const tokens = getTokens();
      const headers: Record<string, string> = { Accept: 'text/plain' };
      if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
      return fetch(
        `/api/playback/versions/${encodeURIComponent(versionId)}/selects/export?format=${format}`,
        { headers },
      );
    };
    let res = await send();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) res = await send();
    }
    if (!res.ok) throw new ApiClientError(res.status, null);
    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') ?? '';
    const fallback =
      format === 'premiere' ? 'selects.xml' : format === 'fcpx' ? 'selects.fcpxml' : 'selects.edl';
    const fname = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ---------------------------------------------------------------------
// Edit requests ("Make changes" — auto-edit pipeline, phase 4).
// OWNER-ONLY end to end: the backend 403s everyone but Nick, and the
// KG Playback UI only wires these when user.isOwner. Never surfaced on
// any client portal path.
// ---------------------------------------------------------------------

export type EditRequestStatus =
  | 'QUEUED'
  | 'PLANNING'
  | 'AWAITING_ANSWERS'
  | 'READY'
  | 'RUNNING'
  | 'DONE'
  | 'FAILED'
  | 'CANCELLED';

export type EditRequestSource = 'FEEDBACK_ROUND' | 'NOTE' | 'VOICE_SHORTCUT';

export type EditRequestQuestion = {
  id: string;
  question: string;
  options: string[];
};

export type EditRequestAnswer = { id: string; answer: string };

export type PlaybackEditRequest = {
  id: string;
  jobId: string;
  versionId: string | null;
  notes: string;
  source: EditRequestSource;
  status: EditRequestStatus;
  questions: EditRequestQuestion[] | null;
  answers: EditRequestAnswer[] | null;
  error: string | null;
  /** The internal ReviewVersion the daemon produced (status DONE). */
  resultVersionId: string | null;
  createdAt: string;
  updatedAt: string;
};

export const editRequests = {
  /** Every edit request on the job (any version / job-level notes). */
  listForJob: (jobId: string) =>
    apiFetch<PlaybackEditRequest[]>(
      `/api/jobs/${encodeURIComponent(jobId)}/edit-requests`,
    ),
  listForVersion: (jobId: string, versionId: string) =>
    apiFetch<PlaybackEditRequest[]>(
      `/api/jobs/${encodeURIComponent(jobId)}/edit-requests?versionId=${encodeURIComponent(versionId)}`,
    ),
  create: (
    jobId: string,
    body: { notes: string; versionId?: string; source?: 'FEEDBACK_ROUND' | 'NOTE' },
  ) =>
    apiFetch<PlaybackEditRequest>(
      `/api/jobs/${encodeURIComponent(jobId)}/edit-requests`,
      { method: 'POST', body },
    ),
  answer: (id: string, answers: EditRequestAnswer[]) =>
    apiFetch<PlaybackEditRequest>(
      `/api/edit-requests/${encodeURIComponent(id)}/answers`,
      { method: 'POST', body: { answers } },
    ),
  cancel: (id: string) =>
    apiFetch<PlaybackEditRequest>(
      `/api/edit-requests/${encodeURIComponent(id)}/cancel`,
      { method: 'POST' },
    ),
  /** Re-queue a FAILED request — stored answers are reused (→ READY
   *  with answers, → QUEUED without), so Nick never re-types them. */
  retry: (id: string) =>
    apiFetch<PlaybackEditRequest>(
      `/api/edit-requests/${encodeURIComponent(id)}/retry`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------
// A-Roll
// ---------------------------------------------------------------------

export type ARollSegmentKind = 'WORD' | 'SENTENCE' | 'FILLER';
export type ARollStatus = 'PENDING' | 'TRANSCRIBING' | 'READY' | 'FAILED';

export type ARollProjectRow = {
  id: string;
  name: string;
  folder: string;
  sortOrder: number;
  assetCount: number;
};

export type ARollApproval = {
  /** ISO timestamp the client signed off, or null while pending. */
  approvedAt: string | null;
  approvedById: string | null;
  approvedByName: string | null;
};
export type ARollAssetList = {
  projects: ARollProjectRow[];
  assets: ARollAssetRow[];
  approval: ARollApproval;
  /** Studio only: cuts with rounds is on for this job. Absent = off. */
  roundsEnabled?: boolean;
};

/** What the owner's "Rounds (beta)" switch did. */
export type ARollRoundsSwitch = {
  enabled: boolean;
  summary: {
    transcripts: number;
    cuts: number;
    rounds: number;
    studioOnly: number;
    folded: number;
    alreadyDone: number;
  };
};

export type ARollAssetRow = {
  id: string;
  jobId: string;
  projectId: string | null;
  dropboxPath: string;
  filename: string;
  sizeBytes: number | null;
  durationMs: number | null;
  language: string | null;
  webOptimized: boolean | null;
  /** Mux playback id (SaaS upload ingest). Set as soon as playback is
   *  ready — i.e. during TRANSCRIBING — which is what lets the
   *  transcribing stage show the clip's washed storyboard. */
  muxPlaybackId: string | null;
  status: ARollStatus;
  errorMessage: string | null;
  uploadedById: string | null;
  uploadedBy: { id: string; name: string } | null;
  transcribedAt: string | null;
  /** Retention: set when the plan window closed and the VIDEO was deleted.
   *  Transcript, drafts and exports still work — only playback is gone. */
  mediaExpiredAt: string | null;
  createdAt: string;
  updatedAt: string;
  _count: { segments: number; highlights: number };
};

export type ARollSegment = {
  id: string;
  assetId: string;
  startMs: number;
  endMs: number;
  text: string;
  kind: ARollSegmentKind;
  speaker: string | null;
};

export type ARollHighlight = {
  id: string;
  assetId: string;
  startMs: number;
  endMs: number;
  label: string | null;
  createdById: string | null;
  // role threaded through so the UI can colour-code KG team picks
  // (yellow) vs client picks (green) without an extra lookup.
  createdBy: { id: string; name: string; role: 'ADMIN' | 'EDITOR' | 'CLIENT' } | null;
  createdAt: string;
  /** Last edit (trim / reorder / label) — feeds the change-tracking
   *  tooltip on a client fork. */
  updatedAt: string;
  sortOrder: number;
  /** Which clip in the KG cut this one was copied from when the client
   *  forked. Null on KG clips, manual bookmarks, clips the client
   *  ADDED, and forks taken before lineage existed (the diff falls
   *  back to bounds-matching for those). */
  forkedFromHighlightId: string | null;
  /** Rounds: on a client version copied from a round, the snapshot clip it
   *  came from (kept even after the studio deletes that clip). */
  forkedFromSnapshotClipId?: string | null;
  /** The pick's position when its AI draft was first generated — our
   *  suggested cut. `sortOrder` gets overwritten by drag-reorder; this
   *  immutable snapshot is what "Reset to our suggested order" restores.
   *  Null for manual picks and pre-existing (un-snapshotted) drafts. */
  suggestedOrder: number | null;
  /** Null = manual bookmark (the "Manual" tab in the workspace).
   *  When set, the highlight belongs to an AI-generated draft and
   *  only renders when that draft's tab is active. */
  draftId: string | null;
  /** Near-duplicate takes the dedupe pass discarded in favour of this
   *  clip — offered on the card as a take swap. Null/absent = none. */
  alternates: ARollAlternate[] | null;
  /** Boundary-pass flags (INCOMPLETE_CLAUSE, BOUNDARY_MOVED,
   *  OVERLAP_TRIMMED) — the card shows a "check" chip when present. */
  reviewFlags: string[] | null;
};

export type ARollAlternate = {
  startMs: number;
  endMs: number;
  label: string;
  reason: string;
};

/** Generation mode for an AI draft. BRIEF = contiguous picks in
 *  transcript order. SHUFFLE = Claude is free to re-order selections
 *  to build the strongest narrative arc, no duration cap. */
export type ARollDraftKind = 'BRIEF' | 'SHUFFLE' | 'CLEAN' | 'TIGHTEN' | 'STORY';

/** Structured brief-builder fields the editor can supply alongside
 *  (or instead of) the free-text brief. All optional — the backend
 *  composes them into Claude's system prompt as hard directives.
 *  Mirrors Plotline's pre-generation questionnaire approach:
 *  structured choices yield sharper cuts than open prompts. */
/** One proposed story spine from the pre-generation options pass. */
export type ARollStorySpine = {
  title: string;
  logline: string;
  opensWith: string;
  middle: string[];
  closesWith: string;
};

export type ARollBriefBuilder = {
  targetSeconds?: number;
  /** Chosen spine from the options pass — hard structural directive. */
  storyline?: ARollStorySpine;
  leadWith?: string;
  closeOn?: string;
  avoid?: string;
  tones?: string[];
  skipIntros?: boolean;
  /** TIGHTEN mode only: how aggressively to frankenbite the messy footage
   *  down to the key message. Defaults to 'aggressive' server-side. */
  tightness?: 'clean' | 'light' | 'standard' | 'aggressive';
  /** STORY mode: key lines/themes the cut must guarantee. */
  mustInclude?: string;
  /** STORY mode: insist every selected voice appears in the cut. */
  requireAllSpeakers?: boolean;
  /** STORY mode: editor-drawn emotional arc — five intensity stops
   *  (open → early → middle → late → close), each 0..1. Omitted when
   *  the editor leaves the curve untouched. */
  arc?: number[];
};

export type ARollDraft = {
  id: string;
  assetId: string;
  /** Multi-interview cuts: the other interviews this cut draws on. */
  sourceAssetIds?: string[];
  name: string;
  brief: string;
  kind: ARollDraftKind;
  durationMs: number | null;
  /** The AI's one-line account of the cut it made (duration-tagged).
   *  Null on drafts generated before 2026-07-22. */
  summary?: string | null;
  /** 2-3 content-grounded alternate angles the AI saw but didn't use —
   *  the Refine modal's one-tap suggestions. Null on older drafts. */
  refineIdeas?: string[] | null;
  /** True on a CLIENT VERSION: the client's own version of a studio draft,
   *  made with "Start your version". Clients may only edit their own. */
  isClientDraft?: boolean;
  /** Which studio draft this client version was made from. Null on studio drafts, and on
   *  client forks made before 2026-08-02 (there was one per asset then).
   *  This is what pairs a client version to its parent so it can live
   *  INSIDE that cut's tab instead of adding one to the strip. */
  forkedFromDraftId?: string | null;
  /** Whether the client can see this cut on the share link. KG-set, and
   *  several cuts can be visible at once — the client gets a tab each. */
  clientVisible?: boolean;
  /** When the studio last sent it (set with clientVisible). */
  clientSentAt?: string | null;
  /** The client's sign-off on this draft, and who gave it. */
  clientApprovedAt?: string | null;
  clientApprovedByName?: string | null;
  /** On a client version: when they last sent it to the studio, and who
   *  (as they named themselves on the link). */
  clientSubmittedAt?: string | null;
  clientSubmittedByName?: string | null;
  /** On a studio draft: the studio's last answer to the client on it
   *  (shown to the client), and when and who: their version accepted or
   *  dismissed, or the studio reopened the draft after they approved it. */
  clientVersionOutcome?: 'ACCEPTED' | 'DISMISSED' | 'REOPENED' | null;
  clientVersionResolvedAt?: string | null;
  clientVersionResolvedByName?: string | null;
  /** On a client version: the draft it was made on, whether that draft is
   *  still out with the client, and a newer draft sent since (if any). */
  versionOf?: {
    draftId: string | null;
    draftName: string;
    parentSent: boolean;
    parentGone: boolean;
    newerDraft: { id: string; name: string } | null;
  } | null;
  /** Cuts with rounds (only present when the project has rounds on). */
  /** The tab name ("Hero", "Cut downs"); null = `name`. */
  cutLabel?: string | null;
  cutKind?: 'HERO' | 'CUT_DOWNS' | 'CUSTOM' | null;
  /** The newest round the client was sent, and the one they approved. */
  latestRoundNumber?: number | null;
  approvedRoundNumber?: number | null;
  /** On a client version: the round of the cut it was made on. */
  roundNumber?: number | null;
  /** Round history, newest first (studio-only history only for the studio). */
  rounds?: ARollRoundSummary[];
  /** On a client version (rounds on): the round it was made on, as clips
   *  and dividers, for the "As sent" compare. */
  madeOnRound?: { highlights: ARollHighlight[]; chapterMarkers: ARollChapterMarker[] } | null;
  /** Generate again's one slot: what the Undo button does now. */
  undoAction?: 'undo' | 'redo' | null;
  /** Single-editor lock (only meaningful on the client fork). Null when free. */
  lockedBySession?: string | null;
  lockedByName?: string | null;
  lockedAt?: string | null;
  lockExpiresAt?: string | null;
  createdById: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
};

/** One round of a cut: the cut exactly as it was sent (rounds on). */
export type ARollRoundSummary = {
  id: string;
  /** Null on studio-only history (an old draft that was never sent). */
  number: number | null;
  studioOnly: boolean;
  sentAt: string;
  sentByName: string | null;
  note: string | null;
  clientApprovedAt: string | null;
  clientApprovedByName: string | null;
  clientVersionOutcome: string | null;
  clientVersionResolvedAt: string | null;
  clientVersionResolvedByName: string | null;
  durationMs: number | null;
  clipCount: number;
};

/** A round opened read-only: the summary plus the frozen clips. */
export type ARollRoundDetail = {
  id: string;
  draftId: string;
  assetId: string;
  cutName: string;
  number: number | null;
  studioOnly: boolean;
  sentAt: string;
  sentByName: string | null;
  note: string | null;
  clientApprovedAt: string | null;
  clientApprovedByName: string | null;
  clientVersionOutcome: string | null;
  snapshot: {
    v: 1;
    durationMs: number | null;
    highlights: Array<{
      id: string;
      assetId: string;
      startMs: number;
      endMs: number;
      label: string | null;
      sortOrder: number;
    }>;
    chapterMarkers: Array<{ id: string; sortOrder: number; title: string }>;
  };
};

/** Response from the lock endpoints — the public lock state + whether the
 *  caller's session currently holds it. */
export type ARollDraftLock = {
  lockedBySession: string | null;
  lockedByName: string | null;
  lockedAt: string | null;
  lockExpiresAt: string | null;
  held: boolean;
  lockable: boolean;
};

export type ARollNote = {
  id: string;
  assetId: string;
  timecodeMs: number;
  body: string;
  /** When set, this note is attached to a cut clip (ARollHighlight) rather
   *  than floating on the transcript timeline. */
  highlightId: string | null;
  createdById: string | null;
  createdBy: { id: string; name: string; role: 'ADMIN' | 'EDITOR' | 'CLIENT' } | null;
  createdAt: string;
  updatedAt: string;
};

/** A saved chapter divider in the cut. sortOrder = the clip index it sits
 *  before (0 = before the first clip). Stays put while clips reorder. */
export type ARollChapterMarker = {
  id: string;
  assetId: string;
  draftId: string | null;
  sortOrder: number;
  title: string;
  createdById: string | null;
  createdBy: { id: string; name: string; role: 'ADMIN' | 'EDITOR' | 'CLIENT' } | null;
  createdAt: string;
  updatedAt: string;
};

/** Editor-assigned identity for a diarized speaker. name/role/context are
 *  display-only (the filter keeps using the raw `speaker` label); isVoiceover
 *  marks the narration spine. Two labels sharing a name = one merged person. */
export type ARollSpeaker = {
  id: string;
  assetId: string;
  speaker: string;
  name: string | null;
  role: string | null;
  context: string | null;
  isVoiceover: boolean;
  /** The hero: the dominant voice the cut is built around (max one). */
  isHero: boolean;
};

export type ARollAssetDetail = ARollAssetRow & {
  /** Other interview files that multi-interview cuts on this asset draw
   *  on, each with the words inside its clips (for the cut cards). */
  sources?: Array<{ id: string; filename: string; durationMs: number | null; words: ARollSegment[] }>;
  muxImageTokens?: { thumbnail: string; gif: string; storyboard: string } | null;
  job: { id: string; code: string; title: string };
  segments: ARollSegment[];
  highlights: ARollHighlight[];
  notes: ARollNote[];
  chapterMarkers: ARollChapterMarker[];
  drafts: ARollDraft[];
  /** Who the client is, for "{Client}'s version" on the studio side. */
  clientName?: string;
  speakers: ARollSpeaker[];
  /** Client-mirror view KG set up: which draft is active (null = Manual),
   *  the hero speaker filtered to (null = all), and whether the selections
   *  Filter is on. The client portal opens on this. */
  viewDraftId: string | null;
  viewSpeaker: string | null;
  viewSelections: boolean;
  /** Background music bed played UNDER the cut. `musicUrl` is derived
   *  server-side from the Mux playback id, so it's null until the
   *  encode lands; `musicEnabled` is KG's on/off choice, mirrored to
   *  the client exactly like viewSelections. */
  musicUrl: string | null;
  /** Whether clients may use AI tools on this project (the workspace
   *  owner's switch). The client UI hides AI when false; the API refuses
   *  it either way. */
  clientAiEnabled?: boolean;
  /** Cuts with rounds is on for this project (AppSetting
   *  aroll_rounds_enabled). Absent = off, today's behaviour. */
  roundsEnabled?: boolean;
  musicFilename: string | null;
  /** KG's own playback switch. */
  musicEnabled: boolean;
  /** The client view's switch (portal + share link). Both this and
   *  musicEnabled must be true for a client to hear the bed. */
  musicClientEnabled: boolean;
  /** Bed level as a percent (0-100), shared by both views. */
  musicVolume: number;
};

/** A Send to Resolve request (server: modules/aroll/resolve-send.ts). */
export type ResolveSend = {
  id: string;
  draftId: string | null;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
  attempts: number;
  projectName: string | null;
  timelineName: string | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
};

export const aroll = {
  /** A-Roll SaaS ingest: mint a one-time Mux direct-upload URL (plus
   *  the pre-created PENDING asset row). The PUT itself goes straight
   *  to Mux via UpChunk. */
  /** Clip groups, including empty ones (the uploader's Add-to picker). */
  listGroups: (jobId: string) =>
    apiFetch<ARollGroup[]>(`/api/jobs/${encodeURIComponent(jobId)}/aroll/projects`),
  createGroup: (jobId: string, name: string) =>
    apiFetch<ARollGroup>(`/api/jobs/${encodeURIComponent(jobId)}/aroll/projects`, {
      method: 'POST',
      body: { name },
    }),
  renameGroup: (projectId: string, name: string) =>
    apiFetch<ARollGroup>(`/api/aroll/projects/${encodeURIComponent(projectId)}`, {
      method: 'PATCH',
      body: { name },
    }),
  /** Clips re-home to the loose bucket; only the folder row goes. */
  deleteGroup: (projectId: string) =>
    apiFetch<{ movedToRoot: number }>(
      `/api/aroll/projects/${encodeURIComponent(projectId)}`,
      { method: 'DELETE' },
    ),
  moveAsset: (assetId: string, projectId: string | null) =>
    apiFetch<void>(`/api/aroll/assets/${encodeURIComponent(assetId)}/group`, {
      method: 'PATCH',
      body: { projectId },
    }),
  createUpload: (
    jobId: string,
    filename: string,
    projectId?: string | null,
    sourceStartTc?: string | null,
  ) =>
    apiFetch<{ assetId: string; uploadUrl: string }>(
      `/api/jobs/${encodeURIComponent(jobId)}/aroll/uploads`,
      {
        method: 'POST',
        body: {
          filename,
          ...(projectId ? { projectId } : {}),
          ...(sourceStartTc ? { sourceStartTc } : {}),
        },
      },
    ),
  listAssets: (jobId: string) =>
    apiFetch<ARollAssetList>(
      `/api/jobs/${encodeURIComponent(jobId)}/aroll/assets`,
    ),
  /** Owner only: turn cuts with rounds on (runs the backfill) or off for a job. */
  setJobRounds: (jobId: string, enabled: boolean) =>
    apiFetch<ARollRoundsSwitch>(`/api/aroll/jobs/${encodeURIComponent(jobId)}/rounds`, {
      method: 'POST',
      body: { enabled },
    }),
  getAsset: (assetId: string) =>
    apiFetch<ARollAssetDetail>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}`,
    ),
  /** KG only: delete a broken export + its source MP4 in Dropbox. */
  deleteAsset: (assetId: string) =>
    apiFetch<void>(`/api/aroll/assets/${encodeURIComponent(assetId)}`, {
      method: 'DELETE',
    }),
  /** KG only: save the client-mirror view (active draft / hero speaker /
   *  Filter) on the asset so the client portal opens on KG's setup. */
  setAssetView: (
    assetId: string,
    view: {
      viewDraftId?: string | null;
      viewSpeaker?: string | null;
      viewSelections?: boolean;
    },
  ) =>
    apiFetch<void>(`/api/aroll/assets/${encodeURIComponent(assetId)}/view`, {
      method: 'PATCH',
      body: view,
    }),
  /** KG only: relabel the diarized speakers (name / role / context + which
   *  one is the voiceover). Names are display-only — the filter still keys on
   *  the raw label. The server enforces a single voiceover per asset. */
  upsertSpeakers: (
    assetId: string,
    speakers: Array<{
      speaker: string;
      name?: string | null;
      role?: string | null;
      context?: string | null;
      isVoiceover?: boolean;
      /** Omit to leave the stored hero flag untouched. */
      isHero?: boolean;
    }>,
  ) =>
    apiFetch<void>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/speakers`,
      { method: 'PUT', body: { speakers } },
    ),
  /** KG only: read each speaker's self-introduction from the transcript and
   *  propose a name / role / context for them. Suggest-then-accept — nothing
   *  is written; the caller accepts (via upsertSpeakers) per row or all at
   *  once. Only speakers where something was recovered are returned (can be
   *  empty). 403 for clients. */
  suggestSpeakerIntros: (assetId: string) =>
    apiFetch<{ suggestions: Array<{ speaker: string; name: string | null; role: string | null; context: string | null }> }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/speakers/suggest-intros`,
      { method: 'POST' },
    ),
  /** AI topic search over the transcript. Describe an idea/topic
   *  ("an uplifting line about the community") and get back ranked soundbites
   *  to fill a gap in the cut. Costs a little Anthropic budget per call.
   *  Can be empty (no matches). Open to the studio; a client (or share-link
   *  guest) gets it only when the workspace owner has switched client AI on
   *  (asset `clientAiEnabled`), otherwise 403. */
  searchTopic: (assetId: string, query: string) =>
    apiFetch<{ results: Array<{ startMs: number; endMs: number; quote: string; reason: string; speaker: string | null }> }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/search-topic`,
      { method: 'POST', body: { query } },
    ),
  scan: (jobId: string) =>
    apiFetch<{ added: number; skipped: number; throttled?: boolean }>(
      `/api/jobs/${encodeURIComponent(jobId)}/aroll/scan`,
      { method: 'POST' },
    ),
  /** Client sign-off on the job's A-Roll cut. approved=true locks the cut
   *  (clients can't reorder until they reopen it); false reopens it.
   *  Returns the fresh approval state. */
  setApproval: (jobId: string, approved: boolean) =>
    apiFetch<ARollApproval>(
      `/api/jobs/${encodeURIComponent(jobId)}/aroll/approval`,
      { method: 'POST', body: { approved } },
    ),
  /** `url` is a plain MP4 any <video> plays. `hlsUrl` (Mux-backed assets
   *  only) is the adaptive manifest the cut player prefers so it can pin
   *  to the ladder's lowest rung. Null on Dropbox-backed assets. */
  /** Background music: mint a Mux direct upload for a track file. The
   *  PUT goes straight to Mux via UpChunk, same as footage. */
  createMusicUpload: (assetId: string, filename: string) =>
    apiFetch<{ uploadUrl: string }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/music/upload`,
      { method: 'POST', body: { filename } },
    ),
  /** Partial: send `enabled` (KG playback), `clientEnabled` (client
   *  view), or both. */
  setMusicEnabled: (
    assetId: string,
    input: { enabled?: boolean; clientEnabled?: boolean; volume?: number },
  ) =>
    apiFetch<{ musicEnabled: boolean; musicClientEnabled: boolean; musicVolume: number }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/music`,
      { method: 'PATCH', body: input },
    ),
  removeMusic: (assetId: string) =>
    apiFetch<{ removed: boolean }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/music`,
      { method: 'DELETE' },
    ),
  streamUrl: (assetId: string) =>
    apiFetch<{ url: string; hlsUrl?: string | null }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/stream`,
    ),
  addHighlight: (
    assetId: string,
    input: { startMs: number; endMs: number; label?: string; draftId?: string | null },
  ) =>
    apiFetch<ARollHighlight>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/highlights`,
      { method: 'POST', body: input },
    ),
  /** Fix mis-categorised diarization: reassign every word in
   *  [startMs, endMs) to the given speaker label. Returns how many words
   *  were updated. KG-only (ADMIN/EDITOR). */
  reassignSpeaker: (
    assetId: string,
    input: { startMs: number; endMs: number; speaker: string },
  ) =>
    apiFetch<{ updated: number }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/segments/reassign`,
      { method: 'POST', body: input },
    ),
  /** Create a brand-new speaker (for a voice diarization missed or merged)
   *  and assign the highlighted span to it. Mints a fresh label server-side.
   *  Owner/editor/client all allowed (asset-scoped), same as reassign. */
  addSpeaker: (
    assetId: string,
    input: { name: string; role?: string; startMs: number; endMs: number },
  ) =>
    apiFetch<{ speaker: { speaker: string; name: string | null; role: string | null; context: string | null; isVoiceover: boolean }; updated: number }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/speakers/add`,
      { method: 'POST', body: input },
    ),
  updateHighlight: (
    highlightId: string,
    input: {
      startMs?: number;
      endMs?: number;
      label?: string | null;
      sortOrder?: number;
    },
  ) =>
    apiFetch<ARollHighlight>(
      `/api/aroll/highlights/${encodeURIComponent(highlightId)}`,
      { method: 'PATCH', body: input },
    ),
  deleteHighlight: (highlightId: string) =>
    apiFetch<void>(
      `/api/aroll/highlights/${encodeURIComponent(highlightId)}`,
      { method: 'DELETE' },
    ),
  /** Swap a clip for one of its stored alternate takes (near-duplicates
   *  the dedupe pass discarded). Reversible: the outgoing take drops
   *  into the vacated alternates slot. */
  swapHighlightAlternate: (highlightId: string, alternateIndex: number) =>
    apiFetch<ARollHighlight>(
      `/api/aroll/highlights/${encodeURIComponent(highlightId)}/swap-alternate`,
      { method: 'POST', body: { alternateIndex } },
    ),
  /** Chapter markers — saved positional dividers in the cut. sortOrder = the
   *  clip index the divider sits before. */
  addChapterMarker: (
    assetId: string,
    input: { draftId?: string | null; sortOrder: number; title: string },
  ) =>
    apiFetch<ARollChapterMarker>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/chapter-markers`,
      { method: 'POST', body: input },
    ),
  updateChapterMarker: (
    markerId: string,
    input: { title?: string; sortOrder?: number },
  ) =>
    apiFetch<ARollChapterMarker>(
      `/api/aroll/chapter-markers/${encodeURIComponent(markerId)}`,
      { method: 'PATCH', body: input },
    ),
  deleteChapterMarker: (markerId: string) =>
    apiFetch<void>(
      `/api/aroll/chapter-markers/${encodeURIComponent(markerId)}`,
      { method: 'DELETE' },
    ),
  /** Bulk reorder: editor drag-reordered the cut list. `ids` is the
   *  new playback order; backend assigns sortOrder = index. `draftId`
   *  scopes the operation (null = Manual tab). */
  reorderHighlights: (
    assetId: string,
    ids: string[],
    draftId: string | null = null,
  ) =>
    apiFetch<void>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/highlights/reorder`,
      { method: 'POST', body: { ids, draftId } },
    ),
  /** "Start your version": the client's own version of a studio draft.
   *  One per draft: returns the existing version if there already is one. */
  forkDraft: (assetId: string, sourceDraftId: string, name?: string | null) =>
    apiFetch<ARollDraft & { highlights: ARollHighlight[] }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/fork`,
      { method: 'POST', body: { sourceDraftId, ...(name ? { name } : {}) } },
    ),
  /** Single-editor lock on the client fork. */
  acquireDraftLock: (
    draftId: string,
    input: { sessionId: string; name?: string; force?: boolean },
  ) =>
    apiFetch<ARollDraftLock>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/lock/acquire`,
      { method: 'POST', body: input },
    ),
  heartbeatDraftLock: (draftId: string, sessionId: string) =>
    apiFetch<ARollDraftLock>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/lock/heartbeat`,
      { method: 'POST', body: { sessionId } },
    ),
  releaseDraftLock: (draftId: string, sessionId: string) =>
    apiFetch<void>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/lock/release`,
      { method: 'POST', body: { sessionId } },
    ),

  /** Caption export for the arranged cut (SRT/VTT), optionally
   *  translated. Returns the file content for a browser download. */
  exportCaptions: (
    assetId: string,
    opts: { draftId?: string | null; order?: 'cut' | 'source'; format: 'srt' | 'vtt'; language?: string | null },
  ) => {
    const params = new URLSearchParams();
    if (opts.draftId) params.set('draftId', opts.draftId);
    if (opts.order) params.set('order', opts.order);
    params.set('format', opts.format);
    if (opts.language) params.set('lang', opts.language);
    return apiFetch<{ filename: string; content: string; mimeType: string }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/captions?${params.toString()}`,
    );
  },
  /** Send to Resolve: queue this cut for the office robot to build as a
   *  timeline in the job's Resolve project (KG jobs, owner only). */
  sendToResolve: (assetId: string, draftId: string | null) =>
    apiFetch<ResolveSend>(`/api/aroll/assets/${encodeURIComponent(assetId)}/send-to-resolve`, {
      method: 'POST',
      body: { draftId },
    }),
  listResolveSends: (assetId: string) =>
    apiFetch<ResolveSend[]>(`/api/aroll/assets/${encodeURIComponent(assetId)}/send-to-resolve`),
  retryResolveSend: (id: string) =>
    apiFetch<ResolveSend>(`/api/aroll/resolve-sends/${encodeURIComponent(id)}/retry`, { method: 'POST' }),
  exportEdl: (
    assetId: string,
    draftId: string | null = null,
    /** 'cut' = arranged cut order (Arrange module); 'source' =
     *  source/transcript order (Transcript module). */
    order: 'cut' | 'source' = 'cut',
    /** NLE flavour: 'resolve' = CMX 3600 EDL (+ companion markers EDL),
     *  'premiere' = FCP7 XML (xmeml), 'fcpx' = FCPXML. */
    format: 'resolve' | 'premiere' | 'fcpx' = 'resolve',
  ) => {
    const params = new URLSearchParams();
    if (draftId) params.set('draftId', draftId);
    if (order === 'source') params.set('order', 'source');
    if (format !== 'resolve') params.set('format', format);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiFetch<{
      dropboxPath: string;
      filename: string;
      /** Content of the exported cut file (EDL / xmeml / FCPXML —
       *  named `edl` for historic reasons; download as-is). */
      edl: string;
      /** Companion markers EDL (clip notes + chapters → timeline markers).
       *  Resolve flavour only; null otherwise or when nothing is marked. */
      markersEdl: string | null;
      markersFilename: string | null;
      /** One-time review ask (SaaS): set on a workspace's FIRST successful
       *  export, when a review destination is configured. Show it once. */
      reviewAskUrl?: string | null;
    }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/edl${qs}`,
      { method: 'POST' },
    );
  },
  retry: (assetId: string) =>
    apiFetch<void>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/retry`,
      { method: 'POST' },
    ),
  /** Pre-generation pass: propose three story spines from the same
   *  steering surface as generateDraft. The editor picks one (or skips)
   *  before the cut is executed. */
  storyOptions: (
    assetId: string,
    body: {
      brief?: string;
      kind?: ARollDraftKind;
      builder?: ARollBriefBuilder;
      range?: { startMs: number; endMs: number };
      speaker?: string;
      speakers?: string[];
      includeDocs?: boolean;
      briefDoc?: { filename: string; mimeType: string; data: string };
      briefText?: string;
      contextDocs?: { filename: string; mimeType: string; data: string }[];
    },
  ) =>
    apiFetch<{ options: ARollStorySpine[] }>(
      `/api/aroll/assets/${assetId}/story-options`,
      { method: 'POST', body },
    ),

  generateDraft: (
    assetId: string,
    /** Omit for CLEAN (a one-click preset). Required for BRIEF / SHUFFLE. */
    brief: string | undefined,
    kind: ARollDraftKind = 'BRIEF',
    builder?: ARollBriefBuilder,
    /** Optional timecode window (ms) — analyse only this section of the
     *  transcript. Omit to analyse the whole thing. */
    range?: { startMs: number; endMs: number },
    /** Optional speaker label — build the draft from only this speaker's
     *  segments (the hero talent). Omit to use every speaker. */
    speaker?: string,
    /** Attach the job's brief + supplied material as context. Omit to let the
     *  server decide (on for the asset's first draft, off after). */
    includeDocs?: boolean,
    /** Subset of speaker labels allowed in the cut (every mode; Tighten
     *  sends its single hero voice as a one-element list). Omit (or pass
     *  undefined) to use the whole cast. Sent in the body as `speakers`. */
    speakers?: string[],
    /** Assembly model. Omit (or 'opus') = default best-quality Opus; 'sonnet'
     *  runs a cheaper, faster draft for iterating. */
    model?: 'sonnet' | 'opus',
    /** BRIEF mode: what the editor attached in the modal, sent one-shot as
     *  native context. `doc` is THE BRIEF; `contextDocs` are supporting
     *  background (tone deck, brand guidelines, prior script) which the
     *  server attaches as `reference` blocks so the prompt can tell the ask
     *  from the context. `text` is the retired paste box, kept for older
     *  clients. All `data` fields are base64 (≤5MB each). */
    attachments?: {
      doc?: { filename: string; mimeType: string; data: string };
      text?: string;
      contextDocs?: { filename: string; mimeType: string; data: string }[];
    },
    /** What the editor called this cut. Omit to let the server
     *  auto-number it ("AI draft 3"). */
    draftName?: string,
    /** Multi-interview cuts: other interviews from the same clip group to
     *  cut from alongside this one (KG jobs). */
    extraAssetIds?: string[],
  ) =>
    apiFetch<{ draft: ARollDraft & { highlights: ARollHighlight[] }; summary: string }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/drafts`,
      {
        method: 'POST',
        body: {
          brief,
          kind,
          builder,
          range,
          speaker,
          includeDocs,
          speakers,
          model,
          briefDoc: attachments?.doc,
          briefText: attachments?.text,
          contextDocs: attachments?.contextDocs,
          draftName,
          ...(extraAssetIds && extraAssetIds.length > 0 ? { extraAssetIds } : {}),
        },
      },
    ),
  /** Cut downs: a SET of shorter cuts from one press, all landing in ONE
   *  draft separated by chapter markers. `perLength` cuts per length, each
   *  on a different angle so they don't come back as the same film three
   *  times. Slow by design (one paid pass per cut, run in series) and can
   *  come back partial: `made < wanted` with `error` set means the earlier
   *  cuts were kept. */
  generateCutDowns: (
    assetId: string,
    brief: string,
    lengths: number[],
    perLength: number,
    /** What to call the set. Omit for "Cut downs N". */
    name?: string,
  ) =>
    apiFetch<{
      draft: ARollDraft & { highlights: ARollHighlight[] };
      made: number;
      wanted: number;
      error: string | null;
    }>(`/api/aroll/assets/${encodeURIComponent(assetId)}/cut-downs`, {
      method: 'POST',
      body: { brief, lengths, perLength, name },
    }),
  /** Rename a cut, from the title on its tab. 1-80 chars. */
  renameDraft: (draftId: string, name: string) =>
    apiFetch<ARollDraft>(`/api/aroll/drafts/${encodeURIComponent(draftId)}/name`, {
      method: 'PATCH',
      body: { name },
    }),
  /** Publish a cut to the client, or take it back. Several cuts can be
   *  published at once — the client gets a tab per published cut. */
  setDraftClientVisible: (draftId: string, visible: boolean) =>
    apiFetch<ARollDraft & { roundNumber?: number | null; roundCreated?: boolean }>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/client-visible`,
      { method: 'PATCH', body: { visible } },
    ),
  /** The sign-off on ONE sent draft (or its reopening). */
  setDraftApproval: (draftId: string, approved: boolean) =>
    apiFetch<ARollDraft>(`/api/aroll/drafts/${encodeURIComponent(draftId)}/approval`, {
      method: 'POST',
      body: { approved },
    }),
  /** A logged-in client hands their changes back to the studio. */
  submitDraftChanges: (draftId: string) =>
    apiFetch<ARollDraft>(`/api/aroll/drafts/${encodeURIComponent(draftId)}/submit-changes`, {
      method: 'POST',
    }),
  /** Studio: the client's changes become the draft. Returns the draft. */
  acceptDraftChanges: (forkId: string) =>
    apiFetch<ARollDraft>(`/api/aroll/drafts/${encodeURIComponent(forkId)}/accept-changes`, {
      method: 'POST',
    }),
  /** Refine an existing AI draft from editor feedback. Creates a NEW draft
   *  (non-destructive) inheriting the parent's kind, folding the parent's
   *  brief + current cut in as context. `feedback` is 3-2000 chars. */
  refineDraft: (draftId: string, feedback: string) =>
    apiFetch<{ draft: ARollDraft & { highlights: ARollHighlight[] }; summary: string }>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/refine`,
      { method: 'POST', body: { feedback } },
    ),
  /** Rounds on: "Generate again" replaces the cut's working copy. */
  regenerateCut: (draftId: string, feedback?: string | null) =>
    apiFetch<{ draft: ARollDraft & { highlights: ARollHighlight[] }; undoAction: 'undo' | 'redo' }>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/regenerate`,
      { method: 'POST', body: { feedback: feedback ?? null } },
    ),
  /** Rounds on: swap the working copy with the undo slot (Undo / Redo). */
  undoRegenerate: (draftId: string) =>
    apiFetch<{ draft: ARollDraft & { highlights: ARollHighlight[] }; undoAction: 'undo' | 'redo' }>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}/undo-regenerate`,
      { method: 'POST' },
    ),
  /** Rounds on: open one round read-only. */
  getRound: (roundId: string) =>
    apiFetch<ARollRoundDetail>(`/api/aroll/rounds/${encodeURIComponent(roundId)}`),
  /** Create an empty draft (no AI) the editor fills by hand. Used by
   *  "Add to shuffle" when the asset has no shuffle yet. */
  createEmptyDraft: (assetId: string, kind: ARollDraftKind = 'SHUFFLE') =>
    apiFetch<ARollDraft & { highlights: ARollHighlight[] }>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/drafts/empty`,
      { method: 'POST', body: { kind } },
    ),
  deleteDraft: (draftId: string) =>
    apiFetch<void>(
      `/api/aroll/drafts/${encodeURIComponent(draftId)}`,
      { method: 'DELETE' },
    ),
  addNote: (
    assetId: string,
    input: { timecodeMs: number; body: string; highlightId?: string },
  ) =>
    apiFetch<ARollNote>(
      `/api/aroll/assets/${encodeURIComponent(assetId)}/notes`,
      { method: 'POST', body: input },
    ),
  updateNote: (
    noteId: string,
    input: { timecodeMs?: number; body?: string },
  ) =>
    apiFetch<ARollNote>(
      `/api/aroll/notes/${encodeURIComponent(noteId)}`,
      { method: 'PATCH', body: input },
    ),
  deleteNote: (noteId: string) =>
    apiFetch<void>(
      `/api/aroll/notes/${encodeURIComponent(noteId)}`,
      { method: 'DELETE' },
    ),
};

/**
 * A-Roll over a share token (magic link). The token is the credential,
 * so no bearer header. Read + approve only — the magic-link client
 * watches the cut and signs off; editing stays behind a real login.
 */
export const arollShare = {
  /** A-Roll SaaS share-page bootstrap: sender branding + the project
   *  this link shows. */
  /** Everything the studio has sent this client, across projects. */
  getRoom: (token: string) =>
    apiFetch<ClientRoomPayload>(`/api/portal/share/${encodeURIComponent(token)}/room`, {
      unauthenticated: true,
    }),
  getContext: (token: string, jobId?: string) =>
    apiFetch<{
      branding: { name: string; logoUrl: string | null } | null;
      job: {
        id: string;
        code: string;
        title: string;
        module: 'AROLL' | 'PLAYBACK';
        arollEnabled?: boolean;
      } | null;
      /** PictureUp: whether this link includes the project's A-Roll.
       *  Server-enforced on every A-Roll share route; UI hint here. */
      allowAroll?: boolean;
      /** What this link permits. Enforced server-side; the UI reads it
       *  only to decide which affordances to draw. */
      access?: 'VIEW' | 'COMMENT' | 'EDIT';
      /** Whether this link may fetch full-quality originals. Same
       *  deal: server-enforced, UI hint only. */
      allowDownload?: boolean;
      /** Whether this link may mark range selects on a review
       *  version. Server-enforced, UI hint only. */
      allowSelects?: boolean;
    }>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/context${
        jobId ? `?job=${encodeURIComponent(jobId)}` : ''
      }`,
      { unauthenticated: true },
    ),
  listAssets: (token: string, jobId: string) =>
    apiFetch<ARollAssetList>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/aroll/assets`,
      { unauthenticated: true },
    ),
  getAsset: (token: string, assetId: string) =>
    apiFetch<ARollAssetDetail>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}`,
      { unauthenticated: true },
    ),
  /** Same shape as the KG `aroll.streamUrl`, including `hlsUrl`: the
   *  cut player is a review surface for clients too, so share links get
   *  the same low-rung stream KG gets (same AROLL_HLS_FROM canary). */
  streamUrl: (token: string, assetId: string) =>
    apiFetch<{ url: string; hlsUrl?: string | null }>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/stream`,
      { unauthenticated: true },
    ),
  setApproval: (token: string, jobId: string, approved: boolean) =>
    apiFetch<ARollApproval>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/aroll/approval`,
      { method: 'POST', body: { approved }, unauthenticated: true },
    ),
  /** Rounds on: open one round of a sent cut, read-only. */
  getRound: (token: string, roundId: string) =>
    apiFetch<ARollRoundDetail>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/rounds/${encodeURIComponent(roundId)}`,
      { unauthenticated: true },
    ),
  setDraftApproval: (token: string, draftId: string, approved: boolean, name?: string | null) =>
    apiFetch<ARollDraft>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/drafts/${encodeURIComponent(draftId)}/approval`,
      { method: 'POST', body: { approved, name: name ?? undefined }, unauthenticated: true },
    ),
  submitDraftChanges: (token: string, draftId: string, name?: string | null) =>
    apiFetch<ARollDraft>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/drafts/${encodeURIComponent(draftId)}/submit-changes`,
      { method: 'POST', body: { name: name ?? undefined }, unauthenticated: true },
    ),
  /** Discard the client's own changes (their copy only). */
  deleteDraft: (token: string, draftId: string) =>
    apiFetch<{ ok: true }>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/drafts/${encodeURIComponent(draftId)}`,
      { method: 'DELETE', unauthenticated: true },
    ),
  // Client editing over a share token. The backend restricts a guest to their
  // own client version, so these can never touch the studio's draft.
  forkDraft: (token: string, assetId: string, sourceDraftId: string, name?: string | null) =>
    apiFetch<ARollDraft & { highlights: ARollHighlight[] }>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/fork`,
      {
        method: 'POST',
        body: { sourceDraftId, ...(name ? { name } : {}) },
        unauthenticated: true,
      },
    ),
  reorderHighlights: (
    token: string,
    assetId: string,
    ids: string[],
    draftId: string | null = null,
  ) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/highlights/reorder`,
      { method: 'POST', body: { ids, draftId }, unauthenticated: true },
    ),
  addHighlight: (
    token: string,
    assetId: string,
    input: { startMs: number; endMs: number; label?: string; draftId?: string | null },
  ) =>
    apiFetch<ARollHighlight>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/highlights`,
      { method: 'POST', body: input, unauthenticated: true },
    ),
  updateHighlight: (
    token: string,
    highlightId: string,
    input: { startMs?: number; endMs?: number; label?: string | null; sortOrder?: number },
  ) =>
    apiFetch<ARollHighlight>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/highlights/${encodeURIComponent(highlightId)}`,
      { method: 'PATCH', body: input, unauthenticated: true },
    ),
  deleteHighlight: (token: string, highlightId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/highlights/${encodeURIComponent(highlightId)}`,
      { method: 'DELETE', unauthenticated: true },
    ),
  swapHighlightAlternate: (token: string, highlightId: string, alternateIndex: number) =>
    apiFetch<ARollHighlight>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/highlights/${encodeURIComponent(highlightId)}/swap-alternate`,
      { method: 'POST', body: { alternateIndex }, unauthenticated: true },
    ),
  addChapterMarker: (
    token: string,
    assetId: string,
    input: { draftId?: string | null; sortOrder: number; title: string },
  ) =>
    apiFetch<ARollChapterMarker>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/chapter-markers`,
      { method: 'POST', body: input, unauthenticated: true },
    ),
  updateChapterMarker: (
    token: string,
    markerId: string,
    input: { title?: string; sortOrder?: number },
  ) =>
    apiFetch<ARollChapterMarker>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/chapter-markers/${encodeURIComponent(markerId)}`,
      { method: 'PATCH', body: input, unauthenticated: true },
    ),
  deleteChapterMarker: (token: string, markerId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/chapter-markers/${encodeURIComponent(markerId)}`,
      { method: 'DELETE', unauthenticated: true },
    ),
  acquireDraftLock: (
    token: string,
    draftId: string,
    input: { sessionId: string; name?: string; force?: boolean },
  ) =>
    apiFetch<ARollDraftLock>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/drafts/${encodeURIComponent(draftId)}/lock/acquire`,
      { method: 'POST', body: input, unauthenticated: true },
    ),
  heartbeatDraftLock: (token: string, draftId: string, sessionId: string) =>
    apiFetch<ARollDraftLock>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/drafts/${encodeURIComponent(draftId)}/lock/heartbeat`,
      { method: 'POST', body: { sessionId }, unauthenticated: true },
    ),
  releaseDraftLock: (token: string, draftId: string, sessionId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/drafts/${encodeURIComponent(draftId)}/lock/release`,
      { method: 'POST', body: { sessionId }, unauthenticated: true },
    ),
  searchTopic: (token: string, assetId: string, query: string) =>
    apiFetch<{ results: Array<{ startMs: number; endMs: number; quote: string; reason: string; speaker: string | null }> }>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/search-topic`,
      { method: 'POST', body: { query }, unauthenticated: true },
    ),
  addNote: (
    token: string,
    assetId: string,
    input: { timecodeMs: number; body: string; highlightId?: string },
  ) =>
    apiFetch<ARollNote>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/assets/${encodeURIComponent(assetId)}/notes`,
      { method: 'POST', body: input, unauthenticated: true },
    ),
  deleteNote: (token: string, noteId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/aroll/notes/${encodeURIComponent(noteId)}`,
      { method: 'DELETE', unauthenticated: true },
    ),
};

export const playbackShare = {
  listAssets: (token: string, jobId: string) =>
    apiFetch<PlaybackAsset[]>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/playback/assets`,
      { unauthenticated: true },
    ),
  streamUrl: (token: string, versionId: string) =>
    apiFetch<{ url: string }>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/stream`,
      { unauthenticated: true },
    ),
  /** Full-quality original download URL + source filename. */
  downloadUrl: (token: string, versionId: string) =>
    apiFetch<{ url: string; filename: string }>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/download`,
      { unauthenticated: true },
    ),
  /**
   * Share-token equivalent of playback.thumbnail. The share-token
   * itself is the credential, no bearer required.
   */
  thumbnail: async (token: string, versionId: string): Promise<Blob | null> => {
    const res = await fetchThumbnailGated(() =>
      fetch(
        `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/thumbnail`,
        { headers: { Accept: 'image/jpeg' } },
      ),
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** Final exports list — share-token variant. */
  finalExports: (token: string, jobId: string) =>
    apiFetch<{ files: FinalExportFile[] }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/exports/finals`,
      { unauthenticated: true },
    ),
  /** Final export thumbnail — share-token variant. */
  finalExportThumbnail: async (
    token: string,
    jobId: string,
    dropboxPath: string,
  ): Promise<Blob | null> => {
    const res = await fetchThumbnailGated(() =>
      fetch(
        `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/exports/finals/thumbnail?path=${encodeURIComponent(dropboxPath)}`,
        { headers: { Accept: 'image/jpeg' } },
      ),
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  /** Final export stream URL — share-token variant. */
  finalExportStreamUrl: (token: string, jobId: string, dropboxPath: string) =>
    apiFetch<{ url: string }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/exports/finals/stream?path=${encodeURIComponent(dropboxPath)}`,
      { unauthenticated: true },
    ),
  /** Final export folder shared link — share-token variant. */
  finalExportFolderLink: (token: string, jobId: string, folderPath: string) =>
    apiFetch<{ url: string }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/exports/finals/folder-link?path=${encodeURIComponent(folderPath)}`,
      { unauthenticated: true },
    ),
  recordDuration: (token: string, versionId: string, durationMs: number) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/duration`,
      { method: 'POST', body: { durationMs }, unauthenticated: true },
    ),
  listComments: (token: string, versionId: string) =>
    apiFetch<PlaybackComment[]>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/comments`,
      { unauthenticated: true },
    ),
  /** Scrub-preview filmstrip (long cuts). Null when none exists. */
  spriteInfo: (token: string, versionId: string) =>
    apiFetch<PlaybackSpriteInfo | null>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/sprite`,
      { unauthenticated: true },
    ),
  postComment: (
    token: string,
    versionId: string,
    body: string,
    timecodeMs: number,
    author?: string,
    /** Accepted for signature parity with the KG path; the share-token
     *  backend forces `internal = false` regardless of what's sent
     *  (clients can't post internal notes by definition). */
    _internal?: boolean,
    parentId?: string,
    endMs?: number | null,
    drawing?: CommentDrawing | null,
    /** The reviewer's stable per-person key (see lib/reviewerIdentity).
     *  Distinguishes two people who were forwarded the same link. */
    guestKey?: string | null,
    attachmentIds?: string[],
  ) =>
    apiFetch<PlaybackComment>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/comments`,
      {
        method: 'POST',
        body: { body, timecodeMs, author, parentId, endMs, drawing, guestKey, attachmentIds },
        unauthenticated: true,
      },
    ),
  deleteComment: (token: string, commentId: string, guestKey?: string | null) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/comments/${encodeURIComponent(commentId)}` +
        (guestKey ? `?guestKey=${encodeURIComponent(guestKey)}` : ''),
      { method: 'DELETE', unauthenticated: true },
    ),
  /** Range selects behind the share token. Writes need the link's
   *  COMMENT rank + allowSelects flag (fenced server-side). */
  listSelects: (token: string, versionId: string) =>
    apiFetch<PlaybackSelect[]>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/selects`,
      { unauthenticated: true },
    ),
  createSelect: (
    token: string,
    versionId: string,
    inMs: number,
    outMs: number,
    label?: string | null,
    author?: string,
    guestKey?: string | null,
  ) =>
    apiFetch<PlaybackSelect>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/selects`,
      { method: 'POST', body: { inMs, outMs, label, author, guestKey }, unauthenticated: true },
    ),
  reorderSelects: (token: string, versionId: string, order: string[], author?: string) =>
    apiFetch<PlaybackSelect[]>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/selects/reorder`,
      { method: 'POST', body: { order, author }, unauthenticated: true },
    ),
  updateSelect: (
    token: string,
    selectId: string,
    patch: { inMs?: number; outMs?: number; label?: string | null },
    guestKey?: string | null,
  ) =>
    apiFetch<PlaybackSelect>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/selects/${encodeURIComponent(selectId)}`,
      { method: 'PATCH', body: { ...patch, guestKey }, unauthenticated: true },
    ),
  deleteSelect: (token: string, selectId: string, guestKey?: string | null) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/selects/${encodeURIComponent(selectId)}` +
        (guestKey ? `?guestKey=${encodeURIComponent(guestKey)}` : ''),
      { method: 'DELETE', unauthenticated: true },
    ),
  /** Upload one composer attachment behind the share token. The
   *  reviewer's guestKey rides in a header so the pending row is
   *  theirs to claim on post (guests must name themselves first). */
  uploadCommentAttachment: (
    token: string,
    versionId: string,
    file: File,
    guestKey?: string | null,
  ) =>
    uploadBinary<PlaybackCommentAttachment>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/attachments`,
      file,
      guestKey ? { 'X-Guest-Key': guestKey } : {},
    ),
  /** Remove a pending attachment (composer × before posting). */
  deleteCommentAttachment: (token: string, attachmentId: string, guestKey?: string | null) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/attachments/${encodeURIComponent(attachmentId)}` +
        (guestKey ? `?guestKey=${encodeURIComponent(guestKey)}` : ''),
      { method: 'DELETE', unauthenticated: true },
    ),
  /** Fetch an attachment's bytes as a Blob behind the share token. */
  commentAttachmentBlob: async (
    token: string,
    attachmentId: string,
  ): Promise<Blob> => {
    const res = await fetch(
      `/api/portal/share/${encodeURIComponent(token)}/playback/attachments/${encodeURIComponent(attachmentId)}/stream`,
    );
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
  resolveComment: (token: string, commentId: string, resolved: boolean) =>
    apiFetch<PlaybackComment>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/comments/${encodeURIComponent(commentId)}/resolve`,
      { method: 'POST', body: { resolved }, unauthenticated: true },
    ),
  decide: (
    token: string,
    versionId: string,
    status: 'APPROVED' | 'CHANGES_REQUESTED',
    message?: string,
  ) =>
    apiFetch<PlaybackApproval>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/decision`,
      {
        method: 'POST',
        body: { status, message },
        unauthenticated: true,
      },
    ),
  submitReview: (token: string, versionId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/playback/versions/${encodeURIComponent(versionId)}/submit-review`,
      { method: 'POST', unauthenticated: true },
    ),
};

// =====================================================================
// INSPO — Claude-curated reference pool + client moodboard
// =====================================================================

/** One item in a pool or selection. Mirrors `InspoItem` on the server. */
export type InspoItem = {
  id: string;
  type: 'video' | 'image';
  title: string;
  sourceUrl: string;
  mediaUrl: string | null;
  source: string;
  description: string;
};

export type InspoPoolPayload = {
  id: string;
  createdAt: string;
  model: string;
  items: InspoItem[];
  estCostUsd: number;
  searchCalls: number;
};

export type InspoSelectionPayload = {
  id: string;
  itemKey: string;
  item: InspoItem;
  createdAt: string;
  poolId: string | null;
};

export type InspoStatus = {
  spinsUsed: number;
  spinsTotal: number;
  spinsRemaining: number;
  nextSpinModel: 'sonnet' | 'haiku';
  /** True when the moodboard is empty + we'd happily spin right
   *  now (brief has content, cap + budget not hit). The component
   *  auto-fires the first spin when this is true. */
  autoSpinPending: boolean;
  /** When false, the Spin button is disabled. */
  spinAllowed: boolean;
  blockedReason: 'LIFETIME_CAP' | 'MONTHLY_BUDGET' | null;
};

export type InspoBundle = {
  pool: InspoPoolPayload | null;
  selections: InspoSelectionPayload[];
  status: InspoStatus;
};

export type InspoBudgetError = {
  error: string;
  reason: 'THROTTLED' | 'LIFETIME_CAP' | 'MONTHLY_BUDGET';
  details: Record<string, unknown>;
};

/**
 * Helper for unwrapping budget rejections. Callers should `catch
 * (err)` from inspo.spin / inspoShare.spin and pass the error through
 * here - returns a typed budget error when applicable, otherwise
 * null so the caller can rethrow / surface a generic error.
 */
export function unwrapInspoBudgetError(err: unknown): InspoBudgetError | null {
  if (!(err instanceof ApiClientError)) return null;
  const reasonMap: Record<string, InspoBudgetError['reason']> = {
    INSPO_THROTTLED: 'THROTTLED',
    INSPO_LIFETIME_CAP: 'LIFETIME_CAP',
    INSPO_MONTHLY_BUDGET: 'MONTHLY_BUDGET',
  };
  const reason = reasonMap[err.code];
  if (!reason) return null;
  return {
    error: err.message,
    reason,
    details: (err.details as Record<string, unknown> | undefined) ?? {},
  };
}

export const inspo = {
  load: (jobId: string) =>
    apiFetch<InspoBundle>(`/api/jobs/${encodeURIComponent(jobId)}/inspo`),
  spin: (jobId: string) =>
    apiFetch<{ pool: InspoPoolPayload }>(
      `/api/jobs/${encodeURIComponent(jobId)}/inspo/spin`,
      { method: 'POST', body: {} },
    ),
  listPools: (jobId: string, limit = 10) =>
    apiFetch<{ pools: InspoPoolPayload[] }>(
      `/api/jobs/${encodeURIComponent(jobId)}/inspo/pools?limit=${limit}`,
    ),
  saveSelection: (jobId: string, poolId: string, itemKey: string) =>
    apiFetch<{ selection: InspoSelectionPayload }>(
      `/api/jobs/${encodeURIComponent(jobId)}/inspo/selections`,
      { method: 'POST', body: { poolId, itemKey } },
    ),
  removeSelection: (jobId: string, itemKey: string) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/inspo/selections/${encodeURIComponent(itemKey)}`,
      { method: 'DELETE' },
    ),
};

export const inspoShare = {
  load: (token: string, jobId: string) =>
    apiFetch<InspoBundle>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/inspo`,
      { unauthenticated: true },
    ),
  spin: (token: string, jobId: string) =>
    apiFetch<{ pool: InspoPoolPayload }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/inspo/spin`,
      { method: 'POST', body: {}, unauthenticated: true },
    ),
  saveSelection: (token: string, jobId: string, poolId: string, itemKey: string) =>
    apiFetch<{ selection: InspoSelectionPayload }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/inspo/selections`,
      { method: 'POST', body: { poolId, itemKey }, unauthenticated: true },
    ),
  removeSelection: (token: string, jobId: string, itemKey: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/inspo/selections/${encodeURIComponent(itemKey)}`,
      { method: 'DELETE', unauthenticated: true },
    ),
};

// ---------------------------------------------------------------------
// Stills review — the low-res stills tab next to Playback. KG uploads
// low-res shoot stills (kind STILLS); KG and the client tick the ones
// they want into one shared per-job selection set.
// ---------------------------------------------------------------------

export type StillItem = {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  selected: boolean;
  /** Poster on R2, null while preparing; the board falls back to the
   *  authed Dropbox thumbnail until it lands. */
  posterUrl?: string | null;
  mediaStatus?: string | null;
  width?: number | null;
  height?: number | null;
};

/** Authed JPEG blob fetch with the one-shot 401 refresh retry the
 *  other thumbnail helpers use. Returns null on 404 (no thumbnail). */
async function authedThumbBlob(url: string): Promise<Blob | null> {
  const send = async () => {
    const tokens = getTokens();
    const headers: Record<string, string> = { Accept: 'image/jpeg' };
    if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
    return fetch(url, { headers });
  };
  let res = await send();
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) res = await send();
  }
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiClientError(res.status, null);
  return res.blob();
}

export const stills = {
  list: (jobId: string) =>
    apiFetch<{ stills: StillItem[] }>(
      `/api/jobs/${encodeURIComponent(jobId)}/stills`,
    ),
  select: (jobId: string, assetId: string) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/stills/${encodeURIComponent(assetId)}/select`,
      { method: 'POST', body: {} },
    ),
  deselect: (jobId: string, assetId: string) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/stills/${encodeURIComponent(assetId)}/select`,
      { method: 'DELETE' },
    ),
  clearSelections: (jobId: string) =>
    apiFetch<void>(
      `/api/jobs/${encodeURIComponent(jobId)}/stills/selections`,
      { method: 'DELETE' },
    ),
  sendSelection: (jobId: string) =>
    apiFetch<{ ticked: number }>(
      `/api/jobs/${encodeURIComponent(jobId)}/stills/notify-selection`,
      { method: 'POST', body: {} },
    ),
  thumb: (jobId: string, assetId: string, size = 'w480h320') =>
    authedThumbBlob(
      `/api/jobs/${encodeURIComponent(jobId)}/stills/${encodeURIComponent(assetId)}/thumb?size=${encodeURIComponent(size)}`,
    ),
  /** Reconcile the job's Low Res Stills Dropbox folder into rows —
   *  stills exported straight into the folder appear without a manual
   *  upload. KG only; fired when the tab mounts. */
  scan: (jobId: string) =>
    apiFetch<{ created: number }>(
      `/api/jobs/${encodeURIComponent(jobId)}/stills/scan`,
      { method: 'POST', body: {} },
    ),
  /** Bulk-upload one still (KG only). Reuses the job asset upload
   *  path with the STILLS kind. */
  upload: (jobId: string, file: File) => jobs.uploadAsset(jobId, file, 'STILLS'),
  remove: (jobId: string, assetId: string) => jobs.deleteAsset(jobId, assetId),
};

export const stillsShare = {
  list: (token: string, jobId: string) =>
    apiFetch<{ stills: StillItem[] }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/stills`,
      { unauthenticated: true },
    ),
  select: (token: string, jobId: string, assetId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/stills/${encodeURIComponent(assetId)}/select`,
      { method: 'POST', body: {}, unauthenticated: true },
    ),
  deselect: (token: string, jobId: string, assetId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/stills/${encodeURIComponent(assetId)}/select`,
      { method: 'DELETE', unauthenticated: true },
    ),
  clearSelections: (token: string, jobId: string) =>
    apiFetch<void>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/stills/selections`,
      { method: 'DELETE', unauthenticated: true },
    ),
  sendSelection: (token: string, jobId: string) =>
    apiFetch<{ ticked: number }>(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/stills/notify-selection`,
      { method: 'POST', body: {}, unauthenticated: true },
    ),
  thumb: async (
    token: string,
    jobId: string,
    assetId: string,
    size = 'w480h320',
  ): Promise<Blob | null> => {
    const res = await fetch(
      `/api/portal/share/${encodeURIComponent(token)}/jobs/${encodeURIComponent(jobId)}/stills/${encodeURIComponent(assetId)}/thumb?size=${encodeURIComponent(size)}`,
      { headers: { Accept: 'image/jpeg' } },
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiClientError(res.status, null);
    return res.blob();
  },
};

// ---------------------------------------------------------------------
// Taste training — /admin/taste-trainer page
// ---------------------------------------------------------------------

export type TasteSignalKind = 'YES' | 'NO' | 'MAYBE';

export type TasteCandidate = {
  id: string;
  type: 'video' | 'image';
  title: string;
  sourceUrl: string;
  mediaUrl: string | null;
  source: string;
  director: string | null;
  studio: string | null;
  description: string;
};

export type TasteProfile = {
  totals: Record<TasteSignalKind, number>;
  byIndustry: Array<{
    industry: string | null;
    yes: number;
    no: number;
    maybe: number;
  }>;
  topYes: Array<{ name: string; yes: number; no: number }>;
  topNo: Array<{ name: string; yes: number; no: number }>;
};

export type TasteYesList = {
  yes: string[];
  no: string[];
};

export type TasteRateInput = {
  sourceUrl: string;
  title: string;
  source: string;
  heroImageUrl: string | null;
  type: 'video' | 'image';
  director: string | null;
  studio: string | null;
  industry: string | null;
  signal: TasteSignalKind;
  notes?: string | null;
};

// --- Folio (public website pieces) ---------------------------------
//
// Mirror of the FolioPiece + FolioCredit shape from the kilogram-app
// backend. Public site reads via /api/folio; the admin tab in this
// dashboard reads via /api/folio/admin/list and writes via
// /api/folio/admin/:id PATCH.

export type FolioCategory = 'MOTION' | 'STILL';
/** Industry sector — mirrors kilogram-app's FolioSector enum. */
export type FolioSector =
  | 'FASHION_BEAUTY'
  | 'MUSIC_ENTERTAINMENT'
  | 'RETAIL_CONSUMER'
  | 'FOOD_HOSPITALITY'
  | 'HEALTH_SPORT'
  | 'PROPERTY_FINANCE'
  | 'TECH'
  | 'INDUSTRY_COMMUNITY';
export const FOLIO_SECTOR_LABELS: Record<FolioSector, string> = {
  FASHION_BEAUTY: 'Fashion & Beauty',
  MUSIC_ENTERTAINMENT: 'Music & Entertainment',
  RETAIL_CONSUMER: 'Retail & Consumer Goods',
  FOOD_HOSPITALITY: 'Food, Drink & Hospitality',
  HEALTH_SPORT: 'Health, Sport & Wellness',
  PROPERTY_FINANCE: 'Property, Finance & Professional',
  TECH: 'Technology',
  INDUSTRY_COMMUNITY: 'Industry & Community',
};
export type FolioStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
/** HIGHLIGHT = one piece per shoot (one job). COLLECTION = one piece per
 *  client that accumulates many jobs over time (e.g. Jellis Craig). */
export type FolioPieceType = 'HIGHLIGHT' | 'COLLECTION';

export type FolioContentBlock =
  | { type: 'image'; url: string }
  /** Two-up pair ('landscape' 16:9 / 'portrait' crops), or 'trio': exactly
   *  three urls, [top, bottom] stacked beside a tall portrait [2].
   *  `stack` crops the pair (absent = 'landscape' 16:9); `portraitSide`
   *  absent = 'right'. Both are trio-only. */
  | {
      type: 'images';
      urls: string[];
      layout?: 'landscape' | 'portrait' | 'trio';
      /** 3 = three across on every screen (BTS rows); absent or 2 = the pair. Not for a trio. */
      columns?: 2 | 3;
      /** Tiles the site shows in black and white, by url. */
      mono?: string[];
      stack?: 'landscape' | 'square';
      portraitSide?: 'left' | 'right';
      /** Trio only: which urls are clips (silent MP4 loops), with their first frame. */
      clips?: Array<{ url: string; posterUrl?: string }>;
      /** Trio only: where each cell's still or clip sits in its crop, by url;
       *  x / y are background-position percentages (0-100). Absent = centred. */
      focus?: Array<{ url: string; x: number; y: number }>;
    }
  | {
      type: 'video';
      vimeoId?: string | null;
      muxPlaybackId?: string | null;
      /** Chosen poster frame (a Mux thumbnail URL at the picked time). */
      posterUrl?: string | null;
      /** Ambient loop: plays muted on its own, on repeat, no controls. */
      loop?: boolean;
      /** Website box shape. Absent = 16:9. */
      aspect?: '16:9' | '4:3';
      /** The URL add_folio_video ingested it from; a re-add from the same
       *  URL is skipped. Kept through a dashboard save. */
      sourceUrl?: string;
    }
  /** A row of portrait (9:16) or square (1:1) Mux clips side by side.
   *  `columns` = how many across on desktop (2 or 3; phones stack).
   *  Absent = 3 for portrait, 2 for square. */
  | {
      type: 'videos';
      items: FolioVideoSetItem[];
      aspect: 'portrait' | 'square';
      columns?: 2 | 3;
    }
  | { type: 'text'; body: string }
  /** The BTS carousel: a full-bleed strip at the foot of the piece that
   *  drifts sideways, loops, pauses on hover and can be dragged. Clips are
   *  plain muted MP4s (added from chat, add_folio_carousel). `speed` in
   *  pixels a second (absent = 30). */
  | { type: 'carousel'; items: FolioCarouselItem[]; speed?: number };

/** `sourcePath` is the Dropbox path add_folio_carousel copied it from, so
 *  the same path is never added twice. Kept through a dashboard save. */
export type FolioCarouselItem =
  | { type: 'image'; url: string; sourcePath?: string }
  | { type: 'video'; url: string; posterUrl?: string | null; sourcePath?: string };

export type FolioVideoSetItem = {
  muxPlaybackId: string;
  /** Chosen poster frame (a Mux thumbnail URL at the picked time). */
  posterUrl?: string | null;
};

export type FolioCredit = {
  id: string;
  role: string;
  name: string;
  sortOrder: number;
};

export type FolioPiece = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  category: FolioCategory;
  /** Industry sector (the client's world). Null = not classified yet. */
  sector: FolioSector | null;
  /** Nick's strength score, 1-5. Null = not rated yet. Internal only. */
  rating: number | null;
  status: FolioStatus;
  pieceType: FolioPieceType;
  publishedAt: string;
  heroImage: string | null;
  heroVimeoId: string | null;
  /** Mux hero playback id (self-hosted), set by ingesting a master. */
  heroMuxPlaybackId: string | null;
  /** Whether the Mux hero is live. The public site uses Mux only when a
   *  playback id is set AND this is true; otherwise it stays on Vimeo. */
  heroMuxLive: boolean;
  hoverVideoUrl: string | null;
  hoverVideoUrlPortrait: string | null;
  tilePosterUrl: string | null;
  tilePosterUrlPortrait: string | null;
  hoverVideoStartTime: number | null;
  hoverVideoStartTimePortrait: number | null;
  /** Rollover cut from the Mux hero (its own Mux asset). Null when the
   *  rollover is a hand-cut upload or there is none. */
  hoverMuxAssetId: string | null;
  hoverMuxPlaybackId: string | null;
  /** In/out points on the hero timeline the rollover was cut from. */
  hoverClipStart: number | null;
  hoverClipEnd: number | null;
  /** The rollover EDIT: ordered sections of the hero the tile clip is
   *  built from, in play order. One section is clipped on Mux; two or
   *  more are rendered by the folio-render worker (Mux cannot stitch). */
  hoverSections: FolioHoverSection[];
  /** Hand-framed 9:16: shots on the hero with where the 9:16 window sits
   *  (x 0 = left, 1 = right). Null until framed by hand. */
  hoverPortraitFraming?: FolioFramedShot[] | null;
  /** 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED' for the framed 9:16. */
  portraitFramingStatus?: string | null;
  portraitFramingError?: string | null;
  /** Low-bitrate, 1-keyframe-per-second copy of the hero, so the
   *  rollover editor can scrub and play sections back instantly. */
  heroScrubUrl: string | null;
  /** RAW column value, which is upper-case ('READY'). The normalised
   *  lower-case form comes from `rolloverBuildStatus`; anything reading
   *  this field directly must fold the case itself. */
  heroScrubStatus: string | null;
  /** RAW column value ('PENDING' | 'PROCESSING' | 'READY' | 'FAILED'),
   *  same case caveat as heroScrubStatus. Set by BOTH rollover paths —
   *  the worker for a multi-section montage, and the Mux cut for a
   *  single window — so the folio grid can report a build without
   *  knowing which one ran. */
  rolloverStatus: string | null;
  rolloverError: string | null;
  /** Bumped by each rendered montage; the file name carries it. */
  rolloverVersion: number;
  intro: string | null;
  contentBlocks: FolioContentBlock[];
  jobId: string | null;
  sortOrder: number | null;
  /** Lower-case kebab tag slugs ("branded" / "fashion" / "music"). */
  tags: string[];
  createdAt: string;
  updatedAt: string;
  credits: FolioCredit[];
};

/** Encode state of a rollover cut on Mux, polled after a cut. */
export type FolioRolloverStatus = {
  status: 'none' | 'preparing' | 'ready' | 'errored';
  /** True once the MP4 files the tiles play are downloadable. */
  mp4Ready: boolean;
};

/** One marked stretch of the hero, seconds on its timeline. Array
 *  position is play order, so sections may overlap or run backwards —
 *  this is an edit, not a selection. */
export type FolioHoverSection = {
  startTime: number;
  endTime: number;
  label?: string | null;
};

export type FolioBuildStatus = 'none' | 'pending' | 'processing' | 'ready' | 'failed';

/** One poll covering both the render and the editor's scrub proxy. */
export type FolioRolloverBuildStatus = {
  rollover: {
    status: FolioBuildStatus;
    error: string | null;
    sections: number;
    totalSeconds: number;
    videoUrl: string | null;
    /** Ordered stages this build goes through, and the one it is in
     *  (null when nothing is in flight). A progress bar is just the
     *  index of `stage` within `stages`. */
    stages: string[];
    stage: string | null;
  };
  scrub: {
    status: FolioBuildStatus;
    error: string | null;
    url: string | null;
    stages: string[];
    stage: string | null;
  };
};

export type FolioUpdate = Partial<{
  title: string;
  subtitle: string | null;
  category: FolioCategory;
  sector: FolioSector | null;
  rating: number | null;
  status: FolioStatus;
  pieceType: FolioPieceType;
  publishedAt: string;
  heroImage: string | null;
  heroVimeoId: string | null;
  /** Flip the staged Mux hero live (true) / back to Vimeo (false). */
  heroMuxLive: boolean;
  hoverVideoUrl: string | null;
  hoverVideoUrlPortrait: string | null;
  tilePosterUrl: string | null;
  tilePosterUrlPortrait: string | null;
  hoverVideoStartTime: number | null;
  hoverVideoStartTimePortrait: number | null;
  intro: string | null;
  contentBlocks: FolioContentBlock[];
  credits: Array<Pick<FolioCredit, 'role' | 'name'> & { sortOrder?: number }>;
  jobId: string | null;
  sortOrder: number | null;
  tags: string[];
}>;

/** Create-piece payload — slug + title + category are required, the
 *  rest mirror the editable fields on the update payload. Status
 *  defaults to DRAFT server-side when not provided. */
export type FolioCreate = {
  slug: string;
  title: string;
  category: FolioCategory;
  sector?: FolioSector | null;
  subtitle?: string | null;
  status?: FolioStatus;
  pieceType?: FolioPieceType;
  publishedAt?: string;
  heroImage?: string | null;
  heroVimeoId?: string | null;
  hoverVideoUrl?: string | null;
  hoverVideoUrlPortrait?: string | null;
  tilePosterUrl?: string | null;
  tilePosterUrlPortrait?: string | null;
  hoverVideoStartTime?: number | null;
  hoverVideoStartTimePortrait?: number | null;
  intro?: string | null;
  tags?: string[];
};

/** AI-drafted folio fields, returned by draftFromJob. The dashboard
 *  pre-fills the uploader with these; nothing is saved server-side. */
export type FolioDraftFields = {
  title: string;
  subtitle: string | null;
  intro: string;
  tags: string[];
  credits: Array<Pick<FolioCredit, 'role' | 'name'>>;
  /** Short body paragraphs to seed text content-blocks. */
  textBlocks: string[];
  /** Numeric Vimeo id found in the job's emails, or null. */
  heroVimeoId: string | null;
};

/** Media pulled from a job's exports by ingestMediaFromJob: the tile
 *  hover urls + content blocks (BTS images), for the uploader to
 *  pre-fill. Nothing is saved server-side. */
export type FolioMediaIngest = {
  hoverVideoUrl: string | null;
  hoverVideoUrlPortrait: string | null;
  /** Auto-grabbed tile posters (a frame from each rollover). */
  tilePosterUrl: string | null;
  tilePosterUrlPortrait: string | null;
  blocks: FolioContentBlock[];
  notes: string[];
};

/** One Mux video in the folio Library / "Add from Mux" picker. */
export type MuxLibraryRow = {
  assetId: string;
  playbackId: string;
  /** Mux status: 'ready' | 'preparing' | 'errored'. */
  status: string;
  duration: number | null;
  /** Mux's ratio string, e.g. "16:9", "256:135", "9:16". */
  aspectRatio: string | null;
  createdAt: string | null;
  /** Its name ("2024 Winter 3 P8"), set in the Library or at ingest. */
  label: string | null;
  /** Client slug from an ingest tag, e.g. "just-jeans". */
  clientSlug: string | null;
  /** A tile hover loop cut from a hero, never a body video. */
  rollover: boolean;
  /** Library folder; null = Unsorted. */
  folder: string | null;
  /** 'filed' = put there by hand; otherwise inferred from its piece. */
  folderSource: 'filed' | 'piece' | 'tag' | 'none';
  /** Every piece already showing this film. Empty = on no piece. */
  usedIn: Array<{
    pieceId: string;
    slug: string;
    title: string;
    status: string;
    role: 'hero' | 'block';
  }>;
};

/** A row in the folio Mux Library — one Mux asset + where it's used. */
export type MuxLibraryAsset = {
  assetId: string;
  playbackId: string | null;
  status: string;
  duration: number | null;
  createdAt: string | null;
  aspectRatio: string | null;
  passthrough: string | null;
  usedBy: { kind: 'hero' | 'block'; title: string; slug: string | null } | null;
};

/** One shot of a hand-framed 9:16 (seconds on the hero, x 0..1). */
export type FolioFramedShot = { startTime: number; endTime: number; x: number };

export const folio = {
  /** Save the hand-framed 9:16 and queue its render from the hero. */
  framePortrait: (id: string, shots: FolioFramedShot[]) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/portrait-framing`, {
      method: 'POST',
      body: { shots },
    }),
  list: () =>
    apiFetch<{ pieces: FolioPiece[] }>('/api/folio/admin/list'),
  /** AI pre-fill: draft the text fields from a finished job's brief,
   *  treatment, crew and emails. Returns fields only — never saves. */
  draftFromJob: (jobId: string) =>
    apiFetch<{ draft: FolioDraftFields }>('/api/folio/admin/draft-from-job', {
      method: 'POST',
      body: { jobId },
    }),
  /** Pull the job's web rollovers (16:9 / 9:16) + BTS images out of its
   *  exports into /media. Returns urls + content blocks — never saves. */
  ingestMediaFromJob: (jobId: string) =>
    apiFetch<{ media: FolioMediaIngest }>('/api/folio/admin/ingest-media-from-job', {
      method: 'POST',
      body: { jobId },
    }),
  get: (id: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}`),
  /** Ingest a piece's hero video master into Mux from a public URL
   *  (e.g. a Dropbox raw link). Returns the piece with the Mux ids set. */
  muxHero: (id: string, sourceUrl: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/mux-hero`, {
      method: 'POST',
      body: { sourceUrl },
    }),
  /** Grab one frame of a rollover clip as a cover image, on the server
   *  (the clip sits on R2, which the browser can play but not read). */
  clipFrame: (url: string, time: number) =>
    apiFetch<{ url: string }>(`/api/folio/admin/clip-frame`, {
      method: 'POST',
      body: { url, time },
    }),
  /** Replace the piece's SINGLE hero film: a film already on Mux (by
   *  playback or asset id) or one ingested from an https link. Hero
   *  fields are overwritten as a unit; content blocks are never touched.
   *  `warning` names a body block that already plays the same film. */
  setHero: (
    id: string,
    source: { muxPlaybackId?: string; muxAssetId?: string; sourceUrl?: string; dropboxPath?: string },
  ) =>
    apiFetch<{ piece: FolioPiece; recutQueued: boolean; warning: string | null }>(
      `/api/folio/admin/${id}/hero`,
      { method: 'POST', body: source },
    ),
  /** Clear every hero field (film, Vimeo id, live flag, scrub proxy).
   *  Content blocks stay. */
  removeHero: (id: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/hero`, { method: 'DELETE' }),
  /** A video block's film becomes the hero and the old hero becomes a
   *  video block in that block's position, in one update. */
  swapHero: (id: string, pick: { muxPlaybackId?: string; index?: number }) =>
    apiFetch<{ piece: FolioPiece; recutQueued: boolean; warning: string | null; index: number }>(
      `/api/folio/admin/${id}/hero-swap`,
      { method: 'POST', body: pick },
    ),
  /** Re-cut the rollover, the automatic 9:16 and the tile covers from
   *  the current hero (its sections, or 0 to 7s) and rebuild the scrub
   *  proxy. Runs on the worker; poll `rolloverBuildStatus`. */
  recutHero: (id: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/hero-recut`, {
      method: 'POST',
      body: {},
    }),
  /** Use a video already on Mux (from the library) as this piece's
   *  hero. No re-ingest. Returns the piece with the Mux ids set. */
  muxHeroFromLibrary: (id: string, assetId: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/mux-hero-existing`, {
      method: 'POST',
      body: { assetId },
    }),
  /** Start a Mux direct upload for this piece's hero. Returns a one-time
   *  upload id + the URL the browser PUTs the master file straight to
   *  (Mux, not our API). Used by the "Upload from computer" button so a
   *  final export goes to Mux without ever touching Vimeo. */
  muxCreateUpload: (id: string) =>
    apiFetch<{ upload: { uploadId: string; url: string } }>(
      `/api/folio/admin/${id}/mux-upload`,
      { method: 'POST', body: {} },
    ),
  /** Finalize a direct upload once the file has landed: resolves the Mux
   *  asset and stores the playback id on the piece (staged). 400 "not
   *  ready" while Mux is still creating the asset — caller retries. */
  muxFinalizeUpload: (id: string, uploadId: string) =>
    apiFetch<{ piece: FolioPiece }>(
      `/api/folio/admin/${id}/mux-upload-finalize`,
      { method: 'POST', body: { uploadId } },
    ),
  /** Start a Mux direct upload for a standalone in-page video block (not
   *  tied to a hero). Returns a one-time upload id + the PUT url. */
  muxBlockUpload: () =>
    apiFetch<{ upload: { uploadId: string; url: string } }>(
      '/api/folio/admin/mux-block-upload',
      { method: 'POST', body: {} },
    ),
  /** Resolve a content-block upload to its Mux asset + playback id (no DB
   *  write — the caller writes the id into the block). 400 "not ready"
   *  while Mux is still creating the asset. */
  muxBlockResolve: (uploadId: string) =>
    apiFetch<{ asset: { assetId: string; playbackId: string } }>(
      '/api/folio/admin/mux-block-resolve',
      { method: 'POST', body: { uploadId } },
    ),
  /** Cut the tile rollover out of the piece's Mux hero (in/out seconds
   *  on the hero timeline). Stores the clip's MP4 + first-frame still on
   *  the piece straight away and returns it; the files finish encoding
   *  on Mux in the background — poll `rolloverStatus`. */
  cutRollover: (id: string, start: number, end: number) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/rollover-cut`, {
      method: 'POST',
      body: { start, end },
    }),
  /** Encode state of the piece's Mux-cut rollover. */
  rolloverStatus: (id: string) =>
    apiFetch<FolioRolloverStatus>(`/api/folio/admin/${id}/rollover-status`),
  /** Replace the rollover edit: the ordered sections of the hero the
   *  tile clip is built from. Saving does NOT render — the editor saves
   *  while you mark, and a render costs a master download plus two
   *  encodes. `renderRollover` is the commit. */
  setSections: (id: string, sections: FolioHoverSection[]) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/sections`, {
      method: 'PUT',
      body: { sections },
    }),
  /** Queue the hero's editor proxy so marking doesn't wait seconds on
   *  every seek. Idempotent; poll `rolloverBuildStatus`. */
  buildScrubProxy: (id: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/scrub-proxy`, {
      method: 'POST',
      body: {},
    }),
  /** Commit the marked edit into the tile rollover. One section is cut
   *  on Mux; two or more are queued for the render worker. */
  renderRollover: (id: string) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}/rollover-render`, {
      method: 'POST',
      body: {},
    }),
  /** Cut a fresh automatic 9:16 rollover from the 16:9 one (text frames
   *  skipped, each shot panned to its subject). Runs on the worker; poll
   *  `get` for the portrait fields to change. Replaces any 9:16 there. */
  makePortraitRollover: (id: string) =>
    apiFetch<{ queued: boolean }>(`/api/folio/admin/${id}/portrait-rollover`, {
      method: 'POST',
      body: {},
    }),
  /** One poll for the editor: render state and scrub-proxy state. */
  rolloverBuildStatus: (id: string) =>
    apiFetch<FolioRolloverBuildStatus>(`/api/folio/admin/${id}/rollover-build-status`),
  /** The folio Library: every video uploaded to Mux, paginated, each
   *  annotated with the folio piece it belongs to. */
  muxAssets: (page = 1) =>
    apiFetch<{ assets: MuxLibraryAsset[]; hasMore: boolean }>(
      `/api/folio/admin/mux-assets?page=${page}`,
    ),
  /** Every Mux video at once, with folders, for the Library and the
   *  editor's "Add from Mux" picker. `fresh` skips the server cache. */
  muxLibrary: (fresh = false) =>
    apiFetch<{ assets: MuxLibraryRow[] }>(
      `/api/folio/admin/mux-library${fresh ? '?fresh=1' : ''}`,
    ),
  /** File videos into a folder (null = Unsorted) and / or rename them. */
  updateMuxLibrary: (input: { assetIds: string[]; folder?: string | null; label?: string | null }) =>
    apiFetch<{ updated: number }>('/api/folio/admin/mux-library', {
      method: 'PATCH',
      body: input,
    }),
  renameMuxFolder: (from: string, to: string) =>
    apiFetch<{ updated: number }>('/api/folio/admin/mux-library/rename-folder', {
      method: 'POST',
      body: { from, to },
    }),
  create: (input: FolioCreate) =>
    apiFetch<{ piece: FolioPiece }>('/api/folio/admin', {
      method: 'POST',
      body: input,
    }),
  update: (id: string, patch: FolioUpdate) =>
    apiFetch<{ piece: FolioPiece }>(`/api/folio/admin/${id}`, {
      method: 'PATCH',
      body: patch,
    }),
  archive: (id: string) =>
    apiFetch<{ piece: { id: string; status: FolioStatus } }>(
      `/api/folio/admin/${id}`,
      { method: 'DELETE' },
    ),
  /** Hard delete — wipes the row + credits. Use only via an explicit
   *  "Delete forever" affordance; the default is `archive`. ADMIN only. */
  purge: (id: string) =>
    apiFetch<{ id: string }>(`/api/folio/admin/${id}/purge`, {
      method: 'DELETE',
    }),
  /** Bulk sortOrder reassignment. Sends every visible piece in the
   *  user's preferred order; backend sets sortOrder = index for each. */
  reorder: (orderedIds: string[]) =>
    apiFetch<{ updated: number }>('/api/folio/admin/reorder', {
      method: 'POST',
      body: { orderedIds },
    }),
};

/**
 * Public-site media uploads. Hits POST /api/media/upload with the
 * raw file bytes; backend writes it to /media/uploads/{yyyy}/{mm}/
 * and returns the URL the website + dashboard can store on a row.
 *
 * Always go through `uploadBinary()` so the 15-min token expiry
 * doesn't silently 401 mid-upload (see project memory).
 */
export type MediaUploaded = {
  url: string;
  sizeBytes: number;
  kind: 'image' | 'video';
};

export const media = {
  upload: (file: File) =>
    uploadBinary<MediaUploaded>('/api/media/upload', file),
};

// --- Site singletons (Home / About / Contact) -----------------------
//
// Mirrors the *PageView shapes returned by /api/site/* on the backend.
// Dashboard "Site" tab reads + writes through these helpers.

export type FeaturedGroup =
  | {
      kind: 'bento';
      portraitSide: 'left' | 'right';
      portraitSlug: string;
      stackedSlugs: string[];
    }
  | { kind: 'fullbleed'; slug: string };

export type HomePageContent = {
  showreelVimeoId: string;
  showreelVimeoIdMobile: string | null;
  watchShowreelHref: string | null;
  introTitleLines: string[];
  introBody: string;
  introCtaHref: string | null;
  introCtaLabel: string | null;
  featuredGroups: FeaturedGroup[];
  updatedAt: string;
};

export type LogoEntry = { src: string; alt: string };

export type AboutPageContent = {
  heroImage: string;
  title: string;
  ctaHref: string | null;
  ctaLabel: string | null;
  body: string;
  awardsTitle: string;
  awardLogos: LogoEntry[];
  brandsTitle: string;
  brandLogos: LogoEntry[];
  updatedAt: string;
};

export type ContactPageContent = {
  heroImage: string;
  title: string;
  studioName: string;
  studioPhone: string | null;
  studioEmail: string | null;
  addressLines: string[];
  addressMapUrl: string | null;
  updatedAt: string;
};

export type HomePageUpdate = Partial<Omit<HomePageContent, 'updatedAt'>>;
export type AboutPageUpdate = Partial<Omit<AboutPageContent, 'updatedAt'>>;
export type ContactPageUpdate = Partial<Omit<ContactPageContent, 'updatedAt'>>;

export const site = {
  getHome: () => apiFetch<{ home: HomePageContent }>('/api/site/home'),
  updateHome: (patch: HomePageUpdate) =>
    apiFetch<{ home: HomePageContent }>('/api/site/home', {
      method: 'PATCH',
      body: patch,
    }),
  getAbout: () => apiFetch<{ about: AboutPageContent }>('/api/site/about'),
  updateAbout: (patch: AboutPageUpdate) =>
    apiFetch<{ about: AboutPageContent }>('/api/site/about', {
      method: 'PATCH',
      body: patch,
    }),
  getContact: () => apiFetch<{ contact: ContactPageContent }>('/api/site/contact'),
  updateContact: (patch: ContactPageUpdate) =>
    apiFetch<{ contact: ContactPageContent }>('/api/site/contact', {
      method: 'PATCH',
      body: patch,
    }),
  /** Stored contact-form submissions. ADMIN only. */
  listSubmissions: (view: ContactSubmissionView, limit = 100) =>
    apiFetch<ContactSubmissionList>(
      `/api/contact/submissions?view=${view}&limit=${limit}`,
    ),
};

/**
 * A contact-form submission as stored. Every submission gets a row,
 * written before the spam gates run, so a wrongly dropped enquiry is
 * recoverable instead of being a log line that ages out.
 */
export type ContactVerdict =
  | 'PASSED'
  | 'DROPPED_HONEYPOT'
  | 'DROPPED_NONCE'
  | 'DROPPED_TURNSTILE';

export type ContactSubmissionView = 'passed' | 'dropped' | 'flagged' | 'all';

export type ContactSubmission = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  verdict: ContactVerdict;
  verdictReason: string | null;
  /** Dropped, but it cleared the honeypot + nonce, so probably a person. */
  flagged: boolean;
  emailedAt: string | null;
  createdAt: string;
};

export type ContactSubmissionList = {
  submissions: ContactSubmission[];
  counts: { passed: number; dropped: number; flagged: number };
};

export const taste = {
  next: (industry: string | null, count = 10) => {
    const params = new URLSearchParams();
    if (industry) params.set('industry', industry);
    params.set('count', String(count));
    return apiFetch<{ items: TasteCandidate[] }>(
      `/api/admin/taste/next?${params.toString()}`,
    );
  },
  rate: (input: TasteRateInput) =>
    apiFetch<{ id: string }>(`/api/admin/taste/rate`, {
      method: 'POST',
      body: input,
    }),
  profile: () => apiFetch<TasteProfile>(`/api/admin/taste/profile`),
  yesList: (industry: string | null) => {
    const params = new URLSearchParams();
    if (industry) params.set('industry', industry);
    return apiFetch<TasteYesList>(
      `/api/admin/taste/yes-list${params.toString() ? `?${params}` : ''}`,
    );
  },
};

/** Restyled first frames (hybrid step 5 gate, 2026-10-31). Team only. */
export type RestyleStillFrame = {
  frame: number;
  state: 'approved' | 'redo' | 'waiting';
  approved: { version: number; path: string; link: string | null } | null;
  latest: { version: number; status: 'DRAFT' | 'APPROVED' | 'REDO'; note: string | null; path: string; link: string | null; checks: { route?: string; verdict?: string; notes?: string[] } | null };
  versions: Array<{ version: number; status: string; note: string | null; decidedAt: string | null }>;
};
export type RestyleStillsView = { frames: RestyleStillFrame[]; waiting: number[]; redo: number[]; approved: number[]; track: string };

export const restyleStills = {
  list: (jobId: string) => apiFetch<RestyleStillsView>(`/api/jobs/${jobId}/restyle-stills`),
  review: (jobId: string, frame: number, body: { decision: 'approve' | 'redo'; version?: number; note?: string }) =>
    apiFetch<RestyleStillsView>(`/api/jobs/${jobId}/restyle-stills/${frame}/review`, { method: 'POST', body }),
};

/** Continuity pack sheets (hybrid step 2 gate, 2026-10-31). Team only. */
export type ContinuitySheetKind = 'CHARACTER' | 'WARDROBE' | 'LOCATION' | 'PROPS' | 'VOICE';
export type ContinuitySheetRow = {
  kind: ContinuitySheetKind;
  subject: string;
  look: string;
  state: 'approved' | 'redo' | 'waiting';
  approved: { version: number; path: string; link: string | null } | null;
  latest: { version: number; status: 'DRAFT' | 'APPROVED' | 'REDO'; note: string | null; path: string; link: string | null; checks: { verdict?: string; notes?: string[] } | null };
  versions: Array<{ version: number; status: string; note: string | null; decidedAt: string | null }>;
};
export type ContinuitySheetsView = { sheets: ContinuitySheetRow[]; waiting: number; redo: number; approved: number };

export const continuitySheets = {
  list: (jobId: string) => apiFetch<ContinuitySheetsView>(`/api/jobs/${jobId}/continuity-sheets`),
  review: (jobId: string, body: { kind: ContinuitySheetKind; subject: string; look: string; version?: number; decision: 'approve' | 'redo'; note?: string }) =>
    apiFetch<ContinuitySheetsView>(`/api/jobs/${jobId}/continuity-sheets/review`, { method: 'POST', body }),
};
